import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import AccountScreen from "./AccountScreen";
import LegalScreen from "./LegalScreen";
import { fetchEquipment, fetchLogs, fetchRepairs, fetchRequests, saveEquipment, saveLog, saveRepair, saveRequest, updateRequest, deleteEquipmentById, invalidateAll } from "./supabaseDB";

// ─── SEED DATA ────────────────────────────────────────────────────────────────

var today = new Date().toISOString().split("T")[0];
function daysAgo(n) { var d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().split("T")[0]; }
function daysFromNow(n) { var d = new Date(); d.setDate(d.getDate()+n); return d.toISOString().split("T")[0]; }
function genId() { return "ch_" + Date.now() + "_" + Math.random().toString(36).slice(2,6); }

var INIT_EQUIPMENT = [];

var INIT_LOGS = [];

var INIT_REPAIRS = [];

var INIT_REMINDERS = [
  { id:"rem_1", equipmentId:"eq_1", scheduleType:"interval", scheduledDays:[], scheduledTime:"08:00", intervalDays:7,  alertMode:"inapp", nextDate:daysFromNow(5) },
  { id:"rem_2", equipmentId:"eq_2", scheduleType:"weekly",   scheduledDays:[5], scheduledTime:"16:30", intervalDays:7, alertMode:"sound", nextDate:daysFromNow(3) },
  { id:"rem_3", equipmentId:"eq_3", scheduleType:"interval", scheduledDays:[], scheduledTime:"08:00", intervalDays:3,  alertMode:"inapp", nextDate:daysFromNow(1) }
];

var INIT_PARTS_LIB = [
  { id:"pl_1", name:"Oil Filter",       partNumber:"Wix 51515",        notes:"Universal - fits most small engines" },
  { id:"pl_2", name:"Air Filter",       partNumber:"Baldwin PA4690",   notes:"Heavy equipment standard" },
  { id:"pl_3", name:"Hydraulic Filter", partNumber:"HF6553",           notes:"Change every 500 hrs" },
  { id:"pl_4", name:"Spark Plug",       partNumber:"NGK-BPR6ES",       notes:"Standard small engine" },
  { id:"pl_5", name:"Drive Belt",       partNumber:"B-2345",           notes:"Inspect every 250 hrs" }
];

var INIT_SERVICES_LIB = [
  { id:"sv_1",  name:"Engine Oil & Filter Change",  intervalHours:100,   types:["lawn","heavy","agricultural","genpump","other"], notes:"Check oil level before each use" },
  { id:"sv_2",  name:"Engine Oil & Filter Change",  intervalHours:5000,  types:["vehicle"],                                       notes:"Reset oil life monitor after service" },
  { id:"sv_3",  name:"Tire Rotation",               intervalHours:7500,  types:["vehicle"],                                       notes:"Check tire pressure and tread depth" },
  { id:"sv_4",  name:"Brake Inspection",            intervalHours:15000, types:["vehicle"],                                       notes:"Check pads, rotors, and fluid" },
  { id:"sv_5",  name:"Air Filter Replacement",      intervalHours:25,    types:["lawn"],                                          notes:"Clean or replace depending on conditions" },
  { id:"sv_6",  name:"Air Filter Replacement",      intervalHours:500,   types:["heavy","agricultural","genpump"],                notes:"Inspect every 250 hrs in dusty conditions" },
  { id:"sv_7",  name:"Blade Sharpening",            intervalHours:25,    types:["lawn"],                                          notes:"Balance blades after sharpening" },
  { id:"sv_8",  name:"Blade Replacement",           intervalHours:200,   types:["lawn"],                                          notes:"Replace if cracked or severely worn" },
  { id:"sv_9",  name:"Spark Plug Replacement",      intervalHours:100,   types:["lawn","genpump"],                                notes:"Check gap before installing" },
  { id:"sv_10", name:"Hydraulic Fluid Check",       intervalHours:250,   types:["heavy","agricultural"],                          notes:"Top off to full mark - check for leaks" },
  { id:"sv_11", name:"Hydraulic Filter Change",     intervalHours:500,   types:["heavy","agricultural"],                          notes:"Replace filter and check lines" },
  { id:"sv_12", name:"Greasing / Lubrication",      intervalHours:50,    types:["heavy","agricultural"],                          notes:"Grease all zerks and pivot points" },
  { id:"sv_13", name:"Drive Belt Inspection",       intervalHours:100,   types:["lawn","agricultural"],                           notes:"Check tension and wear - replace if cracked" },
  { id:"sv_14", name:"Fuel Filter Replacement",     intervalHours:200,   types:["heavy","agricultural","genpump"],                notes:"Drain fuel before replacing" },
  { id:"sv_15", name:"Coolant Flush",               intervalHours:1000,  types:["heavy","vehicle","agricultural"],                notes:"Use manufacturer specified coolant mix" },
  { id:"sv_16", name:"Annual Safety Inspection",    intervalHours:1000,  types:["vehicle","heavy","agricultural","lawn","genpump","other"], notes:"Full safety check per operator manual" }
];


// ─── PLAN PERMISSIONS ─────────────────────────────────────────────────────────

var PLAN_LIMITS = {
  free:    { maxEquipment:3,  maxUsers:1,  serviceRequests:false, weeklyReports:false, crew:false, vinLookup:false },
  starter: { maxEquipment:10, maxUsers:1,  serviceRequests:true,  weeklyReports:true,  crew:false, vinLookup:true  },
  captain: { maxEquipment:25, maxUsers:5,  serviceRequests:true,  weeklyReports:true,  crew:true,  vinLookup:true  },
  pro:     { maxEquipment:null, maxUsers:null, serviceRequests:true, weeklyReports:true, crew:true, vinLookup:true  },
};

function getPlan(user) {
  var p = (user&&user.plan)||"free";
  // Trial users get Free tier features until they upgrade
  if (!p || p==="trial") p = "free";
  return PLAN_LIMITS[p] || PLAN_LIMITS.free;
}

function canAddEquipment(user, currentCount) {
  var p = getPlan(user);
  if (!p.maxEquipment) return true;
  return currentCount < p.maxEquipment;
}

function equipmentLimitMsg(user) {
  var p = getPlan(user); var plan = (user&&user.plan)||"free";
  var next = plan==="free"?"One Man Band":plan==="starter"?"Captain & Crew":"Pro";
  var nextPrice = plan==="free"?"$19.99/mo":plan==="starter"?"$39.99/mo":"$79.99/mo";
  return "You've reached the "+p.maxEquipment+" equipment limit on your "+planName(plan)+" plan. Upgrade to "+next+" ("+nextPrice+") to add more.";
}

function planName(plan) {
  var names = {free:"Free",starter:"One Man Band",captain:"Captain & Crew",pro:"Pro"};
  return names[plan]||"Free";
}

// ─── UPGRADE PROMPT SHEET ─────────────────────────────────────────────────────

function UpgradeSheet(props) {
  var msg = props.msg || "Upgrade your plan to access this feature.";
  var onClose = props.onClose || function(){};
  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:500 }} onClick={onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.accent, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, paddingBottom:32 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 8px" }} />
        <div style={{ padding:"16px 20px 20px", textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>⭐</div>
          <div style={{ fontSize:15, fontWeight:700, color:C.accent, ...FM, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.08em" }}>Upgrade Required</div>
          <div style={{ fontSize:13, color:C.dim, lineHeight:1.7, marginBottom:24 }}>{msg}</div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.muted, fontSize:12, ...FM, padding:12, cursor:"pointer" }}>Not Now</button>
            <button onClick={onClose} style={{ flex:2, background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, fontWeight:700, padding:12, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>View Plans →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────

var T = {
  en: {
    cancel:"Cancel", save:"Save", edit:"Edit", del:"Delete", yes:"Yes", no:"No",
    back:"Back", close:"Close", done:"Done", add:"Add", confirm:"Confirm",
    optional:"optional", required:"required", adminOnly:"Admin Only",
    appName:"ChoreHabit", dashboard:"Dashboard", alerts:"Alerts", logbook:"Logbook",
    fleet:"Fleet", addEquip:"Add", equipment:"Equipment",
    down:"Down", overdue:"Overdue", warnings:"Warnings",
    maintenanceAlerts:"Maintenance Alerts", recentLogs:"Recent Log Entries",
    upcomingReminders:"Upcoming Log Reminders", allUpToDate:"All maintenance is up to date.",
    noLogs:"No recent log entries.", actionNeeded:"ACTION NEEDED", remaining:"remaining",
    manageUsers:"Manage Users", servicesLib:"Services Library", signOut:"Sign Out",
    outOfService:"Out of Service", currentlyDown:"pieces currently down",
    pieceDown:"piece currently down", allActive:"All equipment is active",
    nothingDown:"Nothing is currently out of service.", tapToUpdate:"Tap to update or clear status",
    atShop:"At the Shop", waitingParts:"Waiting on Parts",
    reportedBy:"Reported By", returnDate:"Return Date",
    notSet:"Not set", today:"Today", overdueLbl:"Overdue",
    markAsDown:"Mark as Out of Service", updateStatus:"Update Status",
    markBackInService:"Mark as Back in Service",
    reason:"Reason", expectedReturn:"Expected Return Date",
    expectedBackToday:"Expected back today", expectedBackTomorrow:"Expected back tomorrow",
    returnDatePassed:"Return date passed", expectedBackIn:"Expected back in",
    days:"days", notes:"Notes",
    piecesOfEquip:"pieces of equipment", current:"Current", entries:"Entries",
    lastLog:"Last Log", never:"Never", okStatus:"OK",
    maintenance:"Maint.", history:"History", specs:"Specs", warranty:"Warranty",
    addLogEntry:"+ Log Entry", addRepair:"+ Repair",
    statusBtn:"Down", statusBtnActive:"Status", crewView:"Crew View", active:"Active",
    maintenanceIntervals:"Maintenance Intervals",
    every:"Every", last:"Last",
    addInterval:"Add Interval", editInterval:"Edit Interval",
    taskName:"Task Name", taskPlaceholder:"e.g. Oil change, Blade sharpening",
    everyHrs:"Every (hrs / mi)", lastDoneAt:"Last Done At",
    alertSetting:"Alert Setting", inApp:"In-App", sound:"Sound", off:"Off",
    pickFromLib:"Pick from Services Library", orCustom:"or custom",
    logEntryBadge:"Log Entry", inHouse:"In-House", shopRepair:"Shop Repair",
    warrantyBadge:"Warranty", noHistory:"No history yet.",
    noDriverNoted:"No driver noted", fuel:"Fuel", perGal:"/gal",
    costLabel:"Cost", laborHrs:"hrs labor",
    identity:"Identity", engineOil:"Engine Oil", hydraulicOil:"Hydraulic Oil",
    partsComponents:"Parts and Components",
    typeGrade:"Type / Grade", quantity:"Quantity", oilFilterPN:"Oil Filter P/N",
    typeSpec:"Type / Spec", capacity:"Capacity",
    make:"Make", model:"Model", year:"Year", vinNum:"VIN #", serialNum:"Serial #",
    purchasePrice:"Purchase Price",
    dealer:"Dealer", purchaseDate:"Purchase Date", coverage:"Coverage",
    expires:"Expires", warrantyWork:"Warranty Work",
    fullCoverage:"Full Coverage", partsOnly:"Parts Only",
    laborOnly:"Labor Only", powertrain:"Powertrain", none:"None",
    newLogEntry:"New Log Entry", entryDate:"Entry Date",
    currentOdo:"Odometer (mi)", currentHrs:"Hours (hrs)",
    sinceLast:"since last entry", lowerThanLast:"Lower than last entry",
    fuelFillUp:"Fuel Fill-Up (optional)", gallons:"Gallons", costDollar:"Cost ($)",
    driverOperator:"Driver / Operator (optional)", driverPlaceholder:"e.g. Mike R.",
    notesPlaceholder:"Tire pressure, sounds, vibrations, leaks, anything unusual...",
    saveEntry:"Save Entry",
    logRepair:"Log Repair", repairType:"Repair Type",
    inHouseRepair:"In-House", shopDealer:"Shop / Dealer",
    date:"Date", returnDateLbl:"Return Date",
    whatRepaired:"What Was Repaired *",
    whatRepairedPlaceholder:"e.g. Spindle bearing, hydraulic line, belt...",
    description:"Description (optional)",
    descPlaceholder:"Details about what caused it, how it was fixed...",
    inHouseDetails:"In-House Details", technician:"Technician", laborHoursLbl:"Labor Hours",
    shopDetails:"Shop Details", shopName:"Shop / Dealer Name",
    shopPlaceholder:"e.g. Green Turf Equipment",
    warrantyWork2:"Warranty Work?", noWarranty:"No", yesWarranty:"Yes - Warranty",
    receiptInvoice:"Receipt / Invoice", scanReceipt:"Scan Receipt", camera:"Camera",
    enterManually:"Enter Manually", typeDetails:"Type details",
    invoiceDetails:"Invoice Details",
    invoicePlaceholder:"Invoice #, parts, labor, totals...", retake:"Retake",
    outOfServiceQ:"Mark as Out of Service?",
    stillActive:"No - Still Active", yesOutOfService:"Yes - Out of Service",
    totalCost:"Total Cost ($) - Admin Only",
    additionalDetails:"Additional details...", saveRepair:"Save Repair",
    markComplete:"Mark as Complete", hrsAtService:"hrs at Service", miAtService:"mi at Service",
    nextDueAt:"Next due at", performedBy:"Performed By (optional)",
    techPlaceholder:"Your name or technician",
    serviceNotes:"Parts used, observations, anything worth noting...",
    resetsInterval:"Resets this interval and creates a service record automatically.",
    confirmComplete:"Confirm Complete",
    selectEquipment:"Select equipment...",
    logReminders:"Log Reminders", recentEntries:"Recent Entries", noEntriesYet:"No entries yet",
    servicesLibTitle:"Services Library",
    servicesCount:"services -- picked when adding intervals",
    noServicesCategory:"No services in this category.",
    serviceName:"Service Name *", serviceNamePlaceholder:"e.g. Engine Oil Change",
    defaultInterval:"Default Interval (hrs or mi) *",
    appliesTo:"Applies To *", serviceNotes2:"Notes (optional)",
    serviceTips:"Tips, specs, what to check...",
    addService:"Add Service", editService:"Edit Service", saveChanges:"Save Changes",
    addEquipment:"Add Equipment", equipmentType:"Equipment Type",
    vehicleType:"Vehicle", heavyType:"Heavy Equipment", lawnType:"Lawn & Grounds",
    agType:"Agricultural", genType:"Generator/Pump", otherType:"Other",
    vehicleSub:"Car / truck / van", heavySub:"Bobcat, excavator, bulldozer",
    lawnSub:"Mower, trimmer, blower", agSub:"Tractor, combine, baler",
    genSub:"Hours-based tracking", otherSub:"Custom type",
    nameLbl:"Name *", namePlaceholderVeh:"e.g. Ford F-250 #1",
    namePlaceholderEq:"e.g. Bobcat S650, ZS1 Mower #1",
    vinOptional:"VIN # (optional)", serialOptional:"Serial # (optional)",
    vinPlaceholder:"1FTFW1ET5MFC...", serialPlaceholder:"SN-1A2B3C4D",
    currentMileage:"Current Mileage (mi) *", currentHours:"Current Hours (hrs) *",
    saveEquipment:"Save Equipment",
  },
  es: {
    cancel:"Cancelar", save:"Guardar", edit:"Editar", del:"Eliminar", yes:"Si", no:"No",
    back:"Atras", close:"Cerrar", done:"Listo", add:"Agregar", confirm:"Confirmar",
    optional:"opcional", required:"requerido", adminOnly:"Solo Admin",
    appName:"ChoreHabit", dashboard:"Tablero", alerts:"Alertas", logbook:"Bitacora",
    fleet:"Flota", addEquip:"Agregar", equipment:"Equipos",
    down:"Baja", overdue:"Vencido", warnings:"Avisos",
    maintenanceAlerts:"Alertas de Mantenimiento", recentLogs:"Entradas Recientes",
    upcomingReminders:"Recordatorios de Bitacora", allUpToDate:"Todo el mantenimiento esta al dia.",
    noLogs:"Sin entradas recientes.", actionNeeded:"ATENCION REQUERIDA", remaining:"restante",
    manageUsers:"Gestionar Usuarios", servicesLib:"Biblioteca de Servicios", signOut:"Cerrar Sesion",
    outOfService:"Fuera de Servicio", currentlyDown:"equipos fuera de servicio",
    pieceDown:"equipo fuera de servicio", allActive:"Todo el equipo esta activo",
    nothingDown:"Nada esta fuera de servicio actualmente.", tapToUpdate:"Toca para actualizar o limpiar estado",
    atShop:"En el Taller", waitingParts:"Esperando Piezas",
    reportedBy:"Reportado Por", returnDate:"Fecha de Regreso",
    notSet:"No establecida", today:"Hoy", overdueLbl:"Vencida",
    markAsDown:"Marcar Fuera de Servicio", updateStatus:"Actualizar Estado",
    markBackInService:"Marcar Como Activo",
    reason:"Razon", expectedReturn:"Fecha Esperada de Regreso",
    expectedBackToday:"Se espera de regreso hoy", expectedBackTomorrow:"Se espera de regreso manana",
    returnDatePassed:"Fecha de regreso pasada", expectedBackIn:"Se espera en",
    days:"dias", notes:"Notas",
    piecesOfEquip:"equipos", current:"Actual", entries:"Entradas",
    lastLog:"Ultimo Registro", never:"Nunca", okStatus:"OK",
    maintenance:"Mant.", history:"Historial", specs:"Especif.", warranty:"Garantia",
    addLogEntry:"+ Registro", addRepair:"+ Reparacion",
    statusBtn:"Baja", statusBtnActive:"Estado", crewView:"Vista Equipo", active:"Activo",
    maintenanceIntervals:"Intervalos de Mantenimiento",
    every:"Cada", last:"Ultimo",
    addInterval:"Agregar Intervalo", editInterval:"Editar Intervalo",
    taskName:"Nombre de Tarea", taskPlaceholder:"ej. Cambio de aceite, Afilar cuchillas",
    everyHrs:"Cada (hrs / mi)", lastDoneAt:"Ultimo Realizado",
    alertSetting:"Tipo de Alerta", inApp:"En App", sound:"Sonido", off:"Apagado",
    pickFromLib:"Elegir de Biblioteca", orCustom:"o personalizado",
    logEntryBadge:"Registro", inHouse:"Interno", shopRepair:"Taller",
    warrantyBadge:"Garantia", noHistory:"Sin historial aun.",
    noDriverNoted:"Sin operador registrado", fuel:"Combustible", perGal:"/gal",
    costLabel:"Costo", laborHrs:"hrs trabajo",
    identity:"Identidad", engineOil:"Aceite de Motor", hydraulicOil:"Aceite Hidraulico",
    partsComponents:"Piezas y Componentes",
    typeGrade:"Tipo / Grado", quantity:"Cantidad", oilFilterPN:"Filtro de Aceite N/P",
    typeSpec:"Tipo / Especif.", capacity:"Capacidad",
    make:"Marca", model:"Modelo", year:"Ano", vinNum:"VIN #", serialNum:"Serie #",
    purchasePrice:"Precio de Compra",
    dealer:"Distribuidor", purchaseDate:"Fecha de Compra", coverage:"Cobertura",
    expires:"Vence", warrantyWork:"Trabajos de Garantia",
    fullCoverage:"Cobertura Total", partsOnly:"Solo Piezas",
    laborOnly:"Solo Mano de Obra", powertrain:"Tren de Potencia", none:"Ninguna",
    newLogEntry:"Nuevo Registro", entryDate:"Fecha del Registro",
    currentOdo:"Odometro (mi)", currentHrs:"Horas (hrs)",
    sinceLast:"desde el ultimo registro", lowerThanLast:"Menor que el ultimo registro",
    fuelFillUp:"Carga de Combustible (opcional)", gallons:"Galones", costDollar:"Costo ($)",
    driverOperator:"Operador (opcional)", driverPlaceholder:"ej. Carlos R.",
    notesPlaceholder:"Presion de llantas, sonidos, vibraciones, fugas, algo inusual...",
    saveEntry:"Guardar Registro",
    logRepair:"Registrar Reparacion", repairType:"Tipo de Reparacion",
    inHouseRepair:"Interno", shopDealer:"Taller / Distribuidor",
    date:"Fecha", returnDateLbl:"Fecha de Regreso",
    whatRepaired:"Que se Reparo *",
    whatRepairedPlaceholder:"ej. Rodamiento, linea hidraulica, correa...",
    description:"Descripcion (opcional)",
    descPlaceholder:"Detalles sobre la causa y como se reparo...",
    inHouseDetails:"Detalles Internos", technician:"Tecnico", laborHoursLbl:"Horas de Trabajo",
    shopDetails:"Detalles del Taller", shopName:"Nombre del Taller",
    shopPlaceholder:"ej. Equipos Green Turf",
    warrantyWork2:"Trabajo de Garantia?", noWarranty:"No", yesWarranty:"Si - Garantia",
    receiptInvoice:"Recibo / Factura", scanReceipt:"Escanear Recibo", camera:"Camara",
    enterManually:"Ingresar Manual", typeDetails:"Escribir detalles",
    invoiceDetails:"Detalles de Factura",
    invoicePlaceholder:"Factura #, piezas, mano de obra, totales...", retake:"Retomar",
    outOfServiceQ:"Marcar Fuera de Servicio?",
    stillActive:"No - Sigue Activo", yesOutOfService:"Si - Fuera de Servicio",
    totalCost:"Costo Total ($) - Solo Admin",
    additionalDetails:"Detalles adicionales...", saveRepair:"Guardar Reparacion",
    markComplete:"Marcar como Completo", hrsAtService:"hrs al Servicio", miAtService:"mi al Servicio",
    nextDueAt:"Proximo servicio en", performedBy:"Realizado Por (opcional)",
    techPlaceholder:"Tu nombre o tecnico",
    serviceNotes:"Piezas usadas, observaciones, notas importantes...",
    resetsInterval:"Reinicia el intervalo y crea un registro automaticamente.",
    confirmComplete:"Confirmar Completo",
    selectEquipment:"Seleccionar equipo...",
    logReminders:"Recordatorios de Bitacora", recentEntries:"Entradas Recientes", noEntriesYet:"Sin entradas aun",
    servicesLibTitle:"Biblioteca de Servicios",
    servicesCount:"servicios -- elegidos al agregar intervalos",
    noServicesCategory:"Sin servicios en esta categoria.",
    serviceName:"Nombre del Servicio *", serviceNamePlaceholder:"ej. Cambio de Aceite",
    defaultInterval:"Intervalo por Defecto (hrs o mi) *",
    appliesTo:"Aplica A *", serviceNotes2:"Notas (opcional)",
    serviceTips:"Consejos, especificaciones, que revisar...",
    addService:"Agregar Servicio", editService:"Editar Servicio", saveChanges:"Guardar Cambios",
    addEquipment:"Agregar Equipo", equipmentType:"Tipo de Equipo",
    vehicleType:"Vehiculo", heavyType:"Equipo Pesado", lawnType:"Jardineria",
    agType:"Agricola", genType:"Generador/Bomba", otherType:"Otro",
    vehicleSub:"Auto / camion / van", heavySub:"Bobcat, excavadora, bulldozer",
    lawnSub:"Cortadora, recortadora, sopladora", agSub:"Tractor, cosechadora",
    genSub:"Seguimiento por horas", otherSub:"Tipo personalizado",
    nameLbl:"Nombre *", namePlaceholderVeh:"ej. Ford F-250 #1",
    namePlaceholderEq:"ej. Bobcat S650, Cortadora #3",
    vinOptional:"VIN # (opcional)", serialOptional:"Serie # (opcional)",
    vinPlaceholder:"1FTFW1ET5MFC...", serialPlaceholder:"SN-1A2B3C4D",
    currentMileage:"Kilometraje Actual (mi) *", currentHours:"Horas Actuales (hrs) *",
    saveEquipment:"Guardar Equipo",
  }
};

function getT(lang) {
  return T[lang] || T.en;
}

// ─── STORAGE ──────────────────────────────────────────────────────────────────

var SK = {
  equipment:   "ch_equipment",
  logs:        "ch_logs",
  repairs:     "ch_repairs",
  reminders:   "ch_reminders",
  partsLib:    "ch_parts_lib",
  servicesLib: "ch_services_lib"
};

function load(key, fallback) {
  try {
    var raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    var p = JSON.parse(raw);
    return p !== null ? p : fallback;
  } catch(e) { return fallback; }
}

function save(key, val) {
  try { window.localStorage.setItem(key, JSON.stringify(val)); }
  catch(e) { console.warn("fp save failed", key); }
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

var C = {
  bg:"#0F1117",        surface:"#1A1D27",    surfaceHi:"#222535",
  border:"#2E3248",    accent:"#F5A623",     accentDim:"rgba(245,166,35,0.12)",
  blue:"#4A90D9",      blueDim:"rgba(74,144,217,0.12)",
  teal:"#38BFA1",      tealDim:"rgba(56,191,161,0.10)",
  purple:"#9B72D0",    purpleDim:"rgba(155,114,208,0.12)",
  amber:"#F5A623",
  text:"#E8EAF0",      muted:"#6B7094",      dim:"#9AA0C0",
  danger:"#E05C5C",    dangerDim:"rgba(224,92,92,0.12)",
  success:"#4CAF6E",   successDim:"rgba(76,175,110,0.12)",
  inputBg:"#13151F"
};

var FM  = { fontFamily:"'DM Mono','Courier New',monospace" };
var inp = { background:C.inputBg, border:"1px solid "+C.border, borderRadius:8, padding:"10px 12px", color:C.text, fontSize:14, fontFamily:"'DM Mono','Courier New',monospace", outline:"none", width:"100%", boxSizing:"border-box" };
var lbl = { fontSize:10, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, fontFamily:"'DM Mono','Courier New',monospace" };

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function emoji(type) {
  if (type==="vehicle")      return "\uD83D\uDE97";
  if (type==="heavy")        return "\uD83C\uDFD7";
  if (type==="lawn")         return "\uD83C\uDF3F";
  if (type==="agricultural") return "\uD83D\uDE9C";
  if (type==="genpump")      return "\u26A1";
  if (type==="ev")           return "\u26A1";
  return "\uD83D\uDD27";
}

function fmtDate(iso) {
  if (!iso) return "--";
  var d = new Date(iso+"T00:00:00");
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
}

function daysSince(iso) {
  if (!iso) return 999;
  return Math.floor((Date.now()-new Date(iso).getTime())/86400000);
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.round((new Date(iso+"T00:00:00").getTime()-new Date(today+"T00:00:00").getTime())/86400000);
}

function dueIn(iv, eq) {
  return (parseFloat(iv.intervalHours)||0)-(eq.totalUsage-(parseFloat(iv.lastDoneHours)||0));
}

function dueBadge(iv, eq) {
  var d = dueIn(iv,eq);
  var pct = Math.max(0,Math.min(100,100-(d/iv.intervalHours*100)));
  var u = eq.usesMiles?"mi":"hrs";
  var w = eq.usesMiles?500:50;
  if (d<=0) return { text:"OVERDUE", color:C.danger, pct:100 };
  if (d<=w) return { text:Math.round(d).toLocaleString()+" "+u, color:C.amber,   pct:pct };
  return          { text:Math.round(d).toLocaleString()+" "+u, color:C.success, pct:pct };
}

function fmtTime(t) {
  if (!t) return "";
  var p = t.split(":");
  var hr = parseInt(p[0]);
  return (hr%12||12)+":"+p[1]+" "+(hr>=12?"PM":"AM");
}

function remNext(rem) {
  if (!rem.nextDate) return "--";
  var d = daysUntil(rem.nextDate);
  if (d===0) return "Today";
  if (d===1) return "Tomorrow";
  if (d<0)   return "Overdue";
  return "In "+d+"d";
}

function remSched(rem) {
  if (!rem||rem.alertMode==="off") return "Off";
  var DAY=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  var t = rem.scheduledTime?" @ "+fmtTime(rem.scheduledTime):"";
  if (rem.scheduleType==="weekly"&&rem.scheduledDays&&rem.scheduledDays.length) {
    return rem.scheduledDays.map(function(i){ return DAY[i]; }).join(", ")+t;
  }
  return (rem.intervalDays===1?"Daily":"Every "+rem.intervalDays+"d")+t;
}

function warStatus(expDate) {
  if (!expDate) return { label:"No Warranty", color:C.muted };
  var d = daysUntil(expDate);
  if (d<0)    return { label:"Expired",                              color:C.danger  };
  if (d<=30)  return { label:"Expires in "+d+"d",                   color:C.amber   };
  if (d<=90)  return { label:"Expires in "+Math.round(d/7)+"wk",    color:C.amber   };
  return            { label:"Active - "+Math.round(d/30)+"mo left", color:C.success };
}

function coverLabel(typ, tr) {
  if (!tr) tr = T.en;
  if (typ==="full")       return tr.fullCoverage;
  if (typ==="parts")      return tr.partsOnly;
  if (typ==="labor")      return tr.laborOnly;
  if (typ==="powertrain") return tr.powertrain;
  return tr.none||"None";
}

function oosLabel(r, tr) {
  if (!tr) tr = T.en;
  if (r==="at_shop")       return tr.atShop;
  if (r==="waiting_parts") return tr.waitingParts;
  return tr.outOfService;
}

function oosColor(r) {
  if (r==="at_shop")       return C.blue;
  if (r==="waiting_parts") return C.amber;
  return C.danger;
}

function retColor(dateStr) {
  if (!dateStr) return C.muted;
  var d = daysUntil(dateStr);
  if (d<0)  return C.danger;
  if (d<=2) return C.amber;
  return C.success;
}

// ─── SHARED UI ────────────────────────────────────────────────────────────────

function AppHeader(props) {
  return (
    <div style={{ width:"100%", maxWidth:480, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, borderBottom:"1px solid "+C.border, flexShrink:0 }}>
      {props.back
        ? <button onClick={props.back} style={{ background:"none", border:"none", color:C.blue, fontSize:22, cursor:"pointer", padding:"0 6px 0 0", lineHeight:1 }}>{"<"}</button>
        : <img src="/chorehabit-app-tile.svg" alt="CH" style={{ width:32, height:32, borderRadius:8 }} />
      }
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:700, color:props.titleColor||C.accent, letterSpacing:"0.15em", textTransform:"uppercase" }}>{props.title}</div>
        {props.sub && <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{props.sub}</div>}
      </div>
      {props.right}
    </div>
  );
}

function SecHead(props) {
  return (
    <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:props.color||C.accent, marginBottom:10, display:"flex", alignItems:"center", gap:8, ...FM }}>
      {props.title}
      <div style={{ flex:1, height:1, background:C.border }} />
      {props.action && (
        <button onClick={props.action.fn} style={{ background:props.action.bg||C.accentDim, border:"1px solid "+(props.action.color||C.accent), borderRadius:6, color:props.action.color||C.accent, fontSize:10, ...FM, padding:"3px 10px", cursor:"pointer", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase" }}>
          {props.action.label}
        </button>
      )}
    </div>
  );
}

// ─── MARK DOWN SHEET ──────────────────────────────────────────────────────────

function MarkDownSheet(props) {
  var lang=props.lang||"en"; var t=getT(lang);
  var eq = props.eq;
  var cur = props.current;
  var isDown = !!(cur&&cur.reason);
  var s1=useState(cur?(cur.reason||"at_shop"):"at_shop");       var reason=s1[0];     var setReason=s1[1];
  var s2=useState(cur?(cur.reportedBy||""):"");                  var reportedBy=s2[0]; var setReportedBy=s2[1];
  var s3=useState(cur?(cur.returnDate||""):"");                  var retDate=s3[0];    var setRetDate=s3[1];
  var s4=useState(cur?(cur.notes||""):"");                       var notes=s4[0];      var setNotes=s4[1];
  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={props.onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.danger, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"86vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
        <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.danger, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>{isDown?t.updateStatus:t.markAsDown}</div>
          <div style={{ fontSize:12, color:C.dim, marginTop:3, ...FM }}>{emoji(eq.type)} {eq.name}</div>
        </div>
        <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={lbl}>Reason</div>
            <div style={{ display:"flex", gap:8 }}>
              {[{v:"at_shop",l:t.atShop},{v:"waiting_parts",l:t.waitingParts}].map(function(opt) {
                var sel=reason===opt.v;
                return (
                  <div key={opt.v} onClick={function(){ setReason(opt.v); }} style={{ flex:1, background:sel?C.accentDim:C.inputBg, border:"1.5px solid "+(sel?C.accent:C.border), borderRadius:10, padding:"12px 8px", textAlign:"center", cursor:"pointer" }}>
                    <div style={{ fontSize:12, fontWeight:sel?700:400, color:sel?C.accent:C.text, ...FM }}>{opt.l}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Reported By</label>
            <input style={inp} placeholder="Your name" value={reportedBy} onChange={function(e){ setReportedBy(e.target.value); }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Expected Return Date</label>
            <input style={inp} type="date" min={today} value={retDate} onChange={function(e){ setRetDate(e.target.value); }} />
            {retDate && <div style={{ fontSize:11, color:retColor(retDate), ...FM }}>{daysUntil(retDate)===0?"Expected back today":daysUntil(retDate)===1?"Expected back tomorrow":daysUntil(retDate)<0?"Return date passed":"Expected back in "+daysUntil(retDate)+" days"}</div>}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, resize:"none", minHeight:72, lineHeight:1.6 }} rows={3} placeholder="Parts ordered, shop name, what needs fixing..." value={notes} onChange={function(e){ setNotes(e.target.value); }} />
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={props.onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
            <button onClick={function(){ props.onSave({reason:reason,reportedBy:reportedBy.trim()||null,returnDate:retDate||null,notes:notes.trim()||null}); }} style={{ flex:2, background:C.danger, border:"none", borderRadius:10, color:"#fff", fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer", fontWeight:700 }}>{isDown?t.updateStatus:t.markAsDown}</button>
          </div>
          {isDown&&props.onClear && <button onClick={props.onClear} style={{ width:"100%", background:"none", border:"1px solid "+C.success, borderRadius:10, color:C.success, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:11, cursor:"pointer", fontWeight:700 }}>Mark as Back in Service</button>}
        </div>
      </div>
    </div>
  );
}

// ─── ADD LOG SHEET ────────────────────────────────────────────────────────────

function AddLogSheet(props) {
  var lang=props.lang||"en"; var t=getT(lang);
  var eq = props.eq;
  var last = props.last;
  var currentUser=props.currentUser||{name:""};
  var uL = eq.usesMiles?"mi":"hrs";
  var s1=useState("");    var usage=s1[0];  var setUsage=s1[1];
  var s2=useState("");    var fg=s2[0];     var setFg=s2[1];
  var s3=useState("");    var fc=s3[0];     var setFc=s3[1];
  var s4=useState(last?(last.driver||""):(currentUser.name||"")); var drv=s4[0]; var setDrv=s4[1];
  var s5=useState("");    var nts=s5[0];    var setNts=s5[1];
  var s6=useState(today); var edate=s6[0];  var setEdate=s6[1];
  var s7=useState("green"); var status=s7[0]; var setStatus=s7[1];
  var uNum=parseFloat(usage);
  var uValid=usage.trim()!==""&&!isNaN(uNum);
  var delta=(last&&uValid)?uNum-last.usage:null;
  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={props.onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"90vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
        <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>New Log Entry</div>
          <div style={{ fontSize:12, color:C.dim, marginTop:3, ...FM }}>{emoji(eq.type)} {eq.name}</div>
          {last && <div style={{ fontSize:11, color:C.muted, marginTop:2, ...FM }}>Last: {fmtDate(last.date)} at {last.usage.toLocaleString()} {uL}</div>}
        </div>
        <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Entry Date</label>
            <input style={inp} type="date" value={edate} onChange={function(e){ setEdate(e.target.value); }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Current {eq.usesMiles?"Odometer (mi)":"Hours (hrs)"} *</label>
            <input style={{ ...inp, border:"1px solid "+(uValid||!usage?C.border:C.danger) }} type="number" inputMode="numeric" pattern="[0-9]*" min="0" placeholder={last?String(last.usage+1):"0"} value={usage} onChange={function(e){ setUsage(e.target.value); }} />
            {delta!==null && <div style={{ fontSize:11, color:delta>=0?C.success:C.danger, ...FM }}>{delta>=0?"+"+delta.toLocaleString()+" "+uL+" since last entry":"Lower than last entry ("+last.usage.toLocaleString()+" "+uL+")"}</div>}
          </div>
          <div>
            <div style={{ fontSize:10, color:C.teal, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM, marginBottom:8 }}>Fuel Fill-Up (optional)</div>
            <div style={{ display:"flex", gap:10 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
                <label style={lbl}>Gallons</label>
                <input style={inp} type="number" inputMode="decimal" step="0.1" placeholder="0.0" value={fg} onChange={function(e){ setFg(e.target.value); }} />
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
                <label style={lbl}>Cost ($)</label>
                <input style={inp} type="number" inputMode="decimal" step="0.01" placeholder="0.00" value={fc} onChange={function(e){ setFc(e.target.value); }} />
              </div>
            </div>
            {fg&&fc&&parseFloat(fg)>0 && <div style={{ fontSize:11, color:C.teal, marginTop:6, ...FM }}>{"$"+(parseFloat(fc)/parseFloat(fg)).toFixed(3)+"/gal"}</div>}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Driver / Operator (optional)</label>
            <input style={inp} placeholder="e.g. Mike R." value={drv} onChange={function(e){ setDrv(e.target.value); }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <label style={lbl}>Equipment Status *</label>
            <div style={{ display:"flex", gap:8 }}>
              {[{v:"green",l:"Good to Go",c:"#4CAF6E",bg:"rgba(76,175,110,0.1)",desc:"Running fine"},{v:"yellow",l:"Needs Attention",c:"#F5A623",bg:"rgba(245,166,35,0.1)",desc:"Work needed soon"},{v:"red",l:"Down",c:"#E05C5C",bg:"rgba(224,92,92,0.1)",desc:"Out of service"}].map(function(opt) {
                var sel=status===opt.v;
                return (
                  <div key={opt.v} onClick={function(){ setStatus(opt.v); }} style={{ flex:1, background:sel?opt.bg:"#13151F", border:"2px solid "+(sel?opt.c:"#2E3248"), borderRadius:10, padding:"10px 6px", textAlign:"center", cursor:"pointer" }}>
                    <div style={{ width:14, height:14, borderRadius:"50%", background:opt.c, margin:"0 auto 6px", boxShadow:sel?("0 0 8px "+opt.c):"none" }} />
                    <div style={{ fontSize:10, fontWeight:700, color:sel?opt.c:"#6B7094", fontFamily:"'DM Mono','Courier New',monospace", textTransform:"uppercase", letterSpacing:"0.06em" }}>{opt.l}</div>
                  </div>
                );
              })}
            </div>
            {status!=="green" && (
              <div style={{ fontSize:11, color:status==="yellow"?"#F5A623":"#E05C5C", fontFamily:"'DM Mono','Courier New',monospace", marginTop:2 }}>
                {status==="yellow"?"Describe what needs attention:":"Describe what is wrong:"}
              </div>
            )}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Notes {status!=="green"?"*":"(optional)"}</label>
            <textarea style={{ ...inp, resize:"none", minHeight:72, lineHeight:1.6, borderColor:status!=="green"&&!nts.trim()?"#E05C5C":"#2E3248" }} rows={3} placeholder={status==="green"?"Tire pressure, sounds, vibrations, leaks, anything unusual...": status==="yellow"?"What needs attention? When does it need to be done?":"What is wrong? What happened?"} value={nts} onChange={function(e){ setNts(e.target.value); }} />
          </div>
        </div>
        <div style={{ display:"flex", gap:10, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
          <button onClick={props.onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
          <button disabled={!uValid||(status!=="green"&&!nts.trim())} onClick={function(){ if(!uValid)return; if(status!=="green"&&!nts.trim())return; props.onSave({id:genId(),equipmentId:eq.id,equipmentName:eq.name,date:edate,usage:uNum,driver:drv.trim()||null,fuelGallons:fg.trim()||null,fuelCost:fc.trim()||null,notes:nts.trim()||null,status:status}); }} style={{ flex:2, background:(uValid&&(status==="green"||nts.trim()))?C.accent:C.surfaceHi, border:"none", borderRadius:10, color:(uValid&&(status==="green"||nts.trim()))?"#0F1117":C.muted, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:(uValid&&(status==="green"||nts.trim()))?"pointer":"not-allowed", fontWeight:700 }}>Save Entry</button>
        </div>
      </div>
    </div>
  );
}

// ─── ADD REPAIR SHEET ────────────────────────────────────────────────────────

function AddRepairSheet(props) {
  var lang=props.lang||"en"; var t=getT(lang);
  var eq=props.eq; var isAdmin=props.isAdmin;
  var r1=useState("inhouse"); var rtype=r1[0];     var setRtype=r1[1];
  var r2=useState(today);     var rdate=r2[0];     var setRdate=r2[1];
  var r3=useState("");        var whatFixed=r3[0]; var setWhatFixed=r3[1];
  var r4=useState("");        var desc=r4[0];      var setDesc=r4[1];
  var r5=useState("");        var tech=r5[0];      var setTech=r5[1];
  var r6=useState("");        var labor=r6[0];     var setLabor=r6[1];
  var r7=useState("");        var shop=r7[0];      var setShop=r7[1];
  var r8=useState(false);     var isWarr=r8[0];    var setIsWarr=r8[1];
  var r9=useState("");        var retDate=r9[0];   var setRetDate=r9[1];
  var r10=useState(false);    var outSvc=r10[0];   var setOutSvc=r10[1];
  var r11=useState("");       var cost=r11[0];     var setCost=r11[1];
  var r12=useState("");       var notes=r12[0];    var setNotes=r12[1];
  var r13=useState(null);     var rcptImg=r13[0];  var setRcptImg=r13[1];
  var r14=useState("");       var rcptNotes=r14[0];var setRcptNotes=r14[1];
  var r15=useState(false);    var manual=r15[0];   var setManual=r15[1];
  var saveOk=whatFixed.trim().length>0;
  function doSave() {
    if (!saveOk) return;
    props.onSave({ id:genId(), equipmentId:eq.id, repairType:rtype, date:rdate, whatFixed:whatFixed.trim(), description:desc.trim()||null, techName:rtype==="inhouse"?(tech.trim()||null):null, laborHours:rtype==="inhouse"?(labor.trim()||null):null, shop:rtype==="shop"?(shop.trim()||null):null, warrantyWork:rtype==="shop"?isWarr:false, returnDate:retDate||null, outOfService:outSvc, cost:cost.trim()||"0.00", notes:notes.trim()||null, receiptImg:rcptImg||null, receiptNotes:rcptNotes.trim()||null });
  }
  function handleFile(e) {
    var file=e.target.files&&e.target.files[0];
    if (!file) return;
    var reader=new FileReader();
    reader.onload=function(ev){ setRcptImg(ev.target.result); };
    reader.readAsDataURL(file);
  }
  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={props.onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"92vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
        <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.blue, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>Log Repair</div>
          <div style={{ fontSize:12, color:C.dim, marginTop:2, ...FM }}>{emoji(eq.type)} {eq.name}</div>
        </div>
        <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", gap:8 }}>
            {[{v:"inhouse",l:t.inHouse},{v:"shop",l:t.shopDealer}].map(function(opt) {
              var sel=rtype===opt.v;
              return (
                <div key={opt.v} onClick={function(){ setRtype(opt.v); }} style={{ flex:1, background:sel?C.accentDim:C.inputBg, border:"1.5px solid "+(sel?C.accent:C.border), borderRadius:10, padding:"12px 8px", textAlign:"center", cursor:"pointer" }}>
                  <div style={{ fontSize:12, fontWeight:sel?700:400, color:sel?C.accent:C.text, ...FM }}>{opt.l}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
              <label style={lbl}>Date</label>
              <input style={inp} type="date" value={rdate} onChange={function(e){ setRdate(e.target.value); }} />
            </div>
            {rtype==="shop" && (
              <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
                <label style={lbl}>Return Date</label>
                <input style={inp} type="date" value={retDate} onChange={function(e){ setRetDate(e.target.value); }} />
              </div>
            )}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>What Was Repaired *</label>
            <input style={inp} placeholder="e.g. Spindle bearing, hydraulic line, belt..." value={whatFixed} onChange={function(e){ setWhatFixed(e.target.value); }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Description (optional)</label>
            <textarea style={{ ...inp, resize:"none", minHeight:60, lineHeight:1.6 }} rows={2} placeholder="Details about what caused it, how it was fixed..." value={desc} onChange={function(e){ setDesc(e.target.value); }} />
          </div>
          {rtype==="inhouse" && (
            <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, padding:"12px 14px", display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>In-House Details</div>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <label style={lbl}>Technician</label>
                <input style={inp} placeholder="Who did the repair" value={tech} onChange={function(e){ setTech(e.target.value); }} />
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <label style={lbl}>Labor Hours</label>
                <input style={inp} type="number" inputMode="decimal" step="0.5" placeholder="e.g. 2.5" value={labor} onChange={function(e){ setLabor(e.target.value); }} />
              </div>
            </div>
          )}
          {rtype==="shop" && (
            <div style={{ background:C.blueDim, border:"1px solid "+C.blue, borderRadius:10, padding:"12px 14px", display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ fontSize:10, color:C.blue, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Shop Details</div>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <label style={lbl}>Shop / Dealer Name</label>
                <input style={inp} placeholder="e.g. Green Turf Equipment" value={shop} onChange={function(e){ setShop(e.target.value); }} />
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {[{l:t.no,v:false},{l:"Yes - Warranty",v:true}].map(function(opt) {
                  var sel=isWarr===opt.v; var col=opt.v?C.success:C.accent;
                  return (
                    <div key={String(opt.v)} onClick={function(){ setIsWarr(opt.v); }} style={{ flex:1, background:sel?(opt.v?C.successDim:C.accentDim):C.inputBg, border:"1.5px solid "+(sel?col:C.border), borderRadius:10, padding:"10px 8px", textAlign:"center", cursor:"pointer" }}>
                      <div style={{ fontSize:11, fontWeight:sel?700:400, color:sel?col:C.text, ...FM }}>{opt.l}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ borderTop:"1px solid rgba(74,144,217,0.3)", paddingTop:12 }}>
                <div style={{ fontSize:10, color:C.blue, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:700, ...FM, marginBottom:10 }}>Receipt / Invoice</div>
                {!rcptImg&&!manual && (
                  <div style={{ display:"flex", gap:8 }}>
                    <label style={{ flex:1, cursor:"pointer" }}>
                      <input type="file" accept="image/*" capture="environment" style={{ display:"none" }} onChange={handleFile} />
                      <div style={{ background:C.inputBg, border:"1px solid "+C.blue, borderRadius:9, padding:"12px 8px", textAlign:"center" }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.blue, ...FM }}>Scan Receipt</div>
                        <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Camera</div>
                      </div>
                    </label>
                    <div onClick={function(){ setManual(true); }} style={{ flex:1, background:C.inputBg, border:"1px solid "+C.blue, borderRadius:9, padding:"12px 8px", textAlign:"center", cursor:"pointer" }}>
                      <div style={{ fontSize:12, fontWeight:700, color:C.blue, ...FM }}>Enter Manually</div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Type details</div>
                    </div>
                  </div>
                )}
                {rcptImg && (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    <div style={{ position:"relative" }}>
                      <img src={rcptImg} alt="receipt" style={{ width:"100%", borderRadius:8, border:"1px solid "+C.blue, maxHeight:160, objectFit:"cover" }} />
                      <button onClick={function(){ setRcptImg(null); }} style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,0.65)", border:"none", borderRadius:6, color:"#fff", fontSize:11, padding:"4px 8px", cursor:"pointer", ...FM }}>Retake</button>
                    </div>
                    <input style={inp} placeholder="Invoice number, notes..." value={rcptNotes} onChange={function(e){ setRcptNotes(e.target.value); }} />
                  </div>
                )}
                {manual&&!rcptImg && (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ fontSize:11, color:C.blue, fontWeight:700, ...FM }}>Invoice Details</div>
                      <button onClick={function(){ setManual(false); setRcptNotes(""); }} style={{ background:"none", border:"none", color:C.muted, fontSize:11, cursor:"pointer", ...FM }}>Cancel</button>
                    </div>
                    <textarea style={{ ...inp, resize:"none", minHeight:90, lineHeight:1.6, fontSize:12 }} rows={4} placeholder="Invoice #, parts, labor, totals..." value={rcptNotes} onChange={function(e){ setRcptNotes(e.target.value); }} />
                  </div>
                )}
              </div>
            </div>
          )}
          <div style={{ display:"flex", gap:8 }}>
            {[{l:t.active,v:false},{l:t.outOfService,v:true}].map(function(opt) {
              var sel=outSvc===opt.v; var col=opt.v?C.danger:C.success;
              return (
                <div key={String(opt.v)} onClick={function(){ setOutSvc(opt.v); }} style={{ flex:1, background:sel?(opt.v?C.dangerDim:C.successDim):C.inputBg, border:"1.5px solid "+(sel?col:C.border), borderRadius:10, padding:"11px 8px", textAlign:"center", cursor:"pointer" }}>
                  <div style={{ fontSize:11, fontWeight:sel?700:400, color:sel?col:C.text, ...FM }}>{opt.l}</div>
                </div>
              );
            })}
          </div>
          {isAdmin && (
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              <label style={{ ...lbl, color:C.purple }}>Total Cost ($) - Admin Only</label>
              <input style={{ ...inp, border:"1px solid "+C.purple }} type="number" inputMode="decimal" step="0.01" placeholder="0.00" value={cost} onChange={function(e){ setCost(e.target.value); }} />
            </div>
          )}
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Notes (optional)</label>
            <textarea style={{ ...inp, resize:"none", minHeight:60, lineHeight:1.6 }} rows={2} placeholder="Additional details..." value={notes} onChange={function(e){ setNotes(e.target.value); }} />
          </div>
        </div>
        <div style={{ display:"flex", gap:10, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
          <button onClick={props.onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
          <button disabled={!saveOk} onClick={doSave} style={{ flex:2, background:saveOk?C.blue:C.surfaceHi, border:"none", borderRadius:10, color:saveOk?"#fff":C.muted, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:saveOk?"pointer":"not-allowed", fontWeight:700 }}>Save Repair</button>
        </div>
      </div>
    </div>
  );
}

// ─── MARK SERVICE DONE SHEET ──────────────────────────────────────────────────

function MarkServiceDoneSheet(props) {
  var lang=props.lang||"en"; var t=getT(lang);
  var iv=props.iv; var eq=props.eq;
  var uL=eq.usesMiles?"mi":"hrs";
  var s1=useState(today);                 var doneDate=s1[0];  var setDoneDate=s1[1];
  var s2=useState(String(eq.totalUsage)); var doneHrs=s2[0];   var setDoneHrs=s2[1];
  var s3=useState("");                    var notes=s3[0];     var setNotes=s3[1];
  var currentUser=props.currentUser||{name:""};
  var s4=useState(currentUser.name||"");  var who=s4[0];       var setWho=s4[1];
  var doneNum=parseFloat(doneHrs);
  var ok=!isNaN(doneNum)&&doneHrs.trim()!=="";
  var nextDue=ok?doneNum+(parseFloat(iv.intervalHours)||0):null;
  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={props.onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.success, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"88vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
        <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.success, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>Mark as Complete</div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text, marginTop:4, ...FM }}>{iv.task}</div>
          <div style={{ fontSize:12, color:C.dim, marginTop:2, ...FM }}>{emoji(eq.type)} {eq.name}</div>
        </div>
        <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
              <label style={lbl}>Date Completed</label>
              <input style={inp} type="date" value={doneDate} onChange={function(e){ setDoneDate(e.target.value); }} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:5, flex:1 }}>
              <label style={lbl}>{uL} at Service</label>
              <input style={{ ...inp, border:"1px solid "+(ok?C.border:C.danger) }} type="number" inputMode="numeric" pattern="[0-9]*" min="0" placeholder={String(eq.totalUsage)} value={doneHrs} onChange={function(e){ setDoneHrs(e.target.value); }} />
            </div>
          </div>
          {nextDue!==null && (
            <div style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:8, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:12, color:C.success, ...FM }}>Next due at</span>
              <span style={{ fontSize:14, fontWeight:700, color:C.success, ...FM }}>{nextDue.toLocaleString()+" "+uL}</span>
            </div>
          )}
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Performed By (optional)</label>
            <input style={inp} placeholder="Your name or technician" value={who} onChange={function(e){ setWho(e.target.value); }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Notes (optional)</label>
            <textarea style={{ ...inp, resize:"none", minHeight:72, lineHeight:1.6 }} rows={3} placeholder="Parts used, observations, anything worth noting..." value={notes} onChange={function(e){ setNotes(e.target.value); }} />
          </div>
          <div style={{ background:C.surfaceHi, border:"1px solid "+C.border, borderRadius:8, padding:"10px 14px" }}>
            <div style={{ fontSize:11, color:C.muted, ...FM, lineHeight:1.6 }}>Resets this interval and creates a service record automatically.</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
          <button onClick={props.onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
          <button disabled={!ok} onClick={function(){ if(!ok)return; props.onSave({doneDate:doneDate,doneHours:doneNum,notes:notes.trim(),who:who.trim()}); }} style={{ flex:2, background:ok?C.success:C.surfaceHi, border:"none", borderRadius:10, color:ok?"#fff":C.muted, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:ok?"pointer":"not-allowed", fontWeight:700 }}>Confirm Complete</button>
        </div>
      </div>
    </div>
  );
}

// ─── DEFAULT MAINTENANCE INTERVALS ──────────────────────────────────────────

var DEFAULT_INTERVALS = {
  vehicle: [
    { task:"Oil & Filter Change",          intervalHours:5000,  alertMode:"sound",  timeUnit:"usage" },
    { task:"Tire Rotation",                intervalHours:7500,  alertMode:"inapp",  timeUnit:"usage" },
    { task:"Brake Inspection",             intervalHours:15000, alertMode:"inapp",  timeUnit:"usage" },
    { task:"Cabin Air Filter Clean",       intervalHours:5000,  alertMode:"inapp",  timeUnit:"usage" },
    { task:"Cabin Air Filter Replace",     intervalHours:7500,  alertMode:"inapp",  timeUnit:"usage" },
    { task:"Engine Air Filter Clean",      intervalHours:1000,  alertMode:"inapp",  timeUnit:"usage" },
    { task:"Engine Air Filter Replace",    intervalHours:10000, alertMode:"inapp",  timeUnit:"usage" },
  ],
  heavy: [
    { task:"Engine Oil & Filter",          intervalHours:250,   alertMode:"sound",  timeUnit:"usage" },
    { task:"Hydraulic Fluid Check",        intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Greasing / Lubrication",       intervalHours:50,    alertMode:"sound",  timeUnit:"usage" },
    { task:"Engine Air Filter Replace",    intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Cabin Filter Replace",         intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
  ],
  lawn: [
    { task:"Engine Oil Change",            intervalHours:100,   alertMode:"sound",  timeUnit:"usage" },
    { task:"Air Filter Replace",           intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Spark Plug Replace",           intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Hydraulic Fluid Change",       intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Spark Arrestor Clean",         intervalHours:100,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Belt Inspection",              intervalHours:100,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Greasing / Lubrication",       intervalHours:100,   alertMode:"sound",  timeUnit:"usage" },
  ],
  agricultural: [
    { task:"Engine Oil & Filter",          intervalHours:250,   alertMode:"sound",  timeUnit:"usage" },
    { task:"Hydraulic Fluid Check",        intervalHours:500,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Greasing / Lubrication",       intervalHours:50,    alertMode:"sound",  timeUnit:"usage" },
  ],
  genpump: [
    { task:"Engine Oil & Filter",          intervalHours:100,   alertMode:"sound",  timeUnit:"usage" },
    { task:"Air Filter Replace",           intervalHours:200,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Spark Plug Replace",           intervalHours:300,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Spark Arrestor Clean",         intervalHours:100,   alertMode:"inapp",  timeUnit:"usage" },
  ],
  trailer: [
    { task:"Check Lights & Wiring",        intervalHours:7,     alertMode:"inapp",  timeUnit:"days" },
    { task:"Check Brakes",                 intervalHours:7,     alertMode:"inapp",  timeUnit:"days" },
    { task:"Check Ramp Hinges",            intervalHours:7,     alertMode:"inapp",  timeUnit:"days" },
    { task:"Check Tire Pressure & Tread",  intervalHours:7,     alertMode:"sound",  timeUnit:"days" },
    { task:"Wheel Bearing Inspection",     intervalHours:365,   alertMode:"inapp",  timeUnit:"days" },
    { task:"Decking Inspection",           intervalHours:365,   alertMode:"inapp",  timeUnit:"days" },
  ],
  smalleq: [
    { task:"Air Filter Replace",           intervalHours:30,    alertMode:"inapp",  timeUnit:"days" },
    { task:"Spark Plug Replace",           intervalHours:90,    alertMode:"inapp",  timeUnit:"days" },
    { task:"Spark Arrestor Clean",         intervalHours:30,    alertMode:"inapp",  timeUnit:"days" },
    { task:"Fuel Filter Replace",          intervalHours:90,    alertMode:"inapp",  timeUnit:"days" },
    { task:"2-Stroke Oil Stock Check",     intervalHours:7,     alertMode:"sound",  timeUnit:"days" },
    { task:"Bar & Chain Oil Check",        intervalHours:7,     alertMode:"sound",  timeUnit:"days" },
    { task:"Annual Inspection",            intervalHours:365,   alertMode:"inapp",  timeUnit:"days" },
  ],
  other: [
    { task:"General Inspection",           intervalHours:250,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Oil Change",                   intervalHours:100,   alertMode:"sound",  timeUnit:"usage" },
    { task:"Annual Safety Check",          intervalHours:365,   alertMode:"inapp",  timeUnit:"days"  },
  ],
  ev: [
    { task:"Battery Health Check",         intervalHours:90,    alertMode:"sound",  timeUnit:"days"  },
    { task:"Battery Terminal Cleaning",    intervalHours:180,   alertMode:"inapp",  timeUnit:"days"  },
    { task:"Charging Port & Cable Check",  intervalHours:90,    alertMode:"inapp",  timeUnit:"days"  },
    { task:"Tire Pressure Check",          intervalHours:30,    alertMode:"inapp",  timeUnit:"days"  },
    { task:"Grease Fittings",              intervalHours:100,   alertMode:"inapp",  timeUnit:"usage" },
    { task:"Brake Inspection",             intervalHours:200,   alertMode:"sound",  timeUnit:"usage" },
    { task:"Annual Full Inspection",       intervalHours:365,   alertMode:"inapp",  timeUnit:"days"  },
  ],
};


// Subtypes per equipment vertical
var SUBTYPES = {
  vehicle:      [{v:"pickup",l:"Pickup Truck"},{v:"van",l:"Van / Sprinter"},{v:"suv",l:"SUV"},{v:"car",l:"Car / Sedan"},{v:"dump",l:"Dump Truck"},{v:"flatbed",l:"Flatbed Truck"},{v:"semi",l:"Semi / Tractor"},{v:"other",l:"Other"}],
  heavy:        [{v:"skid_steer",l:"Skid Steer"},{v:"compact_track",l:"Compact Track Loader"},{v:"excavator",l:"Excavator"},{v:"bulldozer",l:"Bulldozer"},{v:"wheel_loader",l:"Wheel Loader"},{v:"backhoe",l:"Backhoe"},{v:"grader",l:"Motor Grader"},{v:"telehandler",l:"Telehandler"},{v:"other",l:"Other"}],
  lawn:         [{v:"zero_turn",l:"Zero Turn Mower"},{v:"stand_on",l:"Stand-On Mower"},{v:"walk_behind",l:"Walk-Behind Mower"},{v:"riding",l:"Riding Mower"},{v:"trimmer",l:"String Trimmer"},{v:"blower",l:"Backpack Blower"},{v:"edger",l:"Edger"},{v:"hedge",l:"Hedge Trimmer"},{v:"other",l:"Other"}],
  agricultural: [{v:"tractor",l:"Tractor"},{v:"combine",l:"Combine"},{v:"sprayer",l:"Sprayer"},{v:"baler",l:"Baler"},{v:"planter",l:"Planter"},{v:"disc",l:"Disc / Tillage"},{v:"atv",l:"ATV / UTV"},{v:"other",l:"Other"}],
  genpump:      [{v:"generator",l:"Generator"},{v:"water_pump",l:"Water Pump"},{v:"air_comp",l:"Air Compressor"},{v:"pressure_wash",l:"Pressure Washer"},{v:"other",l:"Other"}],
  ev:           [{v:"zero_turn_ev",l:"Zero Turn (Electric)"},{v:"standon_ev",l:"Stand-On (Electric)"},{v:"walkbehind_ev",l:"Walk-Behind (Electric)"},{v:"other_ev",l:"Other Electric"}],
  trailer:      [{v:"flatbed",l:"Flatbed"},{v:"enclosed",l:"Enclosed"},{v:"dump",l:"Dump Trailer"},{v:"equipment",l:"Equipment Trailer"},{v:"landscaping",l:"Landscaping Trailer"},{v:"other",l:"Other"}],
  smalleq:      [{v:"chainsaw",l:"Chainsaw"},{v:"sprayer",l:"Backpack Sprayer"},{v:"plate_comp",l:"Plate Compactor"},{v:"trimmer",l:"String Trimmer"},{v:"blower",l:"Backpack Blower"},{v:"hedge",l:"Hedge Trimmer"},{v:"edger",l:"Edger"},{v:"other",l:"Other"}],
  other:        [{v:"other",l:"Other"}],
};

function buildDefaultIntervals(type, currentUsage) {
  var templates = DEFAULT_INTERVALS[type] || DEFAULT_INTERVALS.other;
  return templates.map(function(tmpl) {
    return {
      id:           genId(),
      task:         tmpl.task,
      intervalHours:tmpl.intervalHours,
      lastDoneHours:(tmpl.timeUnit==="days")?0:(parseFloat(currentUsage)||0),
      timeUnit:     tmpl.timeUnit||"usage",
      alertMode:    tmpl.alertMode||"inapp"
    };
  });
}


// ─── CAMERA SCAN SHEET ────────────────────────────────────────────────────────
// Uses device camera + Claude vision API to read VIN, serial numbers, and hour meters

function CameraScanSheet(props) {
  var mode = props.mode || "vin"; // "vin" | "serial" | "hours"
  var onResult = props.onResult || function(){};
  var onClose = props.onClose || function(){};

  var cs1=useState(null);    var imgData=cs1[0];   var setImgData=cs1[1];
  var cs2=useState("idle");  var status=cs2[0];    var setStatus=cs2[1];
  var cs3=useState(null);    var result=cs3[0];    var setResult=cs3[1];
  var cs4=useState(null);    var error=cs4[0];     var setError=cs4[1];

  var titles = { vin:"Scan VIN", serial:"Scan Serial Number", hours:"Scan Hour Meter" };
  var hints  = {
    vin:"Point at the VIN plate on the dashboard, door jamb, or windshield. Make sure all 17 characters are visible.",
    serial:"Point at the serial number plate — usually on the frame, engine, or data tag.",
    hours:"Point at the hour meter display on the instrument panel. Make sure the digits are clear."
  };
  var prompts = {
    vin:"Look at this image and find the VIN (Vehicle Identification Number). It is a 17-character alphanumeric code. Return ONLY the VIN characters with no spaces, dashes, or explanation. If you cannot find a clear VIN, return the word NOTFOUND.",
    serial:"Look at this image and find the serial number. It may be labeled as S/N, SN, Serial, or Serial No. Return ONLY the serial number characters with no spaces or explanation. If you cannot find a clear serial number, return the word NOTFOUND.",
    hours:"Look at this image and find the hour meter reading. It shows total engine hours as a number, often with one decimal place. Return ONLY the numeric value with no units or explanation. If you cannot read a clear number, return the word NOTFOUND."
  };

  function handleFile(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function(ev) {
      var data = ev.target.result;
      setImgData(data);
      setStatus("scanning");
      setError(null);
      setResult(null);
      scanWithVision(data);
    };
    reader.readAsDataURL(file);
  }

  function scanWithVision(dataUrl) {
    var base64 = dataUrl.split(",")[1];
    var mediaType = dataUrl.split(";")[0].split(":")[1] || "image/jpeg";

    fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 100,
        messages: [{
          role: "user",
          content: [
            { type:"image", source:{ type:"base64", media_type:mediaType, data:base64 } },
            { type:"text",  text:prompts[mode] }
          ]
        }]
      })
    })
    .then(function(r){ return r.json(); })
    .then(function(data){
      var text = data.content && data.content[0] && data.content[0].text;
      if (!text || text.trim().toUpperCase() === "NOTFOUND" || !text.trim()) {
        setStatus("error");
        setError("Couldn't read the " + (mode==="hours"?"hour meter":mode==="vin"?"VIN":"serial number") + " clearly. Try better lighting or a closer shot.");
      } else {
        var val = text.trim().replace(/\s/g,"").toUpperCase();
        // For hours, allow decimal
        if (mode === "hours") val = text.trim().replace(/[^0-9.]/g,"");
        setResult(val);
        setStatus("done");
      }
    })
    .catch(function(){
      setStatus("error");
      setError("Scan failed. Check your connection and try again.");
    });
  }

  function retake() {
    setImgData(null); setStatus("idle"); setResult(null); setError(null);
  }

  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.92)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:500 }} onClick={onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"90vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />

        {/* Header */}
        <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>{titles[mode]}</div>
          <div style={{ fontSize:11, color:C.dim, marginTop:4, lineHeight:1.5 }}>{hints[mode]}</div>
        </div>

        <div style={{ overflowY:"auto", flex:1, padding:16 }}>

          {/* Idle — show camera button */}
          {status==="idle" && (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <label style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:C.inputBg, border:"2px dashed "+C.border, borderRadius:14, padding:"40px 20px", cursor:"pointer", gap:12 }}>
                <div style={{ fontSize:48 }}>📷</div>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>Take a Photo</div>
                <div style={{ fontSize:11, color:C.muted, textAlign:"center" }}>Tap to open camera or choose a photo from your library</div>
                <input type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display:"none" }} />
              </label>
              <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, padding:"10px 14px", fontSize:11, color:C.accent, lineHeight:1.6 }}>
                💡 Tips: Good lighting, steady hands, fill the frame with the plate or meter. Avoid glare.
              </div>
            </div>
          )}

          {/* Scanning */}
          {status==="scanning" && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:16, padding:"20px 0" }}>
              {imgData && <img src={imgData} alt="scan" style={{ width:"100%", maxHeight:240, objectFit:"contain", borderRadius:10 }} />}
              <div style={{ fontSize:13, color:C.accent, ...FM, fontWeight:700 }}>Reading image...</div>
              <div style={{ fontSize:11, color:C.muted }}>Claude is analyzing your photo</div>
            </div>
          )}

          {/* Error */}
          {status==="error" && (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {imgData && <img src={imgData} alt="scan" style={{ width:"100%", maxHeight:200, objectFit:"contain", borderRadius:10, opacity:0.6 }} />}
              <div style={{ background:C.dangerDim, border:"1px solid "+C.danger, borderRadius:10, padding:"12px 14px", fontSize:12, color:C.danger, lineHeight:1.6 }}>{error}</div>
              <button onClick={retake} style={{ width:"100%", background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, color:C.accent, fontSize:12, ...FM, fontWeight:700, padding:12, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>Try Again</button>
            </div>
          )}

          {/* Done */}
          {status==="done" && result && (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              {imgData && <img src={imgData} alt="scan" style={{ width:"100%", maxHeight:200, objectFit:"contain", borderRadius:10 }} />}
              <div style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:12, padding:"16px 14px" }}>
                <div style={{ fontSize:10, color:C.success, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:8 }}>
                  {mode==="vin"?"VIN Detected":mode==="serial"?"Serial Number Detected":"Hours Detected"}
                </div>
                <div style={{ fontSize:20, fontWeight:700, color:C.text, letterSpacing:"0.1em", ...FM }}>{result}</div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={retake} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.muted, fontSize:12, ...FM, padding:12, cursor:"pointer" }}>Retake</button>
                <button onClick={function(){ onResult(result); onClose(); }} style={{ flex:2, background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, fontWeight:700, padding:12, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>Use This →</button>
              </div>
            </div>
          )}
        </div>

        {/* Cancel */}
        <div style={{ padding:"0 16px", flexShrink:0 }}>
          <button onClick={onClose} style={{ width:"100%", background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.muted, fontSize:12, ...FM, padding:12, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── ADD EQUIPMENT SCREEN ─────────────────────────────────────────────────────

function AddEquipmentScreen(props) {
  var lang=props.lang||"en"; var t=getT(lang);
  var plan=props.plan||{maxEquipment:3,serviceRequests:false,weeklyReports:false,crew:false,vinLookup:false};
  var canVin=plan.vinLookup!==false;

  var SPEC_DB=[
    {make:"Cub Cadet",   model:"ZS1 60",  type:"lawn",        eot:"SAE 10W-30",eoq:"1.7 qt", ofpn:"490-200-0011",afpn:"490-200-0020",ffpn:"",           sp:"RJ19LM",  ts:"18x9.5-8 / 24x12-12",  hot:"",             hoq:""},
    {make:"Cub Cadet",   model:"ZS1 54",  type:"lawn",        eot:"SAE 10W-30",eoq:"1.7 qt", ofpn:"490-200-0011",afpn:"490-200-0020",ffpn:"",           sp:"RJ19LM",  ts:"18x9.5-8 / 24x12-12",  hot:"",             hoq:""},
    {make:"Cub Cadet",   model:"ZT2 60",  type:"lawn",        eot:"SAE 10W-30",eoq:"2.0 qt", ofpn:"490-200-0011",afpn:"490-200-0020",ffpn:"",           sp:"RJ19LM",  ts:"18x9.5-8 / 26x12-12",  hot:"",             hoq:""},
    {make:"John Deere",  model:"Z960M",   type:"lawn",        eot:"SAE 10W-30",eoq:"2.0 qt", ofpn:"GY20577",     afpn:"M131802",     ffpn:"AM116304",   sp:"RC12YC",  ts:"18x8.5-8 / 24x12-12",  hot:"",             hoq:""},
    {make:"John Deere",  model:"Z930M",   type:"lawn",        eot:"SAE 10W-30",eoq:"2.0 qt", ofpn:"GY20577",     afpn:"M131802",     ffpn:"AM116304",   sp:"RC12YC",  ts:"18x8.5-8 / 24x12-12",  hot:"",             hoq:""},
    {make:"John Deere",  model:"Z920M",   type:"lawn",        eot:"SAE 10W-30",eoq:"1.7 qt", ofpn:"GY20577",     afpn:"M131802",     ffpn:"AM116304",   sp:"RC12YC",  ts:"18x8.5-8 / 24x12-12",  hot:"",             hoq:""},
    {make:"Bobcat",      model:"S650",    type:"heavy",       eot:"15W-40",    eoq:"8.0 qt", ofpn:"6667352",     afpn:"7023674",     ffpn:"6667613",    sp:"",         ts:"12x16.5",               hot:"ISO 46 AW",    hoq:"16 gal"},
    {make:"Bobcat",      model:"S770",    type:"heavy",       eot:"15W-40",    eoq:"9.0 qt", ofpn:"6667352",     afpn:"7023674",     ffpn:"6667613",    sp:"",         ts:"14x17.5",               hot:"ISO 46 AW",    hoq:"20 gal"},
    {make:"Bobcat",      model:"T590",    type:"heavy",       eot:"15W-40",    eoq:"8.0 qt", ofpn:"6667352",     afpn:"7023674",     ffpn:"6667613",    sp:"",         ts:"12x16.5",               hot:"ISO 46 AW",    hoq:"16 gal"},
    {make:"Bobcat",      model:"S450",    type:"heavy",       eot:"15W-40",    eoq:"6.5 qt", ofpn:"6667352",     afpn:"7023674",     ffpn:"6667613",    sp:"",         ts:"10x16.5",               hot:"ISO 46 AW",    hoq:"12 gal"},
    {make:"Caterpillar", model:"299D3",   type:"heavy",       eot:"15W-40",    eoq:"10.0 qt",ofpn:"1R-0716",     afpn:"600-185-5100",ffpn:"1R-0756",    sp:"",         ts:"14x17.5",               hot:"Cat HYDO",     hoq:"30 gal"},
    {make:"Caterpillar", model:"257D",    type:"heavy",       eot:"15W-40",    eoq:"8.0 qt", ofpn:"1R-0716",     afpn:"600-185-3100",ffpn:"1R-0756",    sp:"",         ts:"12x16.5",               hot:"Cat HYDO",     hoq:"20 gal"},
    {make:"Kubota",      model:"L3901",   type:"agricultural",eot:"15W-40",    eoq:"6.3 qt", ofpn:"HH150-32430", afpn:"6C090-99280", ffpn:"15521-43560",sp:"",         ts:"7-14 / 12.4-16",        hot:"Kubota UDT2",  hoq:"13 gal"},
    {make:"Kubota",      model:"BX23S",   type:"agricultural",eot:"10W-40",    eoq:"3.7 qt", ofpn:"HH160-32430", afpn:"6C090-99280", ffpn:"15521-43560",sp:"",         ts:"18x8.5-10",             hot:"Kubota UDT2",  hoq:"5 gal"},
    {make:"Honda",       model:"GX390",   type:"genpump",     eot:"SAE 10W-30",eoq:"1.1 qt", ofpn:"",            afpn:"17210-Z0J-010",ffpn:"",          sp:"BPR6ES",   ts:"N/A",                   hot:"",             hoq:""},
    {make:"Honda",       model:"GX270",   type:"genpump",     eot:"SAE 10W-30",eoq:"0.95 qt",ofpn:"",            afpn:"17210-ZE2-822",ffpn:"",          sp:"BPR6ES",   ts:"N/A",                   hot:"",             hoq:""},
    {make:"Kawasaki",    model:"FX730V",  type:"lawn",        eot:"SAE 10W-30",eoq:"1.8 qt", ofpn:"49065-7007",  afpn:"11029-7032",  ffpn:"49019-0034", sp:"RC12YC",   ts:"N/A",                   hot:"",             hoq:""},
    {make:"Kawasaki",    model:"FX850V",  type:"lawn",        eot:"SAE 10W-30",eoq:"2.0 qt", ofpn:"49065-7007",  afpn:"11029-7032",  ffpn:"49019-0034", sp:"RC12YC",   ts:"N/A",                   hot:"",             hoq:""},
  ];

  var TYPES=[
    {value:"vehicle",      label:"Vehicle",         sub:"Car / truck / van",           icon:"\uD83D\uDE97"},
    {value:"heavy",        label:"Heavy Equipment", sub:"Bobcat, excavator, bulldozer", icon:"\uD83C\uDFD7"},
    {value:"lawn",         label:"Lawn & Grounds",  sub:"Zero-turn, walk-behind",       icon:"\uD83C\uDF3F"},
    {value:"agricultural", label:"Agricultural",    sub:"Tractor, combine, sprayer",    icon:"\uD83D\uDE9C"},
    {value:"genpump",      label:"Generator/Pump",  sub:"Generator, compressor, pump",  icon:"\u26A1"},
    {value:"smalleq",      label:"Small Equipment", sub:"Chainsaw, trimmer, blower",    icon:"\uD83E\uDE9A"},
    {value:"trailer",      label:"Trailer",         sub:"Calendar-based inspection",    icon:"\uD83D\uDE9B"},
    {value:"other",        label:"Other",           sub:"Custom type",                  icon:"\uD83D\uDD27"}
  ,
    {value:"ev",           label:"Electric Equipment", sub:"Electric mowers / equipment", icon:"\u26A1"},
  ];

  var s1=useState("step1"); var step=s1[0];       var setStep=s1[1];
  var s2=useState("");      var type=s2[0];       var setType=s2[1];
  var s3=useState("");      var search=s3[0];     var setSearch=s3[1];
  var s4=useState(false);   var loading=s4[0];    var setLoading=s4[1];
  var s5=useState(null);    var lookErr=s5[0];    var setLookErr=s5[1];
  var s6=useState(null);    var autoFilled=s6[0]; var setAutoFilled=s6[1];
  var f1=useState("");  var name=f1[0];    var setName=f1[1];
  var f2=useState("");  var make=f2[0];    var setMake=f2[1];
  var f3=useState("");  var model=f3[0];   var setModel=f3[1];
  var f4=useState("");  var year=f4[0];    var setYear=f4[1];
  var f5=useState("");  var usage=f5[0];   var setUsage=f5[1];
  var f6=useState("");  var vin=f6[0];     var setVin=f6[1];
  var f7=useState("");  var serial=f7[0];  var setSerial=f7[1];
  var f8=useState("");  var eot=f8[0];     var setEot=f8[1];
  var f9=useState("");  var eoq=f9[0];     var setEoq=f9[1];
  var f10=useState(""); var ofpn=f10[0];   var setOfpn=f10[1];
  var f11=useState(""); var afpn=f11[0];   var setAfpn=f11[1];
  var f12=useState(""); var ffpn=f12[0];   var setFfpn=f12[1];
  var f13=useState(""); var spk=f13[0];    var setSpk=f13[1];
  var f14=useState(""); var ts=f14[0];     var setTs=f14[1];
  var f15=useState(""); var hot=f15[0];    var setHot=f15[1];
  var f16=useState(""); var hoq=f16[0];    var setHoq=f16[1];
  var f17=useState(today); var lastSvcDate=f17[0];  var setLastSvcDate=f17[1];
  var f18=useState("");     var lastSvcUsage=f18[0]; var setLastSvcUsage=f18[1];
  var f19=useState("hrs");  var lastSvcUnit=f19[0];  var setLastSvcUnit=f19[1];
  var f20=useState("");     var lastSvcNotes=f20[0]; var setLastSvcNotes=f20[1];
  var f21=useState("");     var subtype=f21[0];      var setSubtype=f21[1];

  // Step 3 - interval review
  var s7=useState([]); var intervals=s7[0]; var setIntervals=s7[1];
  var s8=useState(false); var saveAsDefault=s8[0]; var setSaveAsDefault=s8[1];
  var s9=useState(null);  var scanMode=s9[0];     var setScanMode=s9[1];

  var isVehicle=type==="vehicle";
  var isTrailer=type==="trailer";
  var isSmallEq=type==="smalleq";
  var usesMiles=isVehicle;
  var needsHydraulic=type==="heavy"||type==="agricultural";
  var saveOk=name.trim()&&type&&(isTrailer||isSmallEq||usage.trim());

  function applySpec(spec) {
    if (spec.make)  setMake(spec.make);  if (spec.model) setModel(spec.model);
    if (spec.eot)   setEot(spec.eot);    if (spec.eoq)   setEoq(spec.eoq);
    if (spec.ofpn)  setOfpn(spec.ofpn);  if (spec.afpn)  setAfpn(spec.afpn);
    if (spec.ffpn)  setFfpn(spec.ffpn);  if (spec.sp)    setSpk(spec.sp);
    if (spec.ts)    setTs(spec.ts);      if (spec.hot)   setHot(spec.hot);
    if (spec.hoq)   setHoq(spec.hoq);
    setAutoFilled(spec.make+" "+spec.model);
  }

  function lookupVIN() {
    var v=search.trim().toUpperCase();
    if (v.length<11){ setLookErr("Enter at least 11 characters of the VIN."); return; }
    setLoading(true); setLookErr(null);
    fetch("/.netlify/functions/vin-lookup?vin="+v)
      .then(function(r){ return r.json(); })
      .then(function(data){
        setLoading(false);
        var r=data.Results&&data.Results[0];
        if (!r||!r.Make||r.Make==="") { setLookErr("VIN not found. Fill in manually below."); setStep("step2"); return; }
        var mk=r.Make?r.Make.charAt(0).toUpperCase()+r.Make.slice(1).toLowerCase():"";
        setVin(v); setMake(mk); setModel(r.Model||""); setYear(r.ModelYear||"");
        if (!name.trim()) setName(mk+" "+(r.Model||"")+" #1");
        setAutoFilled(mk+" "+(r.Model||"")+" "+(r.ModelYear||""));
        setStep("step2");
      })
      .catch(function(){ setLoading(false); setLookErr("Lookup failed. Fill in manually below."); setStep("step2"); });
  }

  function lookupSerial() {
    var q=search.trim().toUpperCase(); setSerial(q);
    var matched=null;
    SPEC_DB.forEach(function(spec){
      if (matched) return;
      var words=q.replace(/[^A-Z0-9]/g," ").split(" ").filter(function(w){ return w.length>1; });
      var mk=spec.make.toUpperCase(); var md=spec.model.toUpperCase();
      var mkHit=words.some(function(w){ return mk.indexOf(w)>=0||w.indexOf(mk.split(" ")[0])>=0; });
      var mdHit=words.some(function(w){ return md.indexOf(w)>=0||w===md; });
      if (mkHit&&mdHit) matched=spec;
    });
    if (matched) applySpec(matched);
    setStep("step2");
  }

  function handleContinue() {
    if (!type) return;
    if (isTrailer||isSmallEq) { setStep("step2"); return; }
    if (!search.trim()) { setStep("step2"); return; }
    if (isVehicle) lookupVIN(); else lookupSerial();
  }

  function goToIntervals() {
    if (!name.trim()) return;
    var ivBase = (String(lastSvcUsage).trim()!=="") ? (parseFloat(lastSvcUsage)||0) : (parseFloat(usage)||0);
    var defaults;
    // #6 — load saved defaults for this type if they exist
    try {
      var saved = localStorage.getItem("ch_default_iv_"+type);
      if (saved) {
        var parsed = JSON.parse(saved);
        defaults = parsed.map(function(tmpl) {
          return { id:genId(), task:tmpl.task, intervalHours:tmpl.intervalHours, lastDoneHours:(tmpl.timeUnit==="days")?0:ivBase, timeUnit:tmpl.timeUnit||"usage", alertMode:tmpl.alertMode||"inapp", _skip:false };
        });
      }
    } catch(e){}
    if (!defaults) defaults = buildDefaultIntervals(type, ivBase);
    setIntervals(defaults);
    setStep("step3");
  }

  function toggleInterval(id) {
    setIntervals(intervals.map(function(iv){ return iv.id===id?Object.assign({},iv,{_skip:!iv._skip}):iv; }));
  }

  function doSave() {
    if (!saveOk) return;
    var finalIntervals = intervals.filter(function(iv){ return !iv._skip; }).map(function(iv){
      var clean = Object.assign({},iv); delete clean._skip; return clean;
    });
    // #6 — save intervals as default for this type if checked
    if (saveAsDefault) {
      try { localStorage.setItem("ch_default_iv_"+type, JSON.stringify(finalIntervals.map(function(iv){ return {task:iv.task,intervalHours:iv.intervalHours,timeUnit:iv.timeUnit,alertMode:iv.alertMode}; }))); } catch(e){}
    }
    props.onSave({
      id:genId(), name:name.trim(), type:type, subtype:subtype||"", make:make.trim(), model:model.trim(), year:year.trim(),
      usesMiles:usesMiles, totalUsage:parseFloat(usage)||0,
      status:"active", outOfService:null,
      vin:vin.trim(), serialNumber:serial.trim(),
      engineOilType:eot.trim(), engineOilQty:eoq.trim(), oilFilterPN:ofpn.trim(),
      airFilterPN:afpn.trim(), fuelFilterPN:ffpn.trim(), sparkPlug:spk.trim(),
      tireSize:ts.trim(), hydraulicOilType:hot.trim(), hydraulicOilQty:hoq.trim(),
      lastServiceDate:lastSvcDate||today, lastServiceUsage:parseFloat(lastSvcUsage)||0, lastServiceNotes:lastSvcNotes.trim(),
      customParts:[], maintenanceIntervals:finalIntervals, warranty:null
    });
  }

  // ── STEP 1: Type + ID ────────────────────────────────────────────────────
  if (step==="step1") {
    return (
      <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:80 }}>
        <AppHeader back={props.onBack} title="Add Equipment" />
        <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0" }}>
          <SecHead title="Equipment Type" />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:20 }}>
            {TYPES.map(function(tp) {
              var sel=type===tp.value;
              return (
                <div key={tp.value} onClick={function(){ setType(tp.value); }} style={{ background:sel?C.accentDim:C.inputBg, border:"1.5px solid "+(sel?C.accent:C.border), borderRadius:10, padding:"12px 6px", display:"flex", flexDirection:"column", alignItems:"center", gap:5, cursor:"pointer" }}>
                  <span style={{ fontSize:22 }}>{tp.icon}</span>
                  <span style={{ fontSize:9.5, fontWeight:sel?700:400, color:sel?C.accent:C.dim, textAlign:"center", textTransform:"uppercase" }}>{tp.label}</span>
                </div>
              );
            })}
          </div>
          {/* Subtype selector */}
          {type && SUBTYPES[type] && SUBTYPES[type].length > 1 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, marginBottom:8, ...FM }}>Subtype</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {SUBTYPES[type].map(function(st) {
                  var sel=subtype===st.v;
                  return <button key={st.v} onClick={function(){ setSubtype(st.v); }} style={{ background:sel?C.accentDim:"none", border:"1px solid "+(sel?C.accent:C.border), borderRadius:20, padding:"5px 14px", cursor:"pointer", fontSize:11, color:sel?C.accent:C.muted, ...FM, fontWeight:sel?700:400 }}>{st.l}</button>;
                })}
              </div>
            </div>
          )}
          {type && !isTrailer && !isSmallEq && !canVin && (
            <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:4 }}>VIN & Spec Lookup</div>
              <div style={{ fontSize:11, color:C.dim, lineHeight:1.6 }}>Upgrade to One Man Band or higher to use VIN lookup and auto-fill equipment specs.</div>
            </div>
          )}
          {type && !isTrailer && !isSmallEq && canVin && (
            <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:12 }}>
              {scanMode && (
                <CameraScanSheet
                  mode={scanMode}
                  onResult={function(val){
                    setSearch(val);
                    setScanMode(null);
                  }}
                  onClose={function(){ setScanMode(null); }}
                />
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <label style={lbl}>{isVehicle?"VIN Number -- auto-fills make, model, year":"Serial number or search by make and model"}</label>
                <div style={{ display:"flex", gap:8 }}>
                  <input style={{ ...inp, flex:1, border:"1px solid "+(lookErr?C.danger:C.border) }}
                    placeholder={isVehicle?"1FTFW1ET5MFC12345":"e.g. Bobcat S650 or serial B3N416648"}
                    value={search} onChange={function(e){ setSearch(e.target.value); setLookErr(null); }} />
                  <button onClick={function(){ setScanMode(isVehicle?"vin":"serial"); }}
                    style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:8, color:C.accent, fontSize:18, padding:"0 14px", cursor:"pointer", flexShrink:0 }}>
                    📷
                  </button>
                </div>
                <div style={{ fontSize:11, color:C.muted, ...FM }}>{isVehicle?"Tap 📷 to scan VIN with camera, or type manually.":"Tap 📷 to scan serial number, or search by make and model."}</div>
                {lookErr && <div style={{ fontSize:11, color:C.danger, ...FM }}>{lookErr}</div>}
              </div>
              <button onClick={function(){ setStep("step2"); }} style={{ background:"none", border:"none", color:C.muted, fontSize:11, ...FM, cursor:"pointer", textAlign:"left", padding:0 }}>Skip -- fill in manually</button>
            </div>
          )}
          {isSmallEq && (
            <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:12, color:C.accent, ...FM, lineHeight:1.6, fontWeight:700, marginBottom:4 }}>Calendar-based tracking</div>
              <div style={{ fontSize:11, color:C.dim, lineHeight:1.6 }}>Small equipment is tracked by days — not hours. Standard intervals for air filter, spark plug, spark arrestor, and fuel filter will be set up automatically.</div>
            </div>
          )}
          {isTrailer && (
            <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:12, color:C.accent, ...FM, lineHeight:1.6 }}>Trailers use calendar-based tracking. Weekly and annual inspection reminders will be set up automatically.</div>
            </div>
          )}
        </div>
        <div style={{ position:"fixed", bottom:0, width:"100%", maxWidth:480, background:C.bg, borderTop:"1px solid "+C.border, padding:"12px 16px", display:"flex", gap:10, boxSizing:"border-box" }}>
          <button onClick={props.onBack} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:13, cursor:"pointer" }}>Cancel</button>
          <button disabled={!type||loading} onClick={handleContinue} style={{ flex:2, background:(type&&!loading)?C.accent:C.surfaceHi, border:"none", borderRadius:10, color:(type&&!loading)?"#0F1117":C.muted, fontSize:12, ...FM, letterSpacing:"0.12em", textTransform:"uppercase", padding:13, cursor:(type&&!loading)?"pointer":"not-allowed", fontWeight:700 }}>{loading?"Looking Up...":"Continue"}</button>
        </div>
      </div>
    );
  }

  // ── STEP 2: Identity + Specs ─────────────────────────────────────────────
  if (step==="step2") {
    return (
      <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:80 }}>
        <AppHeader back={function(){ setStep("step1"); }} title="Equipment Details" sub="Review and edit -- all fields optional except Name" />
        <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0", display:"flex", flexDirection:"column", gap:12 }}>
          {autoFilled && <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, padding:"10px 14px", fontSize:11, color:C.accent, ...FM }}>Specs auto-filled for {autoFilled}. Edit any field for aftermarket parts or preference.</div>}
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Identity</div>
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              <label style={lbl}>Name * (how it appears in the app)</label>
              <input style={inp} placeholder={isTrailer?"e.g. 20ft Flatbed Trailer #1":isVehicle?"e.g. Ford F-250 #1":isSmallEq?"e.g. Stihl MS 271, Echo SRM-225 #1":"e.g. Bobcat S650, ZS1 Mower #3"} value={name} onChange={function(e){ setName(e.target.value); }} />
            </div>
            {!isTrailer && !isSmallEq && (
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Make</label><input style={inp} placeholder="Ford" value={make} onChange={function(e){ setMake(e.target.value); }} /></div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Model</label><input style={inp} placeholder="F-250" value={model} onChange={function(e){ setModel(e.target.value); }} /></div>
                <div style={{ maxWidth:72, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Year</label><input style={inp} placeholder="2023" maxLength={4} value={year} onChange={function(e){ setYear(e.target.value); }} /></div>
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              <label style={lbl}>{isTrailer?"Serial / VIN #":(isVehicle?"VIN #":"Serial #")}</label>
              <input style={inp} placeholder={isVehicle?"1FTFW1ET5MFC12345":"SN-1A2B3C4D"} value={isVehicle?vin:serial} onChange={function(e){ isVehicle?setVin(e.target.value):setSerial(e.target.value); }} />
            </div>
            {!isTrailer && !isSmallEq && (
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                {scanMode && (
                  <CameraScanSheet
                    mode="hours"
                    onResult={function(val){ setUsage(val); setScanMode(null); }}
                    onClose={function(){ setScanMode(null); }}
                  />
                )}
                <label style={lbl}>Current {usesMiles?"Mileage (mi)":"Hours (hrs)"} *</label>
                <div style={{ display:"flex", gap:8 }}>
                  <input style={{ ...inp, flex:1 }} type="number" inputMode="numeric" pattern="[0-9]*" placeholder="0" min="0" value={usage} onChange={function(e){ setUsage(e.target.value); }} />
                  {!usesMiles && <button onClick={function(){ setScanMode("hours"); }} style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:8, color:C.accent, fontSize:18, padding:"0 14px", cursor:"pointer", flexShrink:0 }}>📷</button>}
                </div>
                {!usesMiles && <div style={{ fontSize:10, color:C.muted, ...FM }}>Tap 📷 to scan hour meter with camera</div>}
              </div>
            )}
          </div>
          {!isTrailer && !isSmallEq && (
            <div style={{ background:C.surface, border:"1px solid "+C.teal, borderLeft:"3px solid "+C.teal, borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ fontSize:10, color:C.teal, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Engine Oil</div>
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:2, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Type / Grade</label><input style={inp} placeholder="e.g. SAE 10W-30" value={eot} onChange={function(e){ setEot(e.target.value); }} /></div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Capacity</label><input style={inp} placeholder="1.7 qt" value={eoq} onChange={function(e){ setEoq(e.target.value); }} /></div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Oil Filter P/N</label><input style={inp} placeholder="e.g. Fram PH6607" value={ofpn} onChange={function(e){ setOfpn(e.target.value); }} /></div>
            </div>
          )}
          {!isTrailer && (
            <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Filters and Ignition</div>
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Air Filter P/N</label><input style={inp} placeholder="e.g. KN-1009" value={afpn} onChange={function(e){ setAfpn(e.target.value); }} /></div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Fuel Filter P/N</label><input style={inp} placeholder="e.g. FF5052" value={ffpn} onChange={function(e){ setFfpn(e.target.value); }} /></div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Spark Plug</label><input style={inp} placeholder="e.g. RC12YC" value={spk} onChange={function(e){ setSpk(e.target.value); }} /></div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Tire Size</label><input style={inp} placeholder="e.g. 18x9.5-8" value={ts} onChange={function(e){ setTs(e.target.value); }} /></div>
              </div>
            </div>
          )}
          {needsHydraulic && (
            <div style={{ background:C.surface, border:"1px solid "+C.blue, borderLeft:"3px solid "+C.blue, borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ fontSize:10, color:C.blue, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Hydraulic System</div>
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:2, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Hydraulic Oil Type</label><input style={inp} placeholder="e.g. ISO 46 AW" value={hot} onChange={function(e){ setHot(e.target.value); }} /></div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Capacity</label><input style={inp} placeholder="16 gal" value={hoq} onChange={function(e){ setHoq(e.target.value); }} /></div>
              </div>
            </div>
          )}
          {isTrailer && (
            <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, padding:"12px 14px" }}>
              <div style={{ fontSize:11, color:C.accent, ...FM, lineHeight:1.6 }}>Default trailer inspection intervals will be set up in the next step. You can enable or disable each one.</div>
            </div>
          )}
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:16, display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ fontSize:10, color:C.success, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Last Service (optional)</div>
            <div style={{ fontSize:11, color:C.muted, ...FM }}>Sets the baseline for maintenance interval tracking.</div>
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              <label style={lbl}>Date of Last Service</label>
              <input style={inp} type="date" value={lastSvcDate} onChange={function(e){ setLastSvcDate(e.target.value); }} />
            </div>
            {!isTrailer && (
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <label style={lbl}>Usage at Last Service</label>
                <div style={{ display:"flex", gap:8 }}>
                  <input style={{ ...inp, flex:1 }} type="number" inputMode="numeric" placeholder="0" value={lastSvcUsage} onChange={function(e){ setLastSvcUsage(e.target.value); }} />
                  <div style={{ display:"flex", background:C.inputBg, border:"1px solid "+C.border, borderRadius:8, overflow:"hidden", flexShrink:0 }}>
                    <button onClick={function(){ setLastSvcUnit("hrs"); }} style={{ background:lastSvcUnit==="hrs"?C.accent:"transparent", border:"none", color:lastSvcUnit==="hrs"?"#0F1117":C.muted, fontSize:11, ...FM, fontWeight:700, padding:"0 14px", cursor:"pointer" }}>hrs</button>
                    <button onClick={function(){ setLastSvcUnit("mi"); }} style={{ background:lastSvcUnit==="mi"?C.accent:"transparent", border:"none", color:lastSvcUnit==="mi"?"#0F1117":C.muted, fontSize:11, ...FM, fontWeight:700, padding:"0 14px", cursor:"pointer" }}>mi</button>
                  </div>
                </div>
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              <label style={lbl}>Notes (optional)</label>
              <input style={inp} placeholder="What was done at last service..." value={lastSvcNotes} onChange={function(e){ setLastSvcNotes(e.target.value); }} />
            </div>
          </div>
        </div>
        <div style={{ position:"fixed", bottom:0, width:"100%", maxWidth:480, background:C.bg, borderTop:"1px solid "+C.border, padding:"12px 16px", display:"flex", gap:10, boxSizing:"border-box" }}>
          <button onClick={function(){ setStep("step1"); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:13, cursor:"pointer" }}>Back</button>
          <button disabled={!name.trim()} onClick={goToIntervals} style={{ flex:2, background:name.trim()?C.accent:C.surfaceHi, border:"none", borderRadius:10, color:name.trim()?"#0F1117":C.muted, fontSize:12, ...FM, letterSpacing:"0.12em", textTransform:"uppercase", padding:13, cursor:name.trim()?"pointer":"not-allowed", fontWeight:700 }}>Next -- Maintenance Schedule</button>
        </div>
      </div>
    );
  }

  // ── STEP 3: Default Maintenance Intervals ────────────────────────────────
  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:80 }}>
      <AppHeader back={function(){ setStep("step2"); }} title="Maintenance Schedule" sub="Tap to enable or disable -- all editable after saving" />
      <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0" }}>
        <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
          <div style={{ fontSize:12, color:C.accent, ...FM, fontWeight:700, marginBottom:4 }}>Default intervals for {name}</div>
          <div style={{ fontSize:11, color:C.dim, lineHeight:1.5 }}>These are standard intervals for {TYPES.filter(function(tp){ return tp.value===type; })[0]&&TYPES.filter(function(tp){ return tp.value===type; })[0].label} equipment. Toggle off any that don't apply. You can add, edit, or remove intervals anytime from the equipment profile.</div>
        </div>
        {intervals.map(function(iv) {
          var enabled = !iv._skip;
          return (
            <div key={iv.id} onClick={function(){ toggleInterval(iv.id); }} style={{ background:enabled?C.surface:C.inputBg, border:"1px solid "+(enabled?C.accent:C.border), borderRadius:12, padding:"14px 16px", marginBottom:8, cursor:"pointer", display:"flex", alignItems:"center", gap:12, opacity:enabled?1:0.5 }}>
              <div style={{ width:22, height:22, borderRadius:6, border:"2px solid "+(enabled?C.accent:C.border), background:enabled?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {enabled && <span style={{ color:"#0F1117", fontSize:13, fontWeight:900, lineHeight:1 }}>✓</span>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:enabled?C.text:C.muted, ...FM }}>{iv.task}</div>
                <div style={{ fontSize:11, color:enabled?C.muted:"#444", marginTop:2 }}>
                  {iv.timeUnit==="days"
                    ? "Every "+iv.intervalHours+(iv.intervalHours===7?" week":iv.intervalHours===365?" year":" days")
                    : "Every "+iv.intervalHours.toLocaleString()+" "+(usesMiles?"mi":"hrs")
                  }
                </div>
              </div>
              <div style={{ fontSize:9, color:enabled?C.accent:C.border, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em" }}>{iv.alertMode}</div>
            </div>
          );
        })}
        <div style={{ marginTop:8, textAlign:"center" }}>
          <button onClick={function(){ setIntervals(intervals.map(function(iv){ return Object.assign({},iv,{_skip:false}); })); }} style={{ background:"none", border:"none", color:C.muted, fontSize:11, ...FM, cursor:"pointer", marginRight:16 }}>Enable All</button>
          <button onClick={function(){ setIntervals(intervals.map(function(iv){ return Object.assign({},iv,{_skip:true}); })); }} style={{ background:"none", border:"none", color:C.muted, fontSize:11, ...FM, cursor:"pointer" }}>Disable All</button>
        </div>
      </div>
      <div style={{ position:"fixed", bottom:0, width:"100%", maxWidth:480, background:C.bg, borderTop:"1px solid "+C.border, padding:"10px 16px 12px", boxSizing:"border-box" }}>
        {/* #6 Batch entry — save intervals as default for next unit */}
        <div onClick={function(){ setSaveAsDefault(!saveAsDefault); }} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10, cursor:"pointer" }}>
          <div style={{ width:20, height:20, borderRadius:5, border:"2px solid "+(saveAsDefault?C.accent:C.border), background:saveAsDefault?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            {saveAsDefault && <span style={{ color:"#0F1117", fontSize:12, fontWeight:900, lineHeight:1 }}>✓</span>}
          </div>
          <span style={{ fontSize:11, color:saveAsDefault?C.accent:C.muted, ...FM }}>Save these intervals as default for next {type} I add</span>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={function(){ setStep("step2"); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:13, cursor:"pointer" }}>Back</button>
          <button onClick={doSave} style={{ flex:2, background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, letterSpacing:"0.12em", textTransform:"uppercase", padding:13, cursor:"pointer", fontWeight:700 }}>
            Save Equipment ({intervals.filter(function(iv){ return !iv._skip; }).length} intervals)
          </button>
        </div>
      </div>
    </div>
  );
}


function ServiceRequestSheet(props) {
  var eq=props.eq; var currentUser=props.currentUser; var onSave=props.onSave; var onClose=props.onClose;
  var lang=props.lang||"en"; var t=getT(lang);
  var s1=useState(""); var title=s1[0];    var setTitle=s1[1];
  var s2=useState(""); var details=s2[0];  var setDetails=s2[1];
  var s3=useState("routine"); var priority=s3[0]; var setPriority=s3[1];
  var saveOk=title.trim().length>0;
  function doSave() {
    if (!saveOk) return;
    onSave({
      id:genId(), equipmentId:eq.id, equipmentName:eq.name,
      requestedBy:currentUser.name, requestedById:currentUser.id,
      title:title.trim(), details:details.trim(), priority:priority,
      status:"pending", createdAt:new Date().toISOString(), resolvedAt:null, resolvedBy:null, adminNotes:""
    });
  }
  return (
    <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={onClose}>
      <div style={{ background:C.surface, border:"1px solid "+C.purple, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"88vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
        <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
        <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.purple, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>Request Service</div>
          <div style={{ fontSize:12, color:C.dim, marginTop:2, ...FM }}>{emoji(eq.type)} {eq.name}</div>
        </div>
        <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>What needs service? *</label>
            <input style={inp} placeholder="e.g. Oil change due, strange noise, hydraulic issue..." value={title} onChange={function(e){ setTitle(e.target.value); }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={lbl}>Priority</div>
            <div style={{ display:"flex", gap:8 }}>
              {[{v:"routine",l:"Routine",c:C.success},{v:"soon",l:"Soon",c:C.amber},{v:"urgent",l:"Urgent",c:C.danger}].map(function(opt) {
                var sel=priority===opt.v;
                return (
                  <div key={opt.v} onClick={function(){ setPriority(opt.v); }} style={{ flex:1, background:sel?"rgba(0,0,0,0.2)":C.inputBg, border:"1.5px solid "+(sel?opt.c:C.border), borderRadius:10, padding:"10px 8px", textAlign:"center", cursor:"pointer" }}>
                    <div style={{ fontSize:12, fontWeight:sel?700:400, color:sel?opt.c:C.text, ...FM }}>{opt.l}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={lbl}>Additional Details (optional)</label>
            <textarea style={{ ...inp, resize:"none", minHeight:80, lineHeight:1.6 }} rows={3} placeholder="Describe the issue, when it started, anything unusual..." value={details} onChange={function(e){ setDetails(e.target.value); }} />
          </div>
        </div>
        <div style={{ display:"flex", gap:10, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
          <button onClick={onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
          <button disabled={!saveOk} onClick={doSave} style={{ flex:2, background:saveOk?C.purple:C.surfaceHi, border:"none", borderRadius:10, color:saveOk?"#fff":C.muted, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:saveOk?"pointer":"not-allowed", fontWeight:700 }}>Submit Request</button>
        </div>
      </div>
    </div>
  );
}

// ─── DOWN PAGE ────────────────────────────────────────────────────────────────

function DownPage(props) {
  var equipment=props.equipment; var onUpdate=props.onUpdate; var onBack=props.onBack;
  var isAdmin=props.isAdmin!==undefined?props.isAdmin:true;
  var lang=props.lang||"en"; var t=getT(lang);
  var p1=useState(null); var selEq=p1[0]; var setSelEq=p1[1];
  var downList=equipment.filter(function(e){ return e.status==="out_of_service"||e.status==="waiting_parts"; });
  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {selEq && (
        <MarkDownSheet eq={selEq} current={selEq.outOfService||null} lang={lang}
          onSave={function(d){ onUpdate(Object.assign({},selEq,{status:d.reason,outOfService:d})); setSelEq(null); }}
          onClear={function(){ onUpdate(Object.assign({},selEq,{status:"active",outOfService:null})); setSelEq(null); }}
          onClose={function(){ setSelEq(null); }} />
      )}
      <AppHeader back={onBack} title="Out of Service" titleColor={C.danger} sub={downList.length+" piece"+(downList.length!==1?"s":"")+" currently down"} />
      <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0" }}>
        {downList.length===0
          ? <div style={{ textAlign:"center", padding:"60px 20px" }}><div style={{ fontSize:14, fontWeight:700, color:C.success, marginBottom:6 }}>All equipment is active</div><div style={{ fontSize:12, color:C.muted }}>Nothing is currently out of service.</div></div>
          : downList.map(function(eq) {
            var oos=eq.outOfService||{}; var col=oosColor(oos.reason); var diff=oos.returnDate?daysUntil(oos.returnDate):null;
            return (
              <div key={eq.id} onClick={function(){ setSelEq(eq); }} style={{ background:C.surface, border:"1.5px solid "+C.danger, borderRadius:14, padding:"16px", marginBottom:12, cursor:"pointer" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                  <span style={{ fontSize:26, flexShrink:0 }}>{emoji(eq.type)}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{eq.name}</div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{eq.make} {eq.model} {eq.year}</div>
                  </div>
                  <div style={{ background:"rgba(224,92,92,0.15)", border:"1px solid "+col, borderRadius:8, padding:"4px 10px", fontSize:10, color:col, fontWeight:700, textTransform:"uppercase", flexShrink:0 }}>{oosLabel(oos.reason, t)}</div>
                </div>
                <div style={{ background:C.surfaceHi, borderRadius:10, padding:"12px 14px", display:"flex", flexDirection:"column", gap:8 }}>
                  {oos.reportedBy && <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Reported By</span><span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{oos.reportedBy}</span></div>}
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Return Date</span>
                    <span style={{ fontSize:12, color:retColor(oos.returnDate), fontWeight:700 }}>{diff===null?"Not set":diff===0?"Today":diff<0?"Overdue":diff+"d -- "+fmtDate(oos.returnDate)}</span>
                  </div>
                  {oos.notes && <div style={{ borderTop:"1px solid "+C.border, paddingTop:8, fontSize:12, color:C.dim, lineHeight:1.5 }}>{oos.notes}</div>}
                </div>
                <div style={{ marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ fontSize:11, color:C.muted, ...FM }}>Tap to update or clear status</div>
                  <div style={{ fontSize:13, color:C.danger, fontWeight:700 }}>{">"}</div>
                </div>
              </div>
            );
          })
        }
      </div>
    </div>
  );
}

// ─── LOGBOOK SCREEN ───────────────────────────────────────────────────────────

function LogbookScreen(props) {
  var equipment=props.equipment; var logs=props.logs; var reminders=props.reminders;
  var onAddLog=props.onAddLog; var onBack=props.onBack;
  var currentUser=props.currentUser||{name:""};
  var lang=props.lang||"en"; var t=getT(lang);
  var p1=useState(null);  var logFor=p1[0];  var setLogFor=p1[1];
  var p2=useState(false); var showLog=p2[0]; var setShowLog=p2[1];
  var sorted=logs.slice().sort(function(a,b){ return b.date.localeCompare(a.date); });
  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {showLog&&logFor && (
        <AddLogSheet eq={logFor} lang={lang} currentUser={currentUser}
          last={logs.filter(function(l){ return l.equipmentId===logFor.id; }).sort(function(a,b){ return b.date.localeCompare(a.date); })[0]||null}
          onSave={function(entry){ onAddLog(entry); setShowLog(false); setLogFor(null); }}
          onClose={function(){ setShowLog(false); }} />
      )}
      <AppHeader back={onBack} title="Logbook" />
      <div style={{ width:"100%", maxWidth:480, padding:"0 16px" }}>
        <div style={{ marginTop:16, display:"flex", gap:8 }}>
          <div style={{ flex:1, position:"relative" }}>
            <select value={logFor?logFor.id:""} onChange={function(e){ var eq=equipment.filter(function(x){ return x.id===e.target.value; })[0]; setLogFor(eq||null); }} style={{ ...inp, appearance:"none", cursor:"pointer", paddingRight:32, color:logFor?C.text:C.muted }}>
              <option value="" disabled style={{ background:C.inputBg }}>Select equipment...</option>
              {equipment.map(function(eq) {
                var ll=logs.filter(function(l){ return l.equipmentId===eq.id; }).sort(function(a,b){ return b.date.localeCompare(a.date); })[0];
                var db=ll?daysSince(ll.date):null;
                return <option key={eq.id} value={eq.id} style={{ background:C.inputBg, color:C.text }}>{eq.name+(db!==null?" -- "+(db===0?"today":db+"d ago"):" -- no entries")}</option>;
              })}
            </select>
            <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", color:C.muted, fontSize:10 }}>v</span>
          </div>
          <button onClick={function(){ if(logFor) setShowLog(true); }} disabled={!logFor} style={{ background:logFor?C.accent:C.surfaceHi, border:"none", borderRadius:8, color:logFor?"#0F1117":C.muted, fontSize:12, ...FM, fontWeight:700, padding:"0 18px", cursor:logFor?"pointer":"not-allowed", textTransform:"uppercase", letterSpacing:"0.08em", flexShrink:0 }}>+ Log</button>
        </div>
        {reminders.filter(function(r){ return r.alertMode!=="off"; }).length>0 && (
          <div style={{ marginTop:20 }}>
            <SecHead title="Log Reminders" color={C.blue} />
            {reminders.filter(function(r){ return r.alertMode!=="off"; }).map(function(rem) {
              var eq=equipment.filter(function(e){ return e.id===rem.equipmentId; })[0]; if (!eq) return null;
              var nl=remNext(rem); var sl=remSched(rem); var urgent=nl==="Today"||nl==="Tomorrow"||nl==="Overdue";
              return (
                <div key={rem.id} onClick={function(){ setLogFor(eq); setShowLog(true); }} style={{ display:"flex", alignItems:"center", gap:10, background:C.surface, border:"1px solid "+(urgent?C.accent:C.border), borderRadius:12, padding:"11px 14px", marginBottom:8, cursor:"pointer" }}>
                  <span style={{ fontSize:18 }}>{emoji(eq.type)}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{eq.name}</div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{sl}</div>
                  </div>
                  <div style={{ fontSize:11, fontWeight:700, color:urgent?C.accent:C.dim }}>{nl} →</div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop:20 }}>
          <SecHead title="Recent Entries" color={C.dim} />
          {sorted.slice(0,10).map(function(log) {
            var eq=equipment.filter(function(e){ return e.id===log.equipmentId; })[0]; if (!eq) return null;
            var uL=eq.usesMiles?"mi":"hrs"; var db=daysSince(log.date);
            return (
              <div key={log.id} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"11px 14px", marginBottom:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                  <div><div style={{ fontSize:13, fontWeight:700, color:C.text }}>{eq.name}</div><div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{db===0?"Today":db===1?"Yesterday":db+"d ago"}{log.driver?" -- "+log.driver:""}</div></div>
                  <div style={{ fontSize:14, fontWeight:700, color:C.accent }}>{log.usage.toLocaleString()} {uL}</div>
                </div>
                {log.notes && <div style={{ marginTop:6, fontSize:11, color:C.dim, lineHeight:1.4 }}>{log.notes}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── SERVICE REQUESTS SCREEN ──────────────────────────────────────────────────

function ServiceRequestsScreen(props) {
  var requests=props.requests||[]; var onUpdate=props.onUpdate; var onBack=props.onBack;
  var isAdmin=props.isAdmin; var currentUser=props.currentUser; var equipment=props.equipment;
  var lang=props.lang||"en"; var t=getT(lang);
  var s1=useState("pending"); var filter=s1[0]; var setFilter=s1[1];
  var s2=useState(null);      var managing=s2[0]; var setManaging=s2[1];
  var s3=useState("");        var adminNotes=s3[0]; var setAdminNotes=s3[1];

  var myRequests = isAdmin ? requests : requests.filter(function(r){ return r.requestedById===currentUser.id; });
  var filtered = filter==="all" ? myRequests : myRequests.filter(function(r){ return r.status===filter; });

  function resolve(req) {
    var updated = Object.assign({},req,{ status:"resolved", resolvedAt:new Date().toISOString(), resolvedBy:currentUser.name, adminNotes:adminNotes });
    onUpdate(requests.map(function(r){ return r.id===req.id?updated:r; }));
    setManaging(null); setAdminNotes("");
  }
  function dismiss(req) {
    var updated = Object.assign({},req,{ status:"dismissed", resolvedAt:new Date().toISOString(), resolvedBy:currentUser.name, adminNotes:adminNotes });
    onUpdate(requests.map(function(r){ return r.id===req.id?updated:r; }));
    setManaging(null); setAdminNotes("");
  }

  var priorityColor = { routine:C.success, soon:C.amber, urgent:C.danger };

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {managing && (
        <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:300 }} onClick={function(){ setManaging(null); }}>
          <div style={{ background:C.surface, border:"1px solid "+C.purple, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"80vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
            <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.purple, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>Manage Request</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginTop:4 }}>{managing.title}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{managing.equipmentName} -- {managing.requestedBy}</div>
            </div>
            <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
              {managing.details && <div style={{ background:C.surfaceHi, borderRadius:10, padding:"10px 14px", fontSize:12, color:C.dim, lineHeight:1.5 }}>{managing.details}</div>}
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <label style={lbl}>Admin Notes (optional)</label>
                <textarea style={{ ...inp, resize:"none", minHeight:72, lineHeight:1.6 }} rows={3} placeholder="Parts ordered, scheduled for, notes for the crew..." value={adminNotes} onChange={function(e){ setAdminNotes(e.target.value); }} />
              </div>
            </div>
            <div style={{ display:"flex", gap:8, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
              <button onClick={function(){ dismiss(managing); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.muted, fontSize:11, ...FM, letterSpacing:"0.08em", textTransform:"uppercase", padding:11, cursor:"pointer" }}>Dismiss</button>
              <button onClick={function(){ resolve(managing); }} style={{ flex:2, background:C.success, border:"none", borderRadius:10, color:"#fff", fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer", fontWeight:700 }}>Mark Resolved</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ width:"100%", maxWidth:480, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, borderBottom:"1px solid "+C.border }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:C.blue, fontSize:22, cursor:"pointer", padding:"0 6px 0 0", lineHeight:1 }}>{"<"}</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.purple, letterSpacing:"0.15em", textTransform:"uppercase" }}>Service Requests</div>
          <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{requests.filter(function(r){ return r.status==="pending"; }).length} pending</div>
        </div>
      </div>
      <div style={{ width:"100%", maxWidth:480, padding:"12px 16px 0" }}>
        <div style={{ display:"flex", gap:6, marginBottom:14 }}>
          {["pending","resolved","all"].map(function(f) {
            var sel=filter===f;
            return <button key={f} onClick={function(){ setFilter(f); }} style={{ background:sel?C.purpleDim:"none", border:"1px solid "+(sel?C.purple:C.border), borderRadius:20, padding:"5px 14px", cursor:"pointer", fontSize:10, color:sel?C.purple:C.muted, ...FM, fontWeight:sel?700:400, textTransform:"uppercase", letterSpacing:"0.06em" }}>{f}</button>;
          })}
        </div>
        {filtered.length===0 && <div style={{ textAlign:"center", padding:"40px 0", color:C.muted, fontSize:13, ...FM }}>No {filter} requests.</div>}
        {filtered.map(function(req) {
          var pCol = priorityColor[req.priority]||C.muted;
          var isPending = req.status==="pending";
          return (
            <div key={req.id} style={{ background:C.surface, border:"1px solid "+(isPending?C.purple:C.border), borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                    <div style={{ background:"rgba(0,0,0,0.2)", border:"1px solid "+pCol, borderRadius:4, padding:"1px 8px", fontSize:9, color:pCol, fontWeight:700, textTransform:"uppercase" }}>{req.priority}</div>
                    {req.autoGenerated && <div style={{ background:"rgba(74,144,217,0.15)", border:"1px solid "+C.blue, borderRadius:4, padding:"1px 8px", fontSize:9, color:C.blue, fontWeight:700, textTransform:"uppercase" }}>From Log</div>}
                    <div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>{req.status}</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{req.title}</div>
                  <div style={{ fontSize:11, color:C.blue, marginTop:2 }}>{req.equipmentName}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>By {req.requestedBy} -- {fmtDate(req.createdAt.split("T")[0])}</div>
                  {req.details && <div style={{ fontSize:11, color:C.dim, marginTop:4, lineHeight:1.4 }}>{req.details}</div>}
                  {req.adminNotes && <div style={{ fontSize:11, color:C.teal, marginTop:4, fontStyle:"italic" }}>Admin: {req.adminNotes}</div>}
                </div>
                {isAdmin&&isPending && (
                  <button onClick={function(){ setManaging(req); setAdminNotes(""); }} style={{ background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:8, color:C.purple, fontSize:11, ...FM, fontWeight:700, padding:"6px 12px", cursor:"pointer", flexShrink:0 }}>Manage</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── WEEKLY REPORT SCREEN ─────────────────────────────────────────────────────

function WeeklyReportScreen(props) {
  var equipment=props.equipment; var logs=props.logs; var repairs=props.repairs;
  var onBack=props.onBack; var lang=props.lang||"en";
  var s1=useState(0); var weekOffset=s1[0]; var setWeekOffset=s1[1];
  var s2=useState({showHours:true,showCost:true,showOnTime:true,showUsage:true}); var opts=s2[0]; var setOpts=s2[1];
  var s3=useState(null); var usageEqFilter=s3[0]; var setUsageEqFilter=s3[1];

  var now = new Date();
  var weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() - (weekOffset*7));
  weekStart.setHours(0,0,0,0);
  var weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate()+6); weekEnd.setHours(23,59,59,999);
  var wsStr = weekStart.toISOString().split("T")[0];
  var weStr = weekEnd.toISOString().split("T")[0];

  var weekRepairs = repairs.filter(function(r){ return r.date>=wsStr&&r.date<=weStr; });
  var weekLogs    = logs.filter(function(l){ return l.date>=wsStr&&l.date<=weStr; });

  // On-time vs late services
  var onTime=0; var late=0;
  weekRepairs.forEach(function(r) {
    var eq = equipment.filter(function(e){ return e.id===r.equipmentId; })[0];
    if (!eq) return;
    var iv = eq.maintenanceIntervals.filter(function(iv){ return iv.task===r.whatFixed; })[0];
    if (!iv) return;
    var dueAt = (parseFloat(iv.lastDoneHours)||0) + (parseFloat(iv.intervalHours)||0);
    var doneAt = r.doneAtHours || eq.totalUsage;
    if (doneAt <= dueAt) onTime++; else late++;
  });

  // Cost breakdown
  var maintCost=0; var repairCost=0;
  weekRepairs.forEach(function(r) {
    var c = parseFloat(r.cost)||0;
    if (r.whatFixed&&r.repairType==="inhouse") maintCost+=c; else repairCost+=c;
  });

  // Labor hours (maintenance only — kept separate from logged use hours)
  var totalLabor=0;
  weekRepairs.forEach(function(r){ totalLabor+=parseFloat(r.laborHours)||0; });

  // #13 — Fleet-wide logged use hours per equipment
  var eqUsageMap = {};
  equipment.forEach(function(eq){ eqUsageMap[eq.id] = { eq:eq, logs:[], delta:0 }; });
  weekLogs.forEach(function(log) {
    if (eqUsageMap[log.equipmentId]) eqUsageMap[log.equipmentId].logs.push(log);
  });
  // Calculate hours/miles delta for each piece of equipment this week
  Object.keys(eqUsageMap).forEach(function(id) {
    var item = eqUsageMap[id];
    if (item.logs.length >= 2) {
      var sorted = item.logs.slice().sort(function(a,b){ return a.date.localeCompare(b.date); });
      item.delta = sorted[sorted.length-1].usage - sorted[0].usage;
    } else if (item.logs.length === 1) {
      item.delta = 0; // only one log, can't compute delta
    }
  });
  var eqWithLogs = Object.values(eqUsageMap).filter(function(item){ return item.logs.length > 0; });
  var filteredUsage = usageEqFilter ? eqWithLogs.filter(function(item){ return item.eq.id===usageEqFilter; }) : eqWithLogs;
  var fleetTotalHrs = eqWithLogs.reduce(function(sum, item){ return sum + (item.eq.usesMiles?0:Math.max(0,item.delta)); }, 0);
  var fleetTotalMi  = eqWithLogs.reduce(function(sum, item){ return sum + (item.eq.usesMiles?Math.max(0,item.delta):0); }, 0);

  function toggleOpt(k) { setOpts(function(o){ var n=Object.assign({},o); n[k]=!n[k]; return n; }); }

  var label = weekOffset===0?"This Week":weekOffset===1?"Last Week":weekOffset+" Weeks Ago";

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      <div style={{ width:"100%", maxWidth:480, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, borderBottom:"1px solid "+C.border }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:C.blue, fontSize:22, cursor:"pointer", padding:"0 6px 0 0", lineHeight:1 }}>{"<"}</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase" }}>Weekly Report</div>
          <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{fmtDate(wsStr)} -- {fmtDate(weStr)}</div>
        </div>
      </div>
      <div style={{ width:"100%", maxWidth:480, padding:"12px 16px 0" }}>
        {/* Week nav */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:C.surface, borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
          <button onClick={function(){ setWeekOffset(weekOffset+1); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:8, color:C.dim, fontSize:18, cursor:"pointer", padding:"2px 12px", lineHeight:1 }}>{"<"}</button>
          <div style={{ fontSize:12, fontWeight:700, color:C.accent, ...FM }}>{label}</div>
          <button onClick={function(){ if(weekOffset>0) setWeekOffset(weekOffset-1); }} disabled={weekOffset===0} style={{ background:"none", border:"1px solid "+(weekOffset===0?C.surfaceHi:C.border), borderRadius:8, color:weekOffset===0?C.surfaceHi:C.dim, fontSize:18, cursor:weekOffset===0?"not-allowed":"pointer", padding:"2px 12px", lineHeight:1 }}>{">"}</button>
        </div>
        {/* Options toggles */}
        <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
          {[{k:"showUsage",l:"Usage Hrs"},{k:"showOnTime",l:"On-Time"},{k:"showHours",l:"Labor Hrs"},{k:"showCost",l:"Cost"}].map(function(o) {
            var sel=opts[o.k];
            return <button key={o.k} onClick={function(){ toggleOpt(o.k); }} style={{ background:sel?C.accentDim:"none", border:"1px solid "+(sel?C.accent:C.border), borderRadius:20, padding:"5px 14px", cursor:"pointer", fontSize:10, color:sel?C.accent:C.muted, ...FM, fontWeight:sel?700:400, textTransform:"uppercase", letterSpacing:"0.06em" }}>{o.l}</button>;
          })}
        </div>
        {/* Summary cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:16 }}>
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14 }}>
            <div style={{ fontSize:24, fontWeight:700, color:C.accent }}>{weekRepairs.length}</div>
            <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginTop:2 }}>Services Done</div>
          </div>
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14 }}>
            <div style={{ fontSize:24, fontWeight:700, color:C.blue }}>{weekLogs.length}</div>
            <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginTop:2 }}>Log Entries</div>
          </div>
        </div>
        {/* #13 Fleet-wide usage hours */}
        {opts.showUsage && eqWithLogs.length>0 && (
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14, marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Logged Use This Week</div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={function(){ setUsageEqFilter(null); }} style={{ background:!usageEqFilter?C.accentDim:"none", border:"1px solid "+(usageEqFilter?C.border:C.accent), borderRadius:12, padding:"3px 10px", cursor:"pointer", fontSize:9, color:!usageEqFilter?C.accent:C.muted, ...FM, fontWeight:!usageEqFilter?700:400 }}>Fleet</button>
                {eqWithLogs.map(function(item) {
                  var sel=usageEqFilter===item.eq.id;
                  return <button key={item.eq.id} onClick={function(){ setUsageEqFilter(sel?null:item.eq.id); }} style={{ background:sel?C.accentDim:"none", border:"1px solid "+(sel?C.accent:C.border), borderRadius:12, padding:"3px 10px", cursor:"pointer", fontSize:9, color:sel?C.accent:C.muted, ...FM, fontWeight:sel?700:400 }}>{item.eq.name.split(" ").slice(0,2).join(" ")}</button>;
                })}
              </div>
            </div>
            {!usageEqFilter && (
              <div style={{ display:"flex", gap:10, marginBottom:12 }}>
                {fleetTotalHrs>0 && <div style={{ flex:1, background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:700, color:C.accent }}>{fleetTotalHrs.toFixed(1)}</div>
                  <div style={{ fontSize:10, color:C.accent, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:2 }}>Fleet Hrs</div>
                </div>}
                {fleetTotalMi>0 && <div style={{ flex:1, background:C.blueDim, border:"1px solid "+C.blue, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:700, color:C.blue }}>{fleetTotalMi.toLocaleString()}</div>
                  <div style={{ fontSize:10, color:C.blue, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:2 }}>Fleet Mi</div>
                </div>}
              </div>
            )}
            {filteredUsage.map(function(item) {
              var lastLog = item.logs.slice().sort(function(a,b){ return b.date.localeCompare(a.date); })[0];
              var uL = item.eq.usesMiles?"mi":"hrs";
              return (
                <div key={item.eq.id} style={{ background:C.surfaceHi, borderRadius:10, padding:"10px 14px", marginBottom:6, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:18, flexShrink:0 }}>{emoji(item.eq.type)}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{item.eq.name}</div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{item.logs.length} log entr{item.logs.length===1?"y":"ies"} this week</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:14, fontWeight:700, color:C.accent }}>{lastLog.usage.toLocaleString()} {uL}</div>
                    {item.delta>0 && <div style={{ fontSize:10, color:C.success, marginTop:1 }}>+{item.delta.toLocaleString()} {uL}</div>}
                  </div>
                </div>
              );
            })}
            {filteredUsage.length===0 && <div style={{ textAlign:"center", padding:"10px 0", color:C.muted, fontSize:12 }}>No logs for selected equipment.</div>}
          </div>
        )}

        {/* On-time tracking */}
        {opts.showOnTime && (onTime+late)>0 && (
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM, marginBottom:10 }}>Service Timing</div>
            <div style={{ display:"flex", gap:12, marginBottom:10 }}>
              <div style={{ flex:1, background:C.successDim, border:"1px solid "+C.success, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:700, color:C.success }}>{onTime}</div>
                <div style={{ fontSize:10, color:C.success, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:2 }}>On Time</div>
              </div>
              <div style={{ flex:1, background:C.dangerDim, border:"1px solid "+C.danger, borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:700, color:C.danger }}>{late}</div>
                <div style={{ fontSize:10, color:C.danger, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:2 }}>Late</div>
              </div>
            </div>
            {(onTime+late)>0 && (
              <div style={{ height:8, background:C.surfaceHi, borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:8, background:C.success, width:(onTime/(onTime+late)*100)+"%" }} />
              </div>
            )}
          </div>
        )}
        {/* Labor hours — maintenance/repair only, separate from usage */}
        {opts.showHours && totalLabor>0 && (
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:10, color:C.teal, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM, marginBottom:8 }}>Maintenance Labor Hours</div>
            <div style={{ fontSize:28, fontWeight:700, color:C.teal }}>{totalLabor.toFixed(1)}</div>
            <div style={{ fontSize:11, color:C.muted }}>Hours spent on maintenance and repairs — not logged use hours</div>
          </div>
        )}
        {/* Cost */}
        {opts.showCost && (maintCost+repairCost)>0 && (
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:10, color:C.purple, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM, marginBottom:10 }}>Cost Breakdown</div>
            <div style={{ display:"flex", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:700, color:C.success }}>${maintCost.toFixed(2)}</div>
                <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Maintenance</div>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:700, color:C.danger }}>${repairCost.toFixed(2)}</div>
                <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Repairs</div>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:700, color:C.accent }}>${(maintCost+repairCost).toFixed(2)}</div>
                <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>Total</div>
              </div>
            </div>
          </div>
        )}
        {/* Service log */}
        {weekRepairs.length>0 && (
          <div>
            <SecHead title="Services This Week" color={C.dim} />
            {weekRepairs.map(function(r) {
              var eq=equipment.filter(function(e){ return e.id===r.equipmentId; })[0];
              return (
                <div key={r.id} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:10, padding:"10px 14px", marginBottom:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{r.whatFixed}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{eq?eq.name:"Unknown"} -- {fmtDate(r.date)}</div>
                    </div>
                    {r.cost&&r.cost!=="0.00" && <div style={{ fontSize:12, fontWeight:700, color:C.purple }}>${r.cost}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {weekRepairs.length===0&&weekLogs.length===0 && (
          <div style={{ textAlign:"center", padding:"40px 0", color:C.muted, fontSize:13, ...FM }}>No activity recorded this week.</div>
        )}
      </div>
    </div>
  );
}

// ─── SERVICES LIBRARY SCREEN ──────────────────────────────────────────────────

function ServicesLibraryScreen(props) {
  var servicesLib=props.servicesLib; var onUpdate=props.onUpdate; var isAdmin=props.isAdmin; var onBack=props.onBack;
  var lang=props.lang||"en"; var t=getT(lang);
  var TYPES=[{value:"vehicle",label:"Vehicle"},{value:"heavy",label:"Heavy"},{value:"lawn",label:"Lawn"},{value:"ev",label:"Electric"},{value:"agricultural",label:"Agricultural"},{value:"genpump",label:"Gen/Pump"},{value:"trailer",label:"Trailer"},{value:"smalleq",label:"Small Equip."},{value:"other",label:"Other"}];
  var s1=useState(null); var editSv=s1[0]; var setEditSv=s1[1];
  var s2=useState(null); var confirmDel=s2[0]; var setConfirmDel=s2[1];
  var s3=useState("all"); var filter=s3[0]; var setFilter=s3[1];
  function saveSv() {
    if (!editSv.name.trim()||!editSv.intervalHours||!editSv.types.length) return;
    var item={id:editSv.id||genId(),name:editSv.name.trim(),intervalHours:parseFloat(editSv.intervalHours)||0,types:editSv.types,notes:editSv.notes.trim()};
    if (editSv.id) onUpdate(servicesLib.map(function(s){ return s.id===editSv.id?item:s; }));
    else           onUpdate(servicesLib.concat([item]));
    setEditSv(null);
  }
  function toggleType(v) { setEditSv(function(sv){ var n=Object.assign({},sv); n.types=sv.types.indexOf(v)>=0?sv.types.filter(function(tp){ return tp!==v; }):sv.types.concat([v]); return n; }); }
  function setField(k,v) { setEditSv(function(sv){ var n=Object.assign({},sv); n[k]=v; return n; }); }
  var filtered=filter==="all"?servicesLib:servicesLib.filter(function(sv){ return sv.types.indexOf(filter)>=0; });
  var saveOk=editSv&&editSv.name.trim()&&editSv.intervalHours&&editSv.types.length>0;
  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {editSv && (
        <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:300 }} onClick={function(){ setEditSv(null); }}>
          <div style={{ background:C.surface, border:"1px solid "+C.accent, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"90vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
            <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>{editSv.id?"Edit Service":"Add Service"}</div>
            </div>
            <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Service Name *</label><input style={inp} placeholder="e.g. Engine Oil Change" value={editSv.name} onChange={function(e){ setField("name",e.target.value); }} /></div>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Default Interval (hrs or mi) *</label><input style={inp} type="number" inputMode="numeric" min="1" placeholder="100" value={editSv.intervalHours} onChange={function(e){ setField("intervalHours",e.target.value); }} /></div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <div style={lbl}>Applies To *</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {TYPES.map(function(tp) {
                    var sel=editSv.types.indexOf(tp.value)>=0;
                    return <div key={tp.value} onClick={function(){ toggleType(tp.value); }} style={{ background:sel?C.accentDim:C.inputBg, border:"1.5px solid "+(sel?C.accent:C.border), borderRadius:8, padding:"7px 12px", cursor:"pointer", fontSize:11, fontWeight:sel?700:400, color:sel?C.accent:C.text, ...FM }}>{tp.label}</div>;
                  })}
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Notes (optional)</label><textarea style={{ ...inp, resize:"none", minHeight:60, lineHeight:1.6 }} rows={2} placeholder="Tips, specs, what to check..." value={editSv.notes} onChange={function(e){ setField("notes",e.target.value); }} /></div>
            </div>
            <div style={{ display:"flex", gap:10, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
              <button onClick={function(){ setEditSv(null); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
              <button disabled={!saveOk} onClick={saveSv} style={{ flex:2, background:saveOk?C.accent:C.surfaceHi, border:"none", borderRadius:10, color:saveOk?"#0F1117":C.muted, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:saveOk?"pointer":"not-allowed", fontWeight:700 }}>{editSv.id?"Save Changes":"Add Service"}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ width:"100%", maxWidth:480, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, borderBottom:"1px solid "+C.border }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:C.blue, fontSize:22, cursor:"pointer", padding:"0 6px 0 0", lineHeight:1 }}>{"<"}</button>
        <div style={{ flex:1 }}><div style={{ fontSize:13, fontWeight:700, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase" }}>Services Library</div><div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{servicesLib.length} services</div></div>
        {isAdmin && <button onClick={function(){ setEditSv({id:null,name:"",intervalHours:"",types:[],notes:""}); }} style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:8, color:C.accent, fontSize:10, ...FM, fontWeight:700, padding:"6px 10px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>+ Add</button>}
      </div>
      <div style={{ width:"100%", maxWidth:480, padding:"12px 16px 0" }}>
        <div style={{ display:"flex", gap:6, overflowX:"auto", marginBottom:14, paddingBottom:4 }}>
          {[{value:"all",label:"All"}].concat(TYPES).map(function(tp) {
            var sel=filter===tp.value;
            return <button key={tp.value} onClick={function(){ setFilter(tp.value); }} style={{ background:sel?C.accentDim:"none", border:"1px solid "+(sel?C.accent:C.border), borderRadius:20, padding:"5px 12px", cursor:"pointer", fontSize:10, color:sel?C.accent:C.muted, ...FM, fontWeight:sel?700:400, whiteSpace:"nowrap", flexShrink:0 }}>{tp.label}</button>;
          })}
        </div>
        {filtered.length===0 && <div style={{ textAlign:"center", padding:"40px 0", color:C.muted, fontSize:13, ...FM }}>No services in this category.</div>}
        {filtered.map(function(sv) {
          var isDel=confirmDel===sv.id;
          return (
            <div key={sv.id} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{sv.name}</div>
                  <div style={{ fontSize:11, color:C.accent, fontWeight:600, marginTop:3, ...FM }}>Every {sv.intervalHours} hrs / mi</div>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:6 }}>
                    {sv.types.map(function(tp) {
                      var ti=TYPES.filter(function(x){ return x.value===tp; })[0];
                      return <span key={tp} style={{ background:C.surfaceHi, border:"1px solid "+C.border, borderRadius:4, padding:"1px 7px", fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>{ti?ti.label:tp}</span>;
                    })}
                  </div>
                  {sv.notes && <div style={{ fontSize:11, color:C.dim, marginTop:5, lineHeight:1.4 }}>{sv.notes}</div>}
                </div>
                {isAdmin&&!isDel && (
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    <button onClick={function(){ setEditSv(Object.assign({},sv,{intervalHours:String(sv.intervalHours)})); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:7, color:C.dim, fontSize:11, ...FM, padding:"4px 10px", cursor:"pointer" }}>Edit</button>
                    <button onClick={function(){ setConfirmDel(sv.id); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:7, color:C.danger, fontSize:11, ...FM, padding:"4px 10px", cursor:"pointer", opacity:0.8 }}>Del</button>
                  </div>
                )}
                {isDel && (
                  <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
                    <span style={{ fontSize:11, color:C.danger, ...FM }}>Delete?</span>
                    <button onClick={function(){ onUpdate(servicesLib.filter(function(s){ return s.id!==sv.id; })); setConfirmDel(null); }} style={{ background:C.danger, border:"none", borderRadius:6, color:"#fff", fontSize:11, ...FM, padding:"4px 10px", cursor:"pointer", fontWeight:700 }}>Yes</button>
                    <button onClick={function(){ setConfirmDel(null); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:6, color:C.dim, fontSize:11, ...FM, padding:"4px 10px", cursor:"pointer" }}>No</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── EDIT SPECS FORM ─────────────────────────────────────────────────────────

function EditSpecsForm(props) {
  var eq=props.eq; var onSave=props.onSave; var onClose=props.onClose;
  var isHeavy=eq.type==="heavy"||eq.type==="agricultural"||eq.type==="genpump";
  var isVehicle=eq.type==="vehicle";
  var f1=useState(eq.make||"");              var make=f1[0];   var setMake=f1[1];
  var f2=useState(eq.model||"");             var model=f2[0];  var setModel=f2[1];
  var f3=useState(eq.year||"");              var year=f3[0];   var setYear=f3[1];
  var f4=useState(eq.vin||"");               var vin=f4[0];    var setVin=f4[1];
  var f5=useState(eq.serialNumber||"");      var serial=f5[0]; var setSerial=f5[1];
  var f6=useState(eq.engineOilType||"");     var eot=f6[0];    var setEot=f6[1];
  var f7=useState(eq.engineOilQty||"");      var eoq=f7[0];    var setEoq=f7[1];
  var f8=useState(eq.oilFilterPN||"");       var ofpn=f8[0];   var setOfpn=f8[1];
  var f9=useState(eq.airFilterPN||"");       var afpn=f9[0];   var setAfpn=f9[1];
  var f10=useState(eq.fuelFilterPN||"");     var ffpn=f10[0];  var setFfpn=f10[1];
  var f11=useState(eq.sparkPlug||"");        var spk=f11[0];   var setSpk=f11[1];
  var f12=useState(eq.tireSize||"");         var ts=f12[0];    var setTs=f12[1];
  var f13=useState(eq.hydraulicOilType||""); var hot=f13[0];   var setHot=f13[1];
  var f14=useState(eq.hydraulicOilQty||"");  var hoq=f14[0];   var setHoq=f14[1];
  function doSave() {
    onSave(Object.assign({},eq,{ make:make.trim(),model:model.trim(),year:year.trim(),vin:vin.trim(),serialNumber:serial.trim(),engineOilType:eot.trim(),engineOilQty:eoq.trim(),oilFilterPN:ofpn.trim(),airFilterPN:afpn.trim(),fuelFilterPN:ffpn.trim(),sparkPlug:spk.trim(),tireSize:ts.trim(),hydraulicOilType:hot.trim(),hydraulicOilQty:hoq.trim() }));
  }
  var si = { background:C.inputBg, border:"1px solid "+C.border, borderRadius:8, padding:"9px 10px", color:C.text, fontSize:13, fontFamily:"'DM Mono','Courier New',monospace", outline:"none", width:"100%", boxSizing:"border-box" };
  var lb = { fontSize:10, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, fontFamily:"'DM Mono','Courier New',monospace" };
  return (
    <div style={{ overflowY:"auto", flex:1 }}>
      <div style={{ padding:"16px 16px 0", display:"flex", flexDirection:"column", gap:12 }}>
        <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Identity</div>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Make</label><input style={si} value={make} onChange={function(e){ setMake(e.target.value); }} /></div>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Model</label><input style={si} value={model} onChange={function(e){ setModel(e.target.value); }} /></div>
            <div style={{ maxWidth:68, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Year</label><input style={si} maxLength={4} value={year} onChange={function(e){ setYear(e.target.value); }} /></div>
          </div>
          {isVehicle
            ? <div style={{ display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>VIN #</label><input style={si} value={vin} onChange={function(e){ setVin(e.target.value); }} /></div>
            : <div style={{ display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Serial #</label><input style={si} value={serial} onChange={function(e){ setSerial(e.target.value); }} /></div>
          }
        </div>
        <div style={{ background:C.surface, border:"1px solid "+C.teal, borderLeft:"3px solid "+C.teal, borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ fontSize:10, color:C.teal, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Engine Oil</div>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ flex:2, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Type / Grade</label><input style={si} placeholder="e.g. SAE 10W-30" value={eot} onChange={function(e){ setEot(e.target.value); }} /></div>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Capacity</label><input style={si} placeholder="1.7 qt" value={eoq} onChange={function(e){ setEoq(e.target.value); }} /></div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Oil Filter P/N</label><input style={si} placeholder="e.g. Fram PH6607" value={ofpn} onChange={function(e){ setOfpn(e.target.value); }} /></div>
        </div>
        <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ fontSize:10, color:C.accent, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Filters and Ignition</div>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Air Filter P/N</label><input style={si} placeholder="e.g. KN-1009" value={afpn} onChange={function(e){ setAfpn(e.target.value); }} /></div>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Fuel Filter P/N</label><input style={si} placeholder="e.g. FF5052" value={ffpn} onChange={function(e){ setFfpn(e.target.value); }} /></div>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Spark Plug</label><input style={si} placeholder="e.g. RC12YC" value={spk} onChange={function(e){ setSpk(e.target.value); }} /></div>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Tire Size</label><input style={si} placeholder="e.g. 18x9.5-8" value={ts} onChange={function(e){ setTs(e.target.value); }} /></div>
          </div>
        </div>
        {isHeavy && (
          <div style={{ background:C.surface, border:"1px solid "+C.blue, borderLeft:"3px solid "+C.blue, borderRadius:12, padding:14, display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ fontSize:10, color:C.blue, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Hydraulic System</div>
            <div style={{ display:"flex", gap:10 }}>
              <div style={{ flex:2, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Hydraulic Oil Type</label><input style={si} placeholder="e.g. ISO 46 AW" value={hot} onChange={function(e){ setHot(e.target.value); }} /></div>
              <div style={{ flex:1, display:"flex", flexDirection:"column", gap:4 }}><label style={lb}>Capacity</label><input style={si} placeholder="16 gal" value={hoq} onChange={function(e){ setHoq(e.target.value); }} /></div>
            </div>
          </div>
        )}
        <div style={{ display:"flex", gap:10, paddingBottom:16 }}>
          <button onClick={onClose} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:13, cursor:"pointer" }}>Cancel</button>
          <button onClick={doSave} style={{ flex:2, background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, letterSpacing:"0.12em", textTransform:"uppercase", padding:13, cursor:"pointer", fontWeight:700 }}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ─── EQUIPMENT PROFILE SCREEN ─────────────────────────────────────────────────

function EquipmentProfileScreen(props) {
  var eq=props.eq; var logs=props.logs; var repairs=props.repairs;
  var partsLib=props.partsLib||[]; var isAdmin=props.isAdmin;
  var servicesLib=props.servicesLib||[]; var onUpdateServicesLib=props.onUpdateServicesLib||function(){};
  var onUpdate=props.onUpdate; var onAddLog=props.onAddLog;
  var onAddRepair=props.onAddRepair; var onUpdatePartsLib=props.onUpdatePartsLib||function(){};
  var onBack=props.onBack; var currentUser=props.currentUser||{name:"User"};
  var requests=props.requests||[]; var onAddRequest=props.onAddRequest||function(){};
  var lang=props.lang||"en"; var t=getT(lang);

  var t1=useState("maintenance"); var tab=t1[0];       var setTab=t1[1];
  var t2=useState(false);         var showLog=t2[0];   var setShowLog=t2[1];
  var t3=useState(false);         var showRepair=t3[0];var setShowRepair=t3[1];
  var t4=useState(null);          var markDown=t4[0];  var setMarkDown=t4[1];
  var t5=useState(null);          var doneIv=t5[0];    var setDoneIv=t5[1];
  var t6=useState(null);          var editIv=t6[0];    var setEditIv=t6[1];
  var t7=useState(false);         var editSpecs=t7[0]; var setEditSpecs=t7[1];
  var t8=useState(false);         var showSvcReq=t8[0];var setShowSvcReq=t8[1];
  var t9=useState(false);         var showDelete=t9[0]; var setShowDelete=t9[1];
  var t10=useState(false);        var editName=t10[0];  var setEditName=t10[1];
  var t11=useState(eq.name);      var newName=t11[0];   var setNewName=t11[1];
  var onDelete=props.onDelete||null;

  var uL=eq.usesMiles?"mi":"hrs";
  var eqLogs=logs.filter(function(l){ return l.equipmentId===eq.id; }).sort(function(a,b){ return b.date.localeCompare(a.date); });
  var eqReps=repairs.filter(function(r){ return r.equipmentId===eq.id; }).sort(function(a,b){ return b.date.localeCompare(a.date); });
  var lastLog=eqLogs.length>0?eqLogs[0]:null;
  var si=eq.status==="out_of_service"||eq.status==="waiting_parts";
  var sCol=si?C.danger:C.success; var sLbl=si?oosLabel(eq.status,t):"Active";
  var overdueN=eq.maintenanceIntervals.filter(function(iv){ return dueIn(iv,eq)<=0; }).length;
  var ws=eq.warranty?warStatus(eq.warranty.warrantyExpDate):null;
  var eqRequests=requests.filter(function(r){ return r.equipmentId===eq.id; });
  var pendingReqs=eqRequests.filter(function(r){ return r.status==="pending"; }).length;

  var tabs2=isAdmin?["maintenance","history","specs","requests"]:["maintenance","history","requests"];
  if (eq.warranty&&isAdmin) tabs2=["maintenance","history","specs","warranty","requests"];

  // AI-powered urgency message
  function getUrgencyMessage(iv, eq) {
    var d=dueIn(iv,eq); var uL=eq.usesMiles?"miles":"hours";
    var how=Math.abs(Math.round(d));
    if (d>0) return null;
    var msgs = {
      "oil":       "Oil change is "+how+" "+uL+" overdue. Running without fresh oil risks serious engine damage or complete breakdown. Change now to protect your investment.",
      "blade":     "Blade service is "+how+" "+uL+" overdue. Dull or damaged blades reduce cut quality and strain the engine. Service immediately.",
      "hydraulic": "Hydraulic service is "+how+" "+uL+" overdue. Contaminated fluid can destroy pumps and cylinders costing thousands. Service now.",
      "air filter":"Air filter is "+how+" "+uL+" overdue. A clogged filter starves the engine of air and causes premature wear. Replace now.",
      "tire":      "Tire inspection is "+how+" "+uL+" overdue. Worn tires reduce safety and handling. Inspect now.",
      "grease":    "Lubrication is "+how+" "+uL+" overdue. Dry pivot points and zerks accelerate wear. Grease all fittings now.",
      "belt":      "Belt inspection is "+how+" "+uL+" overdue. A snapped belt means unexpected downtime. Inspect and replace if needed.",
      "fuel filter":"Fuel filter is "+how+" "+uL+" overdue. A clogged filter starves the engine and can cause hard starts or stalling. Replace now.",
      "coolant":   "Coolant service is "+how+" "+uL+" overdue. Old coolant loses its ability to prevent overheating. Flush and replace now.",
      "brake":     "Brake inspection is "+how+" "+uL+" overdue. Worn brakes are a serious safety risk. Inspect immediately.",
      "spark plug":"Spark plug replacement is "+how+" "+uL+" overdue. Worn plugs cause misfires, poor fuel economy, and hard starts. Replace now.",
      "transmission":"Transmission service is "+how+" "+uL+" overdue. Neglected transmission fluid leads to costly repairs. Service now.",
      "battery":   "Battery inspection is "+how+" "+uL+" overdue. Corroded terminals cause no-starts when you need the equipment most. Clean and inspect now."
    };
    var taskLower=iv.task.toLowerCase();
    for (var key in msgs) {
      if (taskLower.indexOf(key)>=0) return msgs[key];
    }
    return iv.task+" is "+how+" "+uL+" overdue. Service now to prevent unexpected breakdowns and protect your equipment.";
  }

  // Pre-alert message
  function getPreAlertMessage(iv, eq) {
    var d=dueIn(iv,eq); var uL=eq.usesMiles?"miles":"hours"; var w=eq.usesMiles?500:50;
    if (d>w||d<=0) return null;
    return iv.task+" is due in "+Math.round(d)+" "+uL+". Schedule service soon to avoid unplanned downtime.";
  }

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {showLog && <AddLogSheet eq={eq} last={lastLog} lang={lang} currentUser={currentUser} onSave={function(e){ onAddLog(e); setShowLog(false); }} onClose={function(){ setShowLog(false); }} />}
      {showRepair && <AddRepairSheet eq={eq} isAdmin={isAdmin} lang={lang} onSave={function(rep){ onAddRepair(rep); setShowRepair(false); }} onClose={function(){ setShowRepair(false); }} />}
      {showSvcReq && <ServiceRequestSheet eq={eq} currentUser={currentUser} lang={lang} onSave={function(req){ onAddRequest(req); setShowSvcReq(false); }} onClose={function(){ setShowSvcReq(false); }} />}
      {doneIv && (
        <MarkServiceDoneSheet iv={doneIv} eq={eq} lang={lang} currentUser={currentUser}
          onSave={function(result){
            var updIvs=eq.maintenanceIntervals.map(function(iv){ return iv.id!==doneIv.id?iv:Object.assign({},iv,{lastDoneHours:result.doneHours}); });
            onUpdate(Object.assign({},eq,{maintenanceIntervals:updIvs}));
            onAddRepair({ id:genId(), equipmentId:eq.id, repairType:"inhouse", date:result.doneDate, whatFixed:doneIv.task, description:"Scheduled maintenance completed.", techName:result.who||null, laborHours:null, shop:null, warrantyWork:false, returnDate:null, outOfService:false, cost:"0.00", notes:result.notes||null, receiptImg:null, receiptNotes:null });
            setDoneIv(null);
          }}
          onClose={function(){ setDoneIv(null); }} />
      )}
      {editIv && (
        <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={function(){ setEditIv(null); }}>
          <div style={{ background:C.surface, border:"1px solid "+C.accent, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"88vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
            <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>{editIv.id?"Edit Interval":"Add Interval"}</div>
              <div style={{ fontSize:12, color:C.dim, marginTop:2, ...FM }}>{emoji(eq.type)} {eq.name}</div>
            </div>
            <div style={{ overflowY:"auto", flex:1, padding:"16px", display:"flex", flexDirection:"column", gap:14 }}>
              {!editIv.id&&servicesLib.length>0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <div style={lbl}>Pick from Services Library</div>
                  <div style={{ maxHeight:160, overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
                    {servicesLib.filter(function(sv){ return sv.types.indexOf(eq.type)>=0||sv.types.indexOf("other")>=0; }).map(function(sv) {
                      var sel=editIv.task===sv.name&&String(editIv.intervalHours)===String(sv.intervalHours);
                      return (
                        <div key={sv.id} onClick={function(){ setEditIv(Object.assign({},editIv,{task:sv.name,intervalHours:String(sv.intervalHours)})); }} style={{ background:sel?C.accentDim:C.inputBg, border:"1px solid "+(sel?C.accent:C.border), borderRadius:8, padding:"9px 12px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <div><div style={{ fontSize:12, fontWeight:sel?700:400, color:sel?C.accent:C.text, ...FM }}>{sv.name}</div>{sv.notes&&<div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{sv.notes}</div>}</div>
                          <div style={{ fontSize:11, color:sel?C.accent:C.muted, fontWeight:700, flexShrink:0, marginLeft:8 }}>Every {sv.intervalHours} {uL}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}><div style={{ flex:1, height:1, background:C.border }} /><span style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.1em" }}>or custom</span><div style={{ flex:1, height:1, background:C.border }} /></div>
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Task Name *</label><input style={inp} placeholder="e.g. Oil change, Blade sharpening" value={editIv.task} onChange={function(e){ setEditIv(Object.assign({},editIv,{task:e.target.value})); }} /></div>
              <div style={{ display:"flex", gap:10 }}>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Every ({uL})</label><input style={inp} type="number" inputMode="numeric" min="1" placeholder="250" value={editIv.intervalHours} onChange={function(e){ setEditIv(Object.assign({},editIv,{intervalHours:e.target.value})); }} /></div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}><label style={lbl}>Last Done At</label><input style={inp} type="number" inputMode="numeric" min="0" placeholder="0" value={editIv.lastDoneHours||""} onChange={function(e){ setEditIv(Object.assign({},editIv,{lastDoneHours:e.target.value})); }} /></div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {[{v:"inapp",l:"In-App"},{v:"sound",l:"Sound"},{v:"off",l:"Off"}].map(function(opt) {
                  var sel=editIv.alertMode===opt.v;
                  return <div key={opt.v} onClick={function(){ setEditIv(Object.assign({},editIv,{alertMode:opt.v})); }} style={{ flex:1, background:sel?C.accentDim:C.inputBg, border:"1.5px solid "+(sel?C.accent:C.border), borderRadius:10, padding:"10px 8px", textAlign:"center", cursor:"pointer" }}><div style={{ fontSize:11, fontWeight:sel?700:400, color:sel?C.accent:C.text, ...FM }}>{opt.l}</div></div>;
                })}
              </div>
            </div>
            <div style={{ display:"flex", gap:10, padding:"14px 16px 0", borderTop:"1px solid "+C.border, flexShrink:0 }}>
              <button onClick={function(){ setEditIv(null); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.dim, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:"pointer" }}>Cancel</button>
              <button disabled={!editIv.task.trim()||!editIv.intervalHours} onClick={function(){
                if (!editIv.task.trim()||!editIv.intervalHours) return;
                var newIv={id:editIv.id||genId(),task:editIv.task.trim(),intervalHours:parseFloat(editIv.intervalHours)||0,lastDoneHours:parseFloat(editIv.lastDoneHours)||0,timeUnit:"usage",alertMode:editIv.alertMode||"inapp"};
                var updEq=Object.assign({},eq,{maintenanceIntervals:editIv.id?eq.maintenanceIntervals.map(function(iv){ return iv.id===editIv.id?newIv:iv; }):eq.maintenanceIntervals.concat([newIv])});
                onUpdate(updEq); setEditIv(null);
              }} style={{ flex:2, background:(editIv.task.trim()&&editIv.intervalHours)?C.accent:C.surfaceHi, border:"none", borderRadius:10, color:(editIv.task.trim()&&editIv.intervalHours)?"#0F1117":C.muted, fontSize:12, ...FM, letterSpacing:"0.1em", textTransform:"uppercase", padding:12, cursor:(editIv.task.trim()&&editIv.intervalHours)?"pointer":"not-allowed", fontWeight:700 }}>{editIv.id?"Save Changes":"Add Interval"}</button>
            </div>
          </div>
        </div>
      )}
      {editSpecs && (
        <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, background:"rgba(0,0,0,0.82)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:400 }} onClick={function(){ setEditSpecs(false); }}>
          <div style={{ background:C.surface, border:"1px solid "+C.accent, borderRadius:"16px 16px 0 0", width:"100%", maxWidth:480, maxHeight:"92vh", display:"flex", flexDirection:"column", paddingBottom:28 }} onClick={function(e){ e.stopPropagation(); }}>
            <div style={{ width:36, height:4, background:C.border, borderRadius:2, margin:"12px auto 0", flexShrink:0 }} />
            <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid "+C.border, flexShrink:0 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.18em", textTransform:"uppercase", ...FM }}>Edit Specs -- {emoji(eq.type)} {eq.name}</div>
            </div>
            <EditSpecsForm eq={eq} lang={lang} onSave={function(updated){ onUpdate(updated); setEditSpecs(false); }} onClose={function(){ setEditSpecs(false); }} />
          </div>
        </div>
      )}
      {markDown && (
        <MarkDownSheet eq={eq} current={eq.outOfService||null} lang={lang}
          onSave={function(d){ onUpdate(Object.assign({},eq,{status:d.reason,outOfService:d})); setMarkDown(null); }}
          onClear={isAdmin?function(){ onUpdate(Object.assign({},eq,{status:"active",outOfService:null})); setMarkDown(null); }:null}
          onClose={function(){ setMarkDown(null); }} />
      )}
      <div style={{ width:"100%", maxWidth:480, borderBottom:"1px solid "+C.border }}>
        <div style={{ padding:"14px 16px 0", display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={onBack} style={{ background:"none", border:"none", color:C.blue, fontSize:22, cursor:"pointer", padding:"0 6px 0 0", lineHeight:1 }}>{"<"}</button>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{emoji(eq.type)} {eq.name}</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:2, textTransform:"uppercase", letterSpacing:"0.08em" }}>{eq.make} {eq.model} {eq.year}</div>
            {!isAdmin && <div style={{ fontSize:9, color:C.success, fontWeight:700, marginTop:1, textTransform:"uppercase", letterSpacing:"0.1em" }}>Crew View</div>}
          </div>
          <div style={{ background:si?C.dangerDim:C.successDim, border:"1px solid "+sCol, borderRadius:8, padding:"4px 10px", fontSize:10, color:sCol, fontWeight:700, textTransform:"uppercase" }}>{sLbl}</div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:0, margin:"12px 16px 0", background:C.surfaceHi, borderRadius:10, overflow:"hidden" }}>
          {[{label:"Current",value:eq.totalUsage.toLocaleString()+" "+uL},{label:"Overdue",value:String(overdueN),color:overdueN>0?C.danger:C.success},{label:"Entries",value:String(eqLogs.length)},{label:"Last Log",value:lastLog?(daysSince(lastLog.date)===0?"Today":daysSince(lastLog.date)+"d ago"):"None"}].map(function(s,i,arr) {
            return <div key={s.label} style={{ padding:"10px 6px", textAlign:"center", borderRight:i<arr.length-1?"1px solid "+C.border:"none" }}><div style={{ fontSize:13, fontWeight:700, color:s.color||C.accent }}>{s.value}</div><div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:2 }}>{s.label}</div></div>;
          })}
        </div>
        <div style={{ display:"flex", gap:8, padding:"12px 16px 0" }}>
          <button onClick={function(){ setShowLog(true); }} style={{ flex:2, background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, fontWeight:700, padding:11, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>+ Log Entry</button>
          <button onClick={function(){ setMarkDown(true); }} style={{ flex:1, background:"none", border:"1px solid "+(si?C.danger:C.border), borderRadius:10, color:si?C.danger:C.dim, fontSize:12, ...FM, fontWeight:700, padding:11, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>{si?"Status":"Down"}</button>
          <button onClick={function(){ setShowSvcReq(true); }} style={{ flex:1, background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:10, color:C.purple, fontSize:12, ...FM, fontWeight:700, padding:11, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>{"Req"+(pendingReqs>0?" ("+pendingReqs+")":"")}</button>
        </div>
        <div style={{ display:"flex", borderTop:"1px solid "+C.border, marginTop:12, overflowX:"auto" }}>
          {tabs2.map(function(tb) {
            var active=tab===tb; var isBadge=tb==="requests"&&pendingReqs>0;
            return <button key={tb} onClick={function(){ setTab(tb); }} style={{ flex:1, background:"none", border:"none", borderBottom:"2px solid "+(active?C.accent:"transparent"), color:active?C.accent:C.muted, fontSize:11, ...FM, fontWeight:active?700:400, padding:"10px 4px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap", position:"relative" }}>
              {tb==="maintenance"?"Maint.":tb==="history"?"History":tb==="specs"?"Specs":tb==="warranty"?"Warranty":"Requests"}
              {isBadge && <span style={{ position:"absolute", top:4, right:4, background:C.purple, borderRadius:8, fontSize:8, color:"#fff", fontWeight:700, padding:"1px 4px", lineHeight:1.4 }}>{pendingReqs}</span>}
            </button>;
          })}
        </div>
      </div>
      <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0" }}>
        {tab==="maintenance" && (
          <div>
            <SecHead title="Maintenance Intervals" action={isAdmin?{label:"+ Add",fn:function(){ setEditIv({id:null,task:"",intervalHours:"",timeUnit:"usage",alertMode:"inapp"}); },color:C.accent,bg:C.accentDim}:null} />
            {eq.maintenanceIntervals.length===0 && <div style={{ textAlign:"center", padding:"30px 0", color:C.muted, fontSize:13, ...FM }}>No intervals set. {isAdmin?"Tap + Add to create one.":""}</div>}
            {eq.maintenanceIntervals.map(function(iv) {
              var due=dueBadge(iv,eq); var over=due.text==="OVERDUE";
              var urgMsg=over?getUrgencyMessage(iv,eq):null;
              var preMsg=!over?getPreAlertMessage(iv,eq):null;
              return (
                <div key={iv.id} style={{ background:over?C.dangerDim:C.surface, border:"1px solid "+(over?C.danger:C.border), borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{iv.task}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2, ...FM }}>Every {iv.intervalHours.toLocaleString()} {uL} -- Last: {iv.lastDoneHours.toLocaleString()} {uL}</div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:due.color, ...FM }}>{due.text}</div>
                      <div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", marginTop:2 }}>{over?"ACTION NEEDED":"remaining"}</div>
                    </div>
                  </div>
                  {urgMsg && <div style={{ background:"rgba(224,92,92,0.2)", border:"1px solid "+C.danger, borderRadius:8, padding:"8px 10px", fontSize:11, color:"#ff9090", lineHeight:1.5, marginBottom:8, ...FM }}>{urgMsg}</div>}
                  {preMsg && <div style={{ background:"rgba(245,166,35,0.12)", border:"1px solid "+C.amber, borderRadius:8, padding:"8px 10px", fontSize:11, color:C.amber, lineHeight:1.5, marginBottom:8, ...FM }}>{preMsg}</div>}
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <div style={{ flex:1, height:4, background:C.surfaceHi, borderRadius:2, overflow:"hidden" }}><div style={{ height:4, borderRadius:2, background:due.color, width:due.pct+"%" }} /></div>
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      {isAdmin && <button onClick={function(){ setEditIv({id:iv.id,task:iv.task,intervalHours:String(iv.intervalHours),lastDoneHours:String(iv.lastDoneHours),timeUnit:iv.timeUnit||"usage",alertMode:iv.alertMode||"inapp"}); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:7, color:C.dim, fontSize:10, ...FM, padding:"4px 10px", cursor:"pointer" }}>Edit</button>}
                      {isAdmin && <button onClick={function(){ onUpdate(Object.assign({},eq,{maintenanceIntervals:eq.maintenanceIntervals.filter(function(x){ return x.id!==iv.id; })})); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:7, color:C.danger, fontSize:10, ...FM, padding:"4px 10px", cursor:"pointer", opacity:0.8 }}>Del</button>}
                      <button onClick={function(){ setDoneIv(iv); }} style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:7, color:C.success, fontSize:10, ...FM, fontWeight:700, padding:"4px 12px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>Done</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {tab==="history" && (
          <div>
            <div style={{ display:"flex", gap:8, marginBottom:14 }}>
              <button onClick={function(){ setShowLog(true); }} style={{ flex:1, background:C.accentDim, border:"1px solid "+C.accent, borderRadius:10, color:C.accent, fontSize:11, ...FM, fontWeight:700, padding:10, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>+ Log Entry</button>
              <button onClick={function(){ setShowRepair(true); }} style={{ flex:1, background:C.blueDim, border:"1px solid "+C.blue, borderRadius:10, color:C.blue, fontSize:11, ...FM, fontWeight:700, padding:10, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>+ Repair</button>
            </div>
            {(function() {
              var items=[];
              eqLogs.forEach(function(e){ items.push({type:"log",date:e.date,data:e}); });
              eqReps.forEach(function(r){ items.push({type:"repair",date:r.date,data:r}); });
              items.sort(function(a,b){ return b.date.localeCompare(a.date); });
              if (items.length===0) return <div style={{ textAlign:"center", padding:"30px 0", color:C.muted, fontSize:13, ...FM }}>No history yet.</div>;
              return items.map(function(item) {
                if (item.type==="log") {
                  var entry=item.data; var idx=eqLogs.indexOf(entry); var prev=eqLogs[idx+1]; var delta=prev?entry.usage-prev.usage:null;
                  return (
                    <div key={"log_"+entry.id} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                        <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:5, padding:"2px 8px", fontSize:9, color:C.accent, fontWeight:700, textTransform:"uppercase" }}>Log Entry</div>
                        <div style={{ fontSize:11, color:C.muted, ...FM }}>{fmtDate(entry.date)}</div>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{entry.driver||"No driver noted"}</div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontSize:15, fontWeight:700, color:C.accent }}>{entry.usage.toLocaleString()} {uL}</div>
                          {delta!==null && <div style={{ fontSize:10, color:delta>=0?C.success:C.danger, marginTop:2 }}>{delta>=0?"+":""}{delta.toLocaleString()} {uL}</div>}
                        </div>
                      </div>
                      {(entry.fuelGallons||entry.fuelCost) && <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid "+C.border, display:"flex", gap:16 }}>{entry.fuelGallons&&<span style={{ fontSize:11, color:C.teal }}>Fuel: {entry.fuelGallons} gal</span>}{entry.fuelCost&&<span style={{ fontSize:11, color:C.teal }}>${entry.fuelCost}</span>}</div>}
                      {entry.notes && <div style={{ marginTop:6, fontSize:11, color:C.dim, lineHeight:1.4 }}>{entry.notes}</div>}
                    </div>
                  );
                }
                if (item.type==="repair") {
                  var rep=item.data; var repCol=rep.repairType==="inhouse"?C.accent:C.blue; var repBg=rep.repairType==="inhouse"?C.accentDim:C.blueDim;
                  return (
                    <div key={"rep_"+rep.id} style={{ background:rep.warrantyWork?C.successDim:C.surface, border:"1px solid "+(rep.warrantyWork?C.success:C.border), borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                        <div style={{ background:repBg, border:"1px solid "+repCol, borderRadius:5, padding:"2px 8px", fontSize:9, color:repCol, fontWeight:700, textTransform:"uppercase" }}>{rep.repairType==="inhouse"?"In-House":"Shop Repair"}</div>
                        {rep.warrantyWork && <div style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:5, padding:"2px 8px", fontSize:9, color:C.success, fontWeight:700 }}>Warranty</div>}
                        <div style={{ fontSize:11, color:C.muted, ...FM }}>{fmtDate(rep.date)}</div>
                      </div>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{rep.whatFixed||rep.description}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{rep.repairType==="inhouse"?(rep.techName||"")+(rep.laborHours?" -- "+rep.laborHours+" hrs":""):(rep.shop||"")}</div>
                      {isAdmin&&rep.cost&&rep.cost!=="0.00" && <div style={{ fontSize:11, color:C.purple, marginTop:4, fontWeight:700 }}>Cost: ${rep.cost}</div>}
                      {rep.notes && <div style={{ marginTop:6, fontSize:11, color:C.dim, lineHeight:1.4 }}>{rep.notes}</div>}
                    </div>
                  );
                }
                return null;
              });
            })()}
          </div>
        )}
        {tab==="specs"&&isAdmin && (
          <div>
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
              <button onClick={function(){ setEditSpecs(true); }} style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:8, color:C.accent, fontSize:11, ...FM, fontWeight:700, padding:"6px 14px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>Edit Specs</button>
            </div>
            <SecHead title="Identity" color={C.teal} />
            <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:16, marginBottom:14 }}>
              {[["Make",eq.make],["Model",eq.model],["Year",eq.year],eq.vin?["VIN #",eq.vin]:null,eq.serialNumber?["Serial #",eq.serialNumber]:null].filter(Boolean).map(function(row) {
                return <div key={row[0]} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid "+C.border }}><span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>{row[0]}</span><span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{row[1]||"--"}</span></div>;
              })}
            </div>
            {eq.engineOilType && (
              <div>
                <SecHead title="Engine Oil" color={C.teal} />
                <div style={{ background:C.surface, border:"1px solid "+C.teal, borderLeft:"3px solid "+C.teal, borderRadius:12, padding:14, marginBottom:14 }}>
                  {[["Type / Grade",eq.engineOilType,false],["Quantity",eq.engineOilQty,false],["Oil Filter P/N",eq.oilFilterPN,true]].filter(function(r){ return r[1]; }).map(function(row) {
                    var isPN=row[2]; var val=row[1];
                    return (
                      <div key={row[0]} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:"1px solid "+C.border }}>
                        <span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>{row[0]}</span>
                        {isPN&&val
                          ? <a href={"https://www.google.com/search?q="+encodeURIComponent(val)+"&tbm=shop"} target="_blank" rel="noreferrer" style={{ fontSize:12, color:C.accent, fontWeight:600, textDecoration:"none", display:"flex", alignItems:"center", gap:4 }}>{val} <span style={{ fontSize:9, opacity:0.7 }}>shop</span></a>
                          : <span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{val}</span>
                        }
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {eq.hydraulicOilType && (
              <div>
                <SecHead title="Hydraulic Oil" color={C.blue} />
                <div style={{ background:C.surface, border:"1px solid "+C.blue, borderLeft:"3px solid "+C.blue, borderRadius:12, padding:14, marginBottom:14 }}>
                  {[["Type / Spec",eq.hydraulicOilType],["Capacity",eq.hydraulicOilQty]].filter(function(r){ return r[1]; }).map(function(row) {
                    return <div key={row[0]} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid "+C.border }}><span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>{row[0]}</span><span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{row[1]}</span></div>;
                  })}
                </div>
              </div>
            )}
            {eq.customParts&&eq.customParts.length>0 && (
              <div>
                <SecHead title="Parts and Components" color={C.accent} />
                {eq.customParts.map(function(p) {
                  return (
                    <div key={p.id} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:10, padding:"10px 14px", marginBottom:8 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{p.name}</div>
                      {p.partNumber && (
                        <a href={"https://www.google.com/search?q="+encodeURIComponent(p.partNumber)+"&tbm=shop"} target="_blank" rel="noreferrer" style={{ fontSize:11, color:C.accent, ...FM, marginTop:2, display:"flex", alignItems:"center", gap:4, textDecoration:"none" }}>
                          P/N: {p.partNumber} <span style={{ fontSize:9, opacity:0.7 }}>shop</span>
                        </a>
                      )}
                      {p.notes && <div style={{ fontSize:11, color:C.muted, ...FM, marginTop:2 }}>{p.notes}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rename equipment */}
            {isAdmin && (
              <div style={{ marginTop:20 }}>
                <SecHead title="Rename Equipment" color={C.blue} />
                {editName ? (
                  <div style={{ display:"flex", gap:8 }}>
                    <input style={{ ...inp, flex:1, fontSize:13 }} value={newName} onChange={function(e){ setNewName(e.target.value); }} autoFocus />
                    <button onClick={function(){ if(newName.trim()){ onUpdate(Object.assign({},eq,{name:newName.trim()})); setEditName(false); } }} disabled={!newName.trim()} style={{ background:newName.trim()?C.accent:C.surfaceHi, border:"none", borderRadius:8, color:newName.trim()?"#0F1117":C.muted, fontSize:11, ...FM, fontWeight:700, padding:"0 14px", cursor:newName.trim()?"pointer":"not-allowed" }}>Save</button>
                    <button onClick={function(){ setNewName(eq.name); setEditName(false); }} style={{ background:"none", border:"1px solid "+C.border, borderRadius:8, color:C.muted, fontSize:11, ...FM, padding:"0 12px", cursor:"pointer" }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:C.surface, border:"1px solid "+C.border, borderRadius:10, padding:"10px 14px" }}>
                    <span style={{ fontSize:13, color:C.text, fontWeight:600 }}>{eq.name}</span>
                    <button onClick={function(){ setNewName(eq.name); setEditName(true); }} style={{ background:C.blueDim, border:"1px solid "+C.blue, borderRadius:7, color:C.blue, fontSize:11, ...FM, fontWeight:700, padding:"4px 12px", cursor:"pointer" }}>Rename</button>
                  </div>
                )}
              </div>
            )}

            {/* Delete equipment */}
            {isAdmin && onDelete && (
              <div style={{ marginTop:16, marginBottom:20 }}>
                <SecHead title="Danger Zone" color={C.danger} />
                {showDelete ? (
                  <div style={{ background:C.dangerDim, border:"1px solid "+C.danger, borderRadius:12, padding:"16px 14px" }}>
                    <div style={{ fontSize:12, color:C.danger, fontWeight:700, marginBottom:6 }}>Delete {eq.name}?</div>
                    <div style={{ fontSize:11, color:C.dim, marginBottom:14, lineHeight:1.6 }}>This will permanently remove this equipment, all its logs, service records, and maintenance history. This cannot be undone.</div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={function(){ setShowDelete(false); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:8, color:C.muted, fontSize:12, ...FM, padding:10, cursor:"pointer" }}>Cancel</button>
                      <button onClick={function(){ onDelete(eq.id); }} style={{ flex:1, background:C.danger, border:"none", borderRadius:8, color:"#fff", fontSize:12, ...FM, fontWeight:700, padding:10, cursor:"pointer" }}>Delete Forever</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={function(){ setShowDelete(true); }} style={{ width:"100%", background:"none", border:"1px solid "+C.danger, borderRadius:10, color:C.danger, fontSize:12, ...FM, fontWeight:700, padding:11, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>Delete This Equipment</button>
                )}
              </div>
            )}
          </div>
        )}
        {tab==="warranty"&&eq.warranty&&isAdmin && (
          <div>
            <SecHead title="Warranty" color={ws.color} />
            <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:16, marginBottom:14 }}>
              {[["Dealer",eq.warranty.dealerName],["Purchase Date",fmtDate(eq.warranty.purchaseDate)],["Coverage",coverLabel(eq.warranty.coverageType,t)]].map(function(row) {
                return <div key={row[0]} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid "+C.border }}><span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>{row[0]}</span><span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{row[1]||"--"}</span></div>;
              })}
              <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid "+C.border }}><span style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em" }}>Expires</span><span style={{ fontSize:12, color:ws.color, fontWeight:700 }}>{ws.label}</span></div>
              {isAdmin&&eq.warranty.purchasePrice && <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0" }}><span style={{ fontSize:11, color:C.purple, textTransform:"uppercase", letterSpacing:"0.08em" }}>Purchase Price</span><span style={{ fontSize:12, color:C.purple, fontWeight:700 }}>${eq.warranty.purchasePrice}</span></div>}
            </div>
            {eqReps.filter(function(r){ return r.warrantyWork; }).length>0 && (
              <div>
                <SecHead title="Warranty Work" color={C.success} />
                {eqReps.filter(function(r){ return r.warrantyWork; }).map(function(rep) {
                  return <div key={rep.id} style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:12, padding:"11px 14px", marginBottom:8 }}><div style={{ fontSize:12, fontWeight:700, color:C.text, ...FM }}>{rep.whatFixed||rep.description}</div><div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{rep.shop?rep.shop+" -- ":""}{fmtDate(rep.date)}</div>{rep.notes&&<div style={{ fontSize:11, color:C.dim, marginTop:4, lineHeight:1.5 }}>{rep.notes}</div>}</div>;
                })}
              </div>
            )}
          </div>
        )}
        {tab==="requests" && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:12, color:C.muted, ...FM }}>{eqRequests.length} total request{eqRequests.length!==1?"s":""}</div>
              <button onClick={function(){ setShowSvcReq(true); }} style={{ background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:8, color:C.purple, fontSize:11, ...FM, fontWeight:700, padding:"6px 12px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>+ Request</button>
            </div>
            {eqRequests.length===0 && <div style={{ textAlign:"center", padding:"30px 0", color:C.muted, fontSize:13, ...FM }}>No service requests for this equipment.</div>}
            {eqRequests.map(function(req) {
              var pCol={routine:C.success,soon:C.amber,urgent:C.danger}[req.priority]||C.muted;
              var isPending=req.status==="pending";
              return (
                <div key={req.id} style={{ background:C.surface, border:"1px solid "+(isPending?C.purple:C.border), borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                    <div style={{ background:"rgba(0,0,0,0.2)", border:"1px solid "+pCol, borderRadius:4, padding:"1px 8px", fontSize:9, color:pCol, fontWeight:700, textTransform:"uppercase" }}>{req.priority}</div>
                    <div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>{req.status}</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{req.title}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>By {req.requestedBy} -- {fmtDate(req.createdAt.split("T")[0])}</div>
                  {req.adminNotes && <div style={{ fontSize:11, color:C.teal, marginTop:4, fontStyle:"italic" }}>Admin: {req.adminNotes}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FLEET SCREEN ─────────────────────────────────────────────────────────────

function FleetScreen(props) {
  var equipment=props.equipment; var logs=props.logs; var repairs=props.repairs;
  var partsLib=props.partsLib||[]; var isAdmin=props.isAdmin;
  var servicesLib=props.servicesLib||[]; var onUpdateServicesLib=props.onUpdateServicesLib||function(){};
  var onUpdateEq=props.onUpdateEq; var onAddLog=props.onAddLog;
  var onAddRepair=props.onAddRepair; var onUpdatePartsLib=props.onUpdatePartsLib||function(){};
  var onBack=props.onBack; var currentUser=props.currentUser||{name:"User"};
  var requests=props.requests||[]; var onAddRequest=props.onAddRequest||function(){};
  var plan=props.plan||getPlan(currentUser);
  var atEqLimit=plan.maxEquipment&&equipment.length>=plan.maxEquipment;
  var lang=props.lang||"en"; var t=getT(lang);
  var p1=useState(null); var selected=p1[0]; var setSelected=p1[1];
  var selEq=selected?equipment.filter(function(e){ return e.id===selected; })[0]:null;
  if (selEq) {
    return (
      <EquipmentProfileScreen
        eq={selEq} logs={logs} repairs={repairs}
        partsLib={partsLib} isAdmin={isAdmin}
        servicesLib={servicesLib} onUpdateServicesLib={onUpdateServicesLib}
        onUpdate={function(u){ onUpdateEq(u); setSelected(u.id); }}
        onAddLog={onAddLog} onAddRepair={onAddRepair}
        onUpdatePartsLib={onUpdatePartsLib}
        currentUser={currentUser} requests={requests} onAddRequest={onAddRequest}
        lang={lang} onBack={function(){ setSelected(null); }} />
    );
  }
  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      <AppHeader back={onBack} title="Fleet"
        sub={plan.maxEquipment ? equipment.length+"/"+plan.maxEquipment+" equipment · "+planName((currentUser&&currentUser.plan)||"free") : equipment.length+" pieces of equipment"}
        right={isAdmin && <button onClick={function(){ if(!atEqLimit){ if(props.onAddEquipmentNav) props.onAddEquipmentNav(); else onBack(); } else { props.onUpgradeMsg && props.onUpgradeMsg(equipmentLimitMsg(currentUser)); } }} style={{ background:atEqLimit?C.surfaceHi:C.accentDim, border:"1px solid "+(atEqLimit?C.muted:C.accent), borderRadius:8, color:atEqLimit?C.muted:C.accent, fontSize:10, ...FM, fontWeight:700, padding:"6px 12px", cursor:atEqLimit?"not-allowed":"pointer", textTransform:"uppercase", letterSpacing:"0.06em", opacity:atEqLimit?0.5:1 }}>{atEqLimit?"Limit Reached":"+ Add"}</button>}
      />
      <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0" }}>
        {equipment.map(function(eq) {
          var uL=eq.usesMiles?"mi":"hrs";
          var ovN=eq.maintenanceIntervals.filter(function(iv){ return dueIn(iv,eq)<=0; }).length;
          var ll=logs.filter(function(l){ return l.equipmentId===eq.id; }).sort(function(a,b){ return b.date.localeCompare(a.date); })[0];
          var ld=ll?daysSince(ll.date):null;
          var isDn=eq.status==="out_of_service"||eq.status==="waiting_parts";
          var pendReqs=requests.filter(function(r){ return r.equipmentId===eq.id&&r.status==="pending"; }).length;
          return (
            <div key={eq.id} onClick={function(){ setSelected(eq.id); }} style={{ background:C.surface, border:"1.5px solid "+(isDn?C.danger:ovN>0?C.danger:C.border), borderRadius:12, padding:"14px 16px", marginBottom:10, cursor:"pointer" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:24, flexShrink:0 }}>{emoji(eq.type)}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{eq.name}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{eq.make} {eq.model} {eq.year}</div>
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  {pendReqs>0 && <div style={{ background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:6, padding:"2px 8px", fontSize:9, color:C.purple, fontWeight:700 }}>{pendReqs} req</div>}
                  {isDn ? <div style={{ background:C.dangerDim, border:"1px solid "+C.danger, borderRadius:6, padding:"2px 8px", fontSize:9, color:C.danger, fontWeight:700, textTransform:"uppercase", flexShrink:0 }}>Down</div>
                    : ovN>0 ? <div style={{ background:C.dangerDim, border:"1px solid "+C.danger, borderRadius:6, padding:"2px 8px", fontSize:9, color:C.danger, fontWeight:700, flexShrink:0 }}>{ovN} Overdue</div>
                    : <div style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:6, padding:"2px 8px", fontSize:9, color:C.success, fontWeight:700, flexShrink:0 }}>OK</div>
                  }
                </div>
              </div>
              <div style={{ display:"flex", marginTop:10, background:C.surfaceHi, borderRadius:8, overflow:"hidden" }}>
                {[{label:"Current",value:eq.totalUsage.toLocaleString()+" "+uL},{label:"Overdue",value:String(ovN),color:ovN>0?C.danger:C.success},{label:"Entries",value:String(logs.filter(function(l){ return l.equipmentId===eq.id; }).length)},{label:"Last Log",value:ld===null?"None":ld===0?"Today":ld+"d ago"}].map(function(s,i,arr) {
                  return <div key={s.label} style={{ flex:1, padding:"8px 0", textAlign:"center", borderRight:i<arr.length-1?"1px solid "+C.border:"none" }}><div style={{ fontSize:12, fontWeight:700, color:s.color||C.text }}>{s.value}</div><div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:2 }}>{s.label}</div></div>;
                })}
              </div>
              {eq.maintenanceIntervals.length>0 && (
                <div style={{ display:"flex", gap:3, marginTop:8 }}>
                  {eq.maintenanceIntervals.map(function(iv) {
                    var d=dueBadge(iv,eq);
                    return <div key={iv.id} style={{ flex:1, height:3, background:C.surfaceHi, borderRadius:2, overflow:"hidden" }}><div style={{ height:3, background:d.color, width:d.pct+"%", borderRadius:2 }} /></div>;
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CREW DASHBOARD ───────────────────────────────────────────────────────────

function CrewDashboard(props) {
  var equipment=props.equipment; var logs=props.logs; var reminders=props.reminders;
  var repairs=props.repairs; var requests=props.requests||[];
  var onNavigate=props.onNavigate; var onAddLog=props.onAddLog;
  var currentUser=props.currentUser; var onLogout=props.onLogout||function(){};
  var onAddRequest=props.onAddRequest||function(){};
  var onShowTutorial=props.onShowTutorial||null;
  // #12 — crew language persists per crew member in localStorage
  var langKey = "ch_lang_"+((currentUser&&currentUser.id)||"crew");
  var cd_L=useState(function(){ try { return localStorage.getItem(langKey)||props.lang||"en"; } catch(e){ return props.lang||"en"; } });
  var lang=cd_L[0]; var setLang=cd_L[1];
  function persistLang(l) { setLang(l); try { localStorage.setItem(langKey,l); } catch(e){} }
  var t=getT(lang);

  var s1=useState(null);  var logFor=s1[0];    var setLogFor=s1[1];
  var s2=useState(false); var showLog=s2[0];   var setShowLog=s2[1];
  var s3=useState(false); var showMenu=s3[0];  var setShowMenu=s3[1];
  var s4=useState(null);  var reqEq=s4[0];     var setReqEq=s4[1];
  var s5=useState(false); var showSvcReq=s5[0];var setShowSvcReq=s5[1];
  var s6=useState(false); var showEqPicker=s6[0]; var setShowEqPicker=s6[1];

  var pendingRequests=requests.filter(function(r){ return r.requestedById===currentUser.id&&r.status==="pending"; }).length;
  var overdueCount=0;
  equipment.forEach(function(eq){ eq.maintenanceIntervals.forEach(function(iv){ if(dueIn(iv,eq)<=0) overdueCount++; }); });

  // Daily reminder - show if no log today
  var loggedToday=logs.some(function(l){ return l.date===today; });

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {showLog&&logFor && (
        <AddLogSheet eq={logFor} lang={lang}
          last={logs.filter(function(l){ return l.equipmentId===logFor.id; }).sort(function(a,b){ return b.date.localeCompare(a.date); })[0]||null}
          onSave={function(entry){ onAddLog(entry); setShowLog(false); setLogFor(null); }}
          onClose={function(){ setShowLog(false); }} />
      )}
      {showSvcReq&&reqEq && (
        <ServiceRequestSheet eq={reqEq} currentUser={currentUser} lang={lang}
          onSave={function(req){ onAddRequest(req); setShowSvcReq(false); setReqEq(null); }}
          onClose={function(){ setShowSvcReq(false); setReqEq(null); }} />
      )}
      {/* Header */}
      <div style={{ width:"100%", maxWidth:480, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, borderBottom:"1px solid "+C.border, overflow:"hidden" }}>
        <img src="/chorehabit-app-tile.svg" alt="CH" style={{ width:32, height:32, borderRadius:8, flexShrink:0 }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.1em", textTransform:"uppercase", ...FM }}>ChoreHabit</div>
          <div style={{ fontSize:10, color:C.muted, marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {(currentUser.name&&currentUser.name!==currentUser.email?currentUser.name:"Crew")} — Crew View
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
          <div style={{ display:"flex", background:C.surfaceHi, border:"1px solid "+C.border, borderRadius:16, overflow:"hidden" }}>
            {["en","es"].map(function(l) {
              var sel=lang===l;
              return <button key={l} onClick={function(){ persistLang(l); }} style={{ background:sel?C.accent:"transparent", border:"none", color:sel?"#0F1117":C.muted, fontSize:9, ...FM, fontWeight:700, padding:"4px 8px", cursor:"pointer", textTransform:"uppercase" }}>{l.toUpperCase()}</button>;
            })}
          </div>
          <div onClick={function(){ setShowMenu(!showMenu); }} style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:16, padding:"4px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:5, maxWidth:110, overflow:"hidden" }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:C.accent, flexShrink:0 }} />
            <span style={{ fontSize:10, color:C.accent, ...FM, fontWeight:700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {(currentUser.name&&currentUser.name!==currentUser.email?currentUser.name:"Crew").split(" ")[0]}
            </span>
          </div>
        </div>
      </div>
      <div style={{ width:"100%", maxWidth:480, padding:"0 16px" }}>
        {/* User menu */}
        {showMenu && (
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"8px 0", marginTop:10, marginBottom:6 }}>
            <div style={{ padding:"10px 16px 8px", borderBottom:"1px solid "+C.border, marginBottom:4 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{currentUser.name}</div>
              <div style={{ fontSize:10, color:C.muted, marginTop:2, ...FM, textTransform:"uppercase" }}>@{currentUser.username} -- Crew</div>
            </div>
            {onShowTutorial && <button onClick={function(){ setShowMenu(false); onShowTutorial(); }} style={{ width:"100%", background:"none", border:"none", color:C.teal, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>View Tutorial</button>}
            <button onClick={function(){ setShowMenu(false); onLogout(); }} style={{ width:"100%", background:"none", border:"none", color:C.danger, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Sign Out</button>
          </div>
        )}
        {/* Daily logbook reminder */}
        {!loggedToday && (
          <div style={{ background:"rgba(245,166,35,0.1)", border:"1px solid "+C.amber, borderRadius:12, padding:"12px 14px", marginTop:14, display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ fontSize:22 }}>{"⏰"}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.amber, ...FM }}>Daily Log Reminder</div>
              <div style={{ fontSize:11, color:C.dim, marginTop:2 }}>No log entry today. Update current hours and any notes to keep records accurate.</div>
            </div>
          </div>
        )}
        {/* Weather alerts */}
        <WeatherTile lang={lang} />

        {/* MAIN ACTION TILE - Log Entry */}
        <div style={{ marginTop:14 }}>
          <div style={{ background:C.accent, borderRadius:16, padding:"24px 20px", display:"flex", flexDirection:"column", alignItems:"center", gap:12, cursor:"pointer", position:"relative", overflow:"hidden" }}
            onClick={function(){ onNavigate("logbook"); }}>
            <div style={{ fontSize:36 }}>{"📋"}</div>
            <div style={{ fontSize:18, fontWeight:700, color:"#0F1117", ...FM, textAlign:"center" }}>Log Entry</div>
            <div style={{ fontSize:12, color:"rgba(15,17,23,0.7)", textAlign:"center", lineHeight:1.4 }}>Update current hours and notes for your equipment</div>
            <div style={{ position:"absolute", top:12, right:16, fontSize:28, opacity:0.15 }}>{"+"}</div>
          </div>
        </div>
        {/* Two secondary tiles */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
          {/* Full Fleet */}
          <div onClick={function(){ onNavigate("fleet"); }} style={{ background:C.surface, border:"1.5px solid "+C.border, borderRadius:14, padding:"20px 12px 16px", display:"flex", flexDirection:"column", alignItems:"center", gap:8, cursor:"pointer" }}>
            <div style={{ fontSize:28 }}>{"🚛"}</div>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM, textAlign:"center" }}>Fleet</div>
            <div style={{ fontSize:10, color:C.muted, ...FM, textAlign:"center", lineHeight:1.4 }}>View all equipment</div>
            <div style={{ background:C.surfaceHi, borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.accent }}>{equipment.length} pieces</div>
          </div>
          {/* Alerts & Schedule */}
          <div onClick={function(){ onNavigate("alerts"); }} style={{ background:C.surface, border:"1.5px solid "+(overdueCount>0?C.danger:C.border), borderRadius:14, padding:"20px 12px 16px", display:"flex", flexDirection:"column", alignItems:"center", gap:8, cursor:"pointer" }}>
            <div style={{ fontSize:28 }}>{"🔔"}</div>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM, textAlign:"center" }}>Alerts</div>
            <div style={{ fontSize:10, color:C.muted, ...FM, textAlign:"center", lineHeight:1.4 }}>Maintenance schedule</div>
            {overdueCount>0
              ? <div style={{ background:C.dangerDim, border:"1px solid "+C.danger, borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.danger }}>{overdueCount} overdue</div>
              : <div style={{ background:C.successDim, border:"1px solid "+C.success, borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.success }}>All good</div>
            }
          </div>
        </div>
        {/* Service Requests tile */}
        <div style={{ marginTop:12, background:C.surface, border:"1.5px solid "+(pendingRequests>0?C.purple:C.border), borderRadius:14, padding:"16px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:showEqPicker?12:0 }}>
            <div style={{ fontSize:28 }}>{"🔧"}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>Service Requests</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>Report issues or request maintenance</div>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {pendingRequests>0 && <div onClick={function(){ onNavigate("requests"); }} style={{ background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.purple, cursor:"pointer" }}>{pendingRequests} pending</div>}
              <button onClick={function(){ setShowEqPicker(!showEqPicker); }} style={{ background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:8, color:C.purple, fontSize:11, ...FM, fontWeight:700, padding:"6px 12px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em" }}>+ New</button>
            </div>
          </div>
          {showEqPicker && (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:10, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, ...FM }}>Select Equipment</div>
              {equipment.map(function(eq) {
                return (
                  <div key={eq.id} onClick={function(){ setReqEq(eq); setShowEqPicker(false); setShowSvcReq(true); }} style={{ background:C.inputBg, border:"1px solid "+C.border, borderRadius:10, padding:"10px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:18 }}>{emoji(eq.type)}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{eq.name}</div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:1 }}>{eq.make} {eq.model} {eq.year}</div>
                    </div>
                    <div style={{ fontSize:16, color:C.purple }}>{">"}</div>
                  </div>
                );
              })}
              <button onClick={function(){ setShowEqPicker(false); }} style={{ background:"none", border:"none", color:C.muted, fontSize:11, ...FM, cursor:"pointer", padding:"4px 0", textAlign:"center" }}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CREW ALERTS SCREEN ───────────────────────────────────────────────────────

function CrewAlertsScreen(props) {
  var equipment=props.equipment; var reminders=props.reminders; var onBack=props.onBack;
  var onUpdateEq=props.onUpdateEq||function(){}; var onAddRepair=props.onAddRepair||function(){};
  var currentUser=props.currentUser||{name:"Crew"}; var lang=props.lang||"en"; var t=getT(lang);

  var c1=useState(null); var doneIv=c1[0]; var setDoneIv=c1[1];
  var c2=useState(null); var doneEq=c2[0]; var setDoneEq=c2[1];
  var c3=useState(null); var expanded=c3[0]; var setExpanded=c3[1];

  var allAlerts=[];
  equipment.forEach(function(eq) {
    eq.maintenanceIntervals.forEach(function(iv) {
      if (iv.alertMode==="off") return;
      allAlerts.push({iv:iv,eq:eq,due:dueIn(iv,eq)});
    });
  });
  allAlerts.sort(function(a,b) {
    var aO=a.due<=0; var bO=b.due<=0;
    if (aO&&!bO) return -1; if (!aO&&bO) return 1; return a.due-b.due;
  });
  var upcoming=reminders.filter(function(r){ return r.alertMode!=="off"; }).sort(function(a,b){ if(!a.nextDate)return 1; if(!b.nextDate)return -1; return a.nextDate.localeCompare(b.nextDate); }).slice(0,5);

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {doneIv&&doneEq && (
        <MarkServiceDoneSheet iv={doneIv} eq={doneEq} lang={lang} currentUser={currentUser}
          onSave={function(result){
            var updIvs=doneEq.maintenanceIntervals.map(function(iv){ return iv.id!==doneIv.id?iv:Object.assign({},iv,{lastDoneHours:result.doneHours}); });
            onUpdateEq(Object.assign({},doneEq,{maintenanceIntervals:updIvs}));
            onAddRepair({ id:genId(), equipmentId:doneEq.id, repairType:"inhouse", date:result.doneDate, whatFixed:doneIv.task, description:"Scheduled maintenance completed.", techName:result.who||currentUser.name, laborHours:null, shop:null, warrantyWork:false, returnDate:null, outOfService:false, cost:"0.00", notes:result.notes||null, receiptImg:null, receiptNotes:null });
            setDoneIv(null); setDoneEq(null);
          }}
          onClose={function(){ setDoneIv(null); setDoneEq(null); }} />
      )}
      <AppHeader back={onBack} title="Alerts and Schedule" />
      <div style={{ width:"100%", maxWidth:480, padding:"16px 16px 0" }}>
        <SecHead title="Maintenance Alerts" color={C.accent} />
        {allAlerts.length===0 && <div style={{ textAlign:"center", padding:"20px 0", color:C.muted, fontSize:13, ...FM }}>All maintenance is up to date.</div>}
        {allAlerts.map(function(a) {
          var due=dueBadge(a.iv,a.eq); var over=due.text==="OVERDUE";
          var isExp=expanded===a.iv.id;
          return (
            <div key={a.iv.id} style={{ background:over?C.dangerDim:C.surface, border:"1px solid "+(over?C.danger:C.border), borderRadius:12, padding:"12px 14px", marginBottom:8 }}>
              <div onClick={function(){ setExpanded(isExp?null:a.iv.id); }} style={{ cursor:"pointer" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{a.iv.task}</div>
                    <div style={{ fontSize:11, color:C.blue, marginTop:2 }}>{a.eq.name}</div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0, marginLeft:8 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:due.color, ...FM }}>{due.text}</div>
                    <div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", marginTop:2 }}>{over?"ACTION NEEDED":"remaining"}</div>
                  </div>
                </div>
                <div style={{ marginTop:8, height:4, background:C.surfaceHi, borderRadius:2, overflow:"hidden" }}><div style={{ height:4, borderRadius:2, background:due.color, width:due.pct+"%" }} /></div>
                <div style={{ marginTop:6, fontSize:10, color:C.muted, textAlign:"right" }}>{isExp?"Tap to collapse":"Tap for details"}</div>
              </div>
              {isExp && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid "+C.border }}>
                  <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                    <div style={{ flex:1, background:C.surfaceHi, border:"1px solid "+C.border, borderRadius:10, padding:"10px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>Last Done</div>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text, marginTop:2 }}>{a.iv.lastDoneHours.toLocaleString()} {a.eq.usesMiles?"mi":"hrs"}</div>
                    </div>
                    <div style={{ flex:1, background:C.surfaceHi, border:"1px solid "+C.border, borderRadius:10, padding:"10px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>Interval</div>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text, marginTop:2 }}>Every {a.iv.intervalHours.toLocaleString()} {a.eq.usesMiles?"mi":"hrs"}</div>
                    </div>
                    <div style={{ flex:1, background:C.surfaceHi, border:"1px solid "+C.border, borderRadius:10, padding:"10px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>Status</div>
                      <div style={{ fontSize:12, fontWeight:700, color:due.color, marginTop:2 }}>{due.text}</div>
                    </div>
                  </div>
                  <button onClick={function(){ setDoneIv(a.iv); setDoneEq(a.eq); setExpanded(null); }} style={{ width:"100%", background:C.successDim, border:"1px solid "+C.success, borderRadius:10, color:C.success, fontSize:12, ...FM, fontWeight:700, padding:"12px 8px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>
                    Mark Complete
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {upcoming.length>0 && (
          <div style={{ marginTop:20 }}>
            <SecHead title="Log Reminders" color={C.blue} />
            {upcoming.map(function(rem) {
              var eq=equipment.filter(function(e){ return e.id===rem.equipmentId; })[0]; if (!eq) return null;
              var nl=remNext(rem); var ug=nl==="Today"||nl==="Tomorrow"||nl==="Overdue";
              return (
                <div key={rem.id} style={{ background:C.surface, border:"1px solid "+(ug?C.accent:C.border), borderRadius:12, padding:"11px 14px", marginBottom:8, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:20 }}>{emoji(eq.type)}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{eq.name}</div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{remSched(rem)}</div>
                  </div>
                  <div style={{ fontSize:11, fontWeight:700, color:ug?C.accent:C.dim }}>{nl}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}



// ─── WEATHER TILE ─────────────────────────────────────────────────────────────
// Uses browser geolocation + Open-Meteo (free, no API key needed)

function WeatherTile(props) {
  var lang = props.lang||"en";
  var w1=useState(null);    var weather=w1[0];   var setWeather=w1[1];
  var w2=useState("idle");  var status=w2[0];    var setStatus=w2[1];
  var w3=useState(false);   var expanded=w3[0];  var setExpanded=w3[1];

  function load() {
    if (!navigator.geolocation) { setStatus("unavailable"); return; }
    setStatus("loading");
    navigator.geolocation.getCurrentPosition(
      function(pos) {
        var lat=pos.coords.latitude.toFixed(4);
        var lon=pos.coords.longitude.toFixed(4);
        fetch("https://api.open-meteo.com/v1/forecast?latitude="+lat+"&longitude="+lon+"&current=temperature_2m,precipitation,windspeed_10m,weathercode&daily=precipitation_sum,windspeed_10m_max,temperature_2m_max,temperature_2m_min,weathercode&temperature_unit=fahrenheit&windspeed_unit=mph&precipitation_unit=inch&timezone=auto&forecast_days=3")
          .then(function(r){ return r.json(); })
          .then(function(data){
            var cur = data.current; var daily = data.daily;
            var code = cur.weathercode;
            var alert = buildAlert(code, cur.precipitation, cur.windspeed_10m, cur.temperature_2m, daily);
            setWeather({ temp:Math.round(cur.temperature_2m), wind:Math.round(cur.windspeed_10m), precip:cur.precipitation, code:code, alert:alert, daily:daily });
            setStatus("ok");
          })
          .catch(function(){ setStatus("error"); });
      },
      function(){ setStatus("denied"); }
    );
  }

  function buildAlert(code, precip, wind, temp, daily) {
    var alerts = [];
    // Rain / precipitation
    if (precip >= 0.1) alerts.push({ icon:"🌧️", text:"Active precipitation — defer spraying, seeding, or freshly seeded areas", color:"#4A90D9" });
    // Heavy wind
    if (wind >= 20) alerts.push({ icon:"💨", text:"High winds ("+Math.round(wind)+" mph) — hold spraying operations, secure loose equipment", color:"#F5A623" });
    if (wind >= 35) alerts.push({ icon:"⚠️", text:"Dangerous wind speeds — suspend aerial work, secure trailers and attachments", color:"#E05C5C" });
    // Freeze
    if (temp <= 32) alerts.push({ icon:"🧊", text:"Freezing temps — drain water lines, check hydraulic fluid, watch for ice on work surfaces", color:"#38BFA1" });
    // Heat
    if (temp >= 95) alerts.push({ icon:"🌡️", text:"Extreme heat — watch engine temps, increase coolant checks, keep crew hydrated", color:"#E05C5C" });
    // Check tomorrow rain
    if (daily && daily.precipitation_sum && daily.precipitation_sum[1] >= 0.25) alerts.push({ icon:"🌂", text:"Heavy rain expected tomorrow — plan outdoor work for today", color:"#4A90D9" });
    // Thunderstorm codes
    if (code >= 95 && code <= 99) alerts.push({ icon:"⛈️", text:"Thunderstorm conditions — suspend outdoor operations, ground crew", color:"#E05C5C" });
    // All clear
    if (alerts.length === 0) alerts.push({ icon:"✅", text:"No weather concerns today — good conditions for field work", color:"#4CAF6E" });
    return alerts;
  }

  function wDesc(code) {
    if (code===0) return "Clear"; if (code<=2) return "Partly Cloudy"; if (code===3) return "Overcast";
    if (code<=49) return "Foggy"; if (code<=59) return "Drizzle"; if (code<=69) return "Rain";
    if (code<=79) return "Snow"; if (code<=84) return "Rain Showers"; if (code<=94) return "Snow Showers";
    return "Thunderstorm";
  }

  if (status==="idle") {
    return (
      <div onClick={load} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:14, padding:"14px 16px", marginBottom:12, cursor:"pointer", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ fontSize:28 }}>🌤️</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Weather Alerts</div>
          <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>Tap to check field conditions</div>
        </div>
        <div style={{ fontSize:11, color:C.accent, fontWeight:700, ...FM }}>CHECK →</div>
      </div>
    );
  }
  if (status==="loading") {
    return (
      <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:14, padding:"14px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ fontSize:28 }}>🌤️</div>
        <div style={{ fontSize:12, color:C.muted, ...FM }}>Checking conditions...</div>
      </div>
    );
  }
  if (status==="denied"||status==="unavailable"||status==="error") {
    return (
      <div onClick={load} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:14, padding:"14px 16px", marginBottom:12, cursor:"pointer", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ fontSize:28 }}>🌤️</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, color:C.muted }}>Weather unavailable — {status==="denied"?"location access denied":"tap to retry"}</div>
        </div>
      </div>
    );
  }
  if (!weather) return null;

  var primary = weather.alert[0];
  return (
    <div style={{ background:C.surface, border:"1px solid "+(primary.color===C.danger||primary.color==="#E05C5C"?C.danger:C.border), borderRadius:14, marginBottom:12, overflow:"hidden" }}>
      <div onClick={function(){ setExpanded(!expanded); }} style={{ padding:"14px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
        <div style={{ fontSize:28 }}>{primary.icon}</div>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{weather.temp}°F — {wDesc(weather.code)}</div>
            <div style={{ fontSize:10, color:C.muted, ...FM }}>{weather.wind} mph wind</div>
          </div>
          <div style={{ fontSize:11, color:primary.color, marginTop:3, lineHeight:1.4 }}>{primary.text}</div>
        </div>
        <div style={{ fontSize:14, color:C.muted, flexShrink:0 }}>{expanded?"▲":"▼"}</div>
      </div>
      {expanded && (
        <div style={{ borderTop:"1px solid "+C.border, padding:"12px 16px", display:"flex", flexDirection:"column", gap:10 }}>
          {weather.alert.slice(1).map(function(a,i) {
            return (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <span style={{ fontSize:18, flexShrink:0 }}>{a.icon}</span>
                <div style={{ fontSize:11, color:a.color, lineHeight:1.5 }}>{a.text}</div>
              </div>
            );
          })}
          {weather.daily && (
            <div style={{ background:C.surfaceHi, borderRadius:10, padding:"10px 12px", marginTop:4 }}>
              <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8, fontWeight:700 }}>3-Day Outlook</div>
              <div style={{ display:"flex", gap:8 }}>
                {["Today","Tomorrow","Day 3"].map(function(day,i) {
                  var hi=weather.daily.temperature_2m_max&&Math.round(weather.daily.temperature_2m_max[i]);
                  var lo=weather.daily.temperature_2m_min&&Math.round(weather.daily.temperature_2m_min[i]);
                  var rain=weather.daily.precipitation_sum&&weather.daily.precipitation_sum[i];
                  return (
                    <div key={day} style={{ flex:1, textAlign:"center" }}>
                      <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>{day}</div>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{hi}°/{lo}°</div>
                      {rain>0 && <div style={{ fontSize:10, color:C.blue, marginTop:2 }}>{rain.toFixed(2)}"</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <button onClick={load} style={{ background:"none", border:"none", color:C.muted, fontSize:10, ...FM, cursor:"pointer", textAlign:"right", padding:0 }}>Refresh ↺</button>
        </div>
      )}
    </div>
  );
}

// ─── ADMIN DASHBOARD ──────────────────────────────────────────────────────────

function DashboardScreen(props) {
  var equipment=props.equipment; var logs=props.logs; var reminders=props.reminders;
  var repairs=props.repairs||[]; var requests=props.requests||[];
  var onNavigate=props.onNavigate; var onUpdateEq=props.onUpdateEq;
  var isAdmin=props.isAdmin!==undefined?props.isAdmin:true;
  var currentUser=props.currentUser||{id:"guest",name:"User",role:"crew",username:"user"};
  var onLogout=props.onLogout||function(){};
  var onManageUsers=props.onManageUsers||null;
  var onShowServices=props.onShowServices||null;
  var plan=props.plan||getPlan(currentUser);
  var atEqLimit=plan.maxEquipment&&equipment.length>=plan.maxEquipment;
  var lang=props.lang||"en"; var t=getT(lang);

  var d1=useState("alerts"); var activeTab=d1[0]; var setActiveTab=d1[1];
  var d2=useState(null);     var markDownEq=d2[0];var setMarkDownEq=d2[1];
  var d3=useState(false);    var showMenu=d3[0];  var setShowMenu=d3[1];

  var downEq=equipment.filter(function(e){ return e.status==="out_of_service"||e.status==="waiting_parts"; });
  var downCount=downEq.length;
  var allAlerts=[];
  equipment.forEach(function(eq) {
    eq.maintenanceIntervals.forEach(function(iv) {
      if (iv.alertMode==="off") return;
      allAlerts.push({iv:iv,eq:eq,due:dueIn(iv,eq)});
    });
  });
  allAlerts.sort(function(a,b) {
    var aO=a.due<=0; var bO=b.due<=0;
    if (aO&&!bO) return -1; if (!aO&&bO) return 1; return a.due-b.due;
  });
  var overdueCount=allAlerts.filter(function(a){ return a.due<=0; }).length;
  var warningCount=allAlerts.filter(function(a){ var w=a.eq.usesMiles?500:50; return a.due>0&&a.due<=w; }).length;
  var pendingRequests=requests.filter(function(r){ return r.status==="pending"; }).length;
  var recentLogs=logs.slice().sort(function(a,b){ return b.date.localeCompare(a.date); }).slice(0,5);
  var upcoming=reminders.filter(function(r){ return r.alertMode!=="off"; }).sort(function(a,b){ if(!a.nextDate)return 1; if(!b.nextDate)return -1; return a.nextDate.localeCompare(b.nextDate); }).slice(0,5);

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", paddingBottom:40 }}>
      {markDownEq && (
        <MarkDownSheet eq={markDownEq} current={markDownEq.outOfService||null} lang={lang}
          onSave={function(d){ onUpdateEq(Object.assign({},markDownEq,{status:d.reason,outOfService:d})); setMarkDownEq(null); }}
          onClear={function(){ onUpdateEq(Object.assign({},markDownEq,{status:"active",outOfService:null})); setMarkDownEq(null); }}
          onClose={function(){ setMarkDownEq(null); }} />
      )}
      {/* Header */}
      <div style={{ width:"100%", maxWidth:480, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, borderBottom:"1px solid "+C.border, overflow:"hidden" }}>
        <img src="/chorehabit-app-tile.svg" alt="CH" style={{ width:32, height:32, borderRadius:8, flexShrink:0 }} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.accent, letterSpacing:"0.1em", textTransform:"uppercase", ...FM }}>ChoreHabit</div>
          <div style={{ fontSize:10, color:C.muted, letterSpacing:"0.06em", textTransform:"uppercase", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {currentUser.name&&currentUser.name!==currentUser.email?currentUser.name:"Admin"} — Admin
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
          <div onClick={function(){ setShowMenu(!showMenu); }} style={{ background:C.purpleDim, border:"1px solid "+C.purple, borderRadius:16, padding:"4px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:5, maxWidth:110, overflow:"hidden" }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:C.purple, flexShrink:0 }} />
            <span style={{ fontSize:10, color:C.purple, ...FM, fontWeight:700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {(currentUser.name&&currentUser.name!==currentUser.email?currentUser.name:"Admin").split(" ")[0]}
            </span>
          </div>
          {overdueCount>0 && <div style={{ fontSize:10, color:C.danger, fontWeight:700 }}>{overdueCount} OVERDUE</div>}
        </div>
      </div>
      <div style={{ width:"100%", maxWidth:480, padding:"0 16px" }}>
        {/* User menu */}
        {showMenu && (
          <div style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"8px 0", marginTop:10, marginBottom:6 }}>
            <div style={{ padding:"10px 16px 8px", borderBottom:"1px solid "+C.border, marginBottom:4 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{currentUser.name}</div>
              <div style={{ fontSize:10, color:C.muted, marginTop:2, ...FM, textTransform:"uppercase" }}>@{currentUser.username} -- Admin</div>
            </div>
            {onManageUsers && <button onClick={function(){ setShowMenu(false); onManageUsers(); }} style={{ width:"100%", background:"none", border:"none", color:C.blue, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Manage Users</button>}
            {onShowServices && <button onClick={function(){ setShowMenu(false); onShowServices(); }} style={{ width:"100%", background:"none", border:"none", color:C.teal, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Services Library</button>}
            <button onClick={function(){ setShowMenu(false); onNavigate("report"); }} style={{ width:"100%", background:"none", border:"none", color:C.accent, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Weekly Report</button>
            <button onClick={function(){ setShowMenu(false); onNavigate("account"); }} style={{ width:"100%", background:"none", border:"none", color:C.blue, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Account Settings</button>
            <button onClick={function(){ setShowMenu(false); onNavigate("support"); }} style={{ width:"100%", background:"none", border:"none", color:C.teal, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Support</button>
            <button onClick={function(){ setShowMenu(false); onLogout(); }} style={{ width:"100%", background:"none", border:"none", color:C.danger, fontSize:13, ...FM, fontWeight:700, padding:"10px 16px", cursor:"pointer", textAlign:"left" }}>Sign Out</button>
          </div>
        )}

        {/* DOWN EQUIPMENT - full width header tile */}
        <div onClick={function(){ onNavigate("down"); }} style={{ marginTop:14, background:downCount>0?C.dangerDim:C.surface, border:"2px solid "+(downCount>0?C.danger:C.border), borderRadius:14, padding:"16px 20px", cursor:"pointer", display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:36 }}>{downCount>0?"🚨":"✅"}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, color:downCount>0?C.danger:C.success, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700, ...FM }}>Equipment Down</div>
            <div style={{ fontSize:28, fontWeight:700, color:downCount>0?C.danger:C.success, lineHeight:1.1 }}>{downCount}</div>
            <div style={{ fontSize:12, color:downCount>0?C.danger:C.muted, marginTop:2 }}>{downCount===0?"All equipment active":downCount===1?"1 piece out of service":downCount+" pieces out of service"}</div>
          </div>
          <div style={{ fontSize:20, color:downCount>0?C.danger:C.muted }}>{">"}</div>
        </div>

        {/* Secondary tiles grid */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginTop:10 }}>
          {/* Equipment */}
          <div onClick={function(){ onNavigate("fleet"); }} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"14px 12px", cursor:"pointer", display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.1em" }}>Equipment</div>
            <div style={{ fontSize:26, fontWeight:700, color:C.accent }}>{equipment.length}</div>
            <div style={{ fontSize:11, color:C.muted }}>Total pieces</div>
          </div>
          {/* Overdue */}
          <div onClick={function(){ setActiveTab("alerts"); document.getElementById("ch-tabs")&&document.getElementById("ch-tabs").scrollIntoView(); }} style={{ background:overdueCount>0?C.dangerDim:C.surface, border:"1px solid "+(overdueCount>0?C.danger:C.border), borderRadius:12, padding:"14px 12px", cursor:"pointer", display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontSize:11, color:overdueCount>0?C.danger:C.muted, textTransform:"uppercase", letterSpacing:"0.1em" }}>Overdue</div>
            <div style={{ fontSize:26, fontWeight:700, color:overdueCount>0?C.danger:C.success }}>{overdueCount}</div>
            <div style={{ fontSize:11, color:overdueCount>0?C.danger:C.muted }}>{overdueCount>0?"Need attention":"Up to date"}</div>
          </div>
          {/* Warnings */}
          <div onClick={function(){ setActiveTab("alerts"); }} style={{ background:warningCount>0?"rgba(245,166,35,0.1)":C.surface, border:"1px solid "+(warningCount>0?C.amber:C.border), borderRadius:12, padding:"14px 12px", cursor:"pointer", display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontSize:11, color:warningCount>0?C.amber:C.muted, textTransform:"uppercase", letterSpacing:"0.1em" }}>Warnings</div>
            <div style={{ fontSize:26, fontWeight:700, color:warningCount>0?C.amber:C.success }}>{warningCount}</div>
            <div style={{ fontSize:11, color:warningCount>0?C.amber:C.muted }}>{warningCount>0?"Due soon":"All good"}</div>
          </div>
          {/* Service Requests */}
          <div onClick={function(){ onNavigate("requests"); }} style={{ background:pendingRequests>0?C.purpleDim:C.surface, border:"1px solid "+(pendingRequests>0?C.purple:C.border), borderRadius:12, padding:"14px 12px", cursor:"pointer", display:"flex", flexDirection:"column", gap:4 }}>
            <div style={{ fontSize:11, color:pendingRequests>0?C.purple:C.muted, textTransform:"uppercase", letterSpacing:"0.1em" }}>Requests</div>
            <div style={{ fontSize:26, fontWeight:700, color:pendingRequests>0?C.purple:C.success }}>{pendingRequests}</div>
            <div style={{ fontSize:11, color:pendingRequests>0?C.purple:C.muted }}>{pendingRequests>0?"Pending review":"None pending"}</div>
          </div>
        </div>

        {/* Equipment limit banner */}
        {atEqLimit && (
          <div style={{ background:"rgba(245,166,35,0.08)", border:"1px solid "+C.accent, borderRadius:12, padding:"12px 14px", marginTop:10, display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:20 }}>⭐</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.accent, ...FM }}>Equipment limit reached</div>
              <div style={{ fontSize:11, color:C.dim, marginTop:2 }}>{equipment.length}/{plan.maxEquipment} on {planName((currentUser&&currentUser.plan)||"free")} — upgrade to add more</div>
            </div>
            <button onClick={function(){ onNavigate("account"); }} style={{ background:C.accent, border:"none", borderRadius:8, color:"#0F1117", fontSize:10, ...FM, fontWeight:700, padding:"6px 12px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.06em", flexShrink:0 }}>Upgrade</button>
          </div>
        )}

        {/* Quick nav row */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginTop:10 }}>
          {[{key:"logbook",icon:"📓",label:"Logbook"},{key:"add",icon:"+",label:"Add Equip.",locked:atEqLimit},{key:"report",icon:"📊",label:"Report",locked:!plan.weeklyReports}].map(function(tile) {
            return (
              <div key={tile.key} onClick={function(){ if(!tile.locked) onNavigate(tile.key); }} style={{ background:tile.locked?C.surfaceHi:C.surface, border:"1px solid "+(tile.locked?C.muted:C.border), borderRadius:12, padding:"14px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:tile.locked?"not-allowed":"pointer", opacity:tile.locked?0.5:1, position:"relative" }}>
                <div style={{ fontSize:22 }}>{tile.icon}</div>
                <div style={{ fontSize:10, fontWeight:700, color:tile.locked?C.muted:C.text, ...FM, textAlign:"center", textTransform:"uppercase", letterSpacing:"0.06em" }}>{tile.label}</div>
                {tile.locked && <div style={{ position:"absolute", top:6, right:6, fontSize:10 }}>🔒</div>}
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        {/* Weather alerts */}
        <WeatherTile lang={lang} />

        <div id="ch-tabs" style={{ display:"flex", gap:6, marginTop:14, background:C.surfaceHi, borderRadius:10, padding:4 }}>
          {[{key:"alerts",label:"Alerts",badge:overdueCount>0?overdueCount:null},{key:"logs",label:"Logbook"}].map(function(tb) {
            var active=activeTab===tb.key;
            return (
              <button key={tb.key} onClick={function(){ setActiveTab(tb.key); }} style={{ flex:1, background:active?C.surface:"transparent", border:active?"1px solid "+C.border:"1px solid transparent", borderRadius:8, padding:"8px 4px", cursor:"pointer", position:"relative", color:active?C.accent:C.muted, fontSize:11, ...FM, fontWeight:active?700:400, letterSpacing:"0.06em", textTransform:"uppercase" }}>
                {tb.label}
                {tb.badge && <span style={{ position:"absolute", top:2, right:2, background:C.danger, borderRadius:8, fontSize:8, color:"#fff", fontWeight:700, padding:"1px 4px", lineHeight:1.4 }}>{tb.badge}</span>}
              </button>
            );
          })}
        </div>

        {/* Alerts tab */}
        {activeTab==="alerts" && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:C.accent, marginBottom:10, display:"flex", alignItems:"center", gap:8, ...FM }}>
              Maintenance Alerts
              <div style={{ flex:1, height:1, background:C.border }} />
              <span style={{ background:overdueCount>0?C.dangerDim:C.accentDim, border:"1px solid "+(overdueCount>0?C.danger:C.accent), borderRadius:10, padding:"1px 8px", fontSize:10, color:overdueCount>0?C.danger:C.accent }}>{allAlerts.length}</span>
            </div>
            {allAlerts.length===0
              ? <div style={{ textAlign:"center", padding:"30px 0", color:C.muted, fontSize:13 }}>All maintenance is up to date.</div>
              : allAlerts.slice(0,8).map(function(a) {
                var due=dueBadge(a.iv,a.eq); var over=due.text==="OVERDUE";
                return (
                  <div key={a.iv.id} onClick={function(){ onNavigate("profile",a.eq.id); }} style={{ background:over?C.dangerDim:C.surface, border:"1px solid "+(over?C.danger:C.border), borderRadius:12, padding:"12px 14px", marginBottom:8, cursor:"pointer" }}>
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                      <span style={{ fontSize:22, flexShrink:0 }}>{emoji(a.eq.type)}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:C.text, ...FM }}>{a.iv.task}</div>
                            <div style={{ fontSize:11, color:C.blue, marginTop:2, ...FM }}>{a.eq.name}</div>
                          </div>
                          <div style={{ textAlign:"right", flexShrink:0, marginLeft:8 }}>
                            <div style={{ fontSize:14, fontWeight:700, color:due.color, ...FM }}>{due.text}</div>
                            <div style={{ fontSize:9, color:C.muted, textTransform:"uppercase", marginTop:2 }}>{over?"ACTION NEEDED":"remaining"}</div>
                          </div>
                        </div>
                        <div style={{ marginTop:8, height:4, background:C.surfaceHi, borderRadius:2, overflow:"hidden" }}><div style={{ height:4, borderRadius:2, background:due.color, width:due.pct+"%" }} /></div>
                      </div>
                    </div>
                  </div>
                );
              })
            }
            {upcoming.length>0 && (
              <div style={{ marginTop:20 }}>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:C.blue, marginBottom:10, display:"flex", alignItems:"center", gap:8, ...FM }}>Upcoming Reminders <div style={{ flex:1, height:1, background:C.border }} /></div>
                {upcoming.map(function(rem) {
                  var eq=equipment.filter(function(e){ return e.id===rem.equipmentId; })[0]; if (!eq) return null;
                  var nl=remNext(rem); var ug=nl==="Today"||nl==="Tomorrow"||nl==="Overdue";
                  return (
                    <div key={rem.id} onClick={function(){ onNavigate("profile",eq.id); }} style={{ background:C.surface, border:"1px solid "+(ug?C.accent:C.border), borderRadius:12, padding:"11px 14px", marginBottom:8, cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:20 }}>{emoji(eq.type)}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", justifyContent:"space-between" }}>
                          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{eq.name}</div>
                          <div style={{ fontSize:12, fontWeight:700, color:ug?C.accent:C.dim }}>{nl}</div>
                        </div>
                        <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>{remSched(rem)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* Logbook tab */}
        {activeTab==="logs" && (
          <div style={{ marginTop:16 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:C.accent, marginBottom:10, display:"flex", alignItems:"center", gap:8, ...FM }}>Recent Log Entries <div style={{ flex:1, height:1, background:C.border }} /></div>
            {recentLogs.map(function(log) {
              var eq=equipment.filter(function(e){ return e.id===log.equipmentId; })[0]; if (!eq) return null;
              var uL=eq.usesMiles?"mi":"hrs"; var db=daysSince(log.date);
              return (
                <div key={log.id} onClick={function(){ onNavigate("profile",eq.id); }} style={{ background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:"11px 14px", marginBottom:8, cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:20 }}>{emoji(eq.type)}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{eq.name}</div>
                      <div style={{ fontSize:12, fontWeight:700, color:C.accent }}>{log.usage.toLocaleString()} {uL}</div>
                    </div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>{db===0?"Today":db===1?"Yesterday":db+"d ago"}{log.driver?" -- "+log.driver:""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────

export default function ChoreHabitApp(props) {
  var currentUser=props.currentUser||{id:"test_admin",name:"Admin User",role:"admin",username:"admin",lang:"en"};
  var isAdmin=props.isAdmin!==undefined?props.isAdmin:currentUser.role==="admin";
  var onLogout=props.onLogout||function(){};
  var onManageUsers=props.onManageUsers||null;
  var onShowTutorial=props.onShowTutorial||null;
  var lang=currentUser.lang||"en";

  var companyId = currentUser && currentUser.company_id;

  // Load from localStorage cache immediately (fast), then sync from Supabase in background
  var a1=useState(function(){
    if (props.initialEquipment && props.initialEquipment.length > 0) {
      save(SK.equipment, props.initialEquipment);
      return props.initialEquipment;
    }
    return load(SK.equipment, []);
  }); var equipment  =a1[0]; var setEquipment  =a1[1];
  var a2=useState(function(){ return load(SK.logs,        []); }); var logs        =a2[0]; var setLogs       =a2[1];
  var a3=useState(function(){ return load(SK.repairs,     []); }); var repairs     =a3[0]; var setRepairs    =a3[1];
  var a4=useState(function(){ return load(SK.reminders,   INIT_REMINDERS); }); var reminders   =a4[0]; var setRemRaw     =a4[1];
  var a5=useState(function(){ return load(SK.partsLib,    INIT_PARTS_LIB); }); var partsLib    =a5[0]; var setPartsLib   =a5[1];
  var a6=useState(function(){ return load(SK.servicesLib, INIT_SERVICES_LIB); }); var servicesLib =a6[0]; var setSvcRaw     =a6[1];
  var a7=useState([]); var requests    =a7[0]; var setRequestsRaw=a7[1];
  var a8=useState({screen:"dashboard"});  var nav         =a8[0]; var setNav          =a8[1];
  var a9=useState(false);                 var showServices=a9[0]; var setShowServices =a9[1];
  var a10=useState(null);                 var upgradeMsg  =a10[0];var setUpgradeMsg   =a10[1];
  var a11=useState(false);                var dbLoaded    =a11[0];var setDbLoaded     =a11[1];

  var plan = getPlan(currentUser);

  // Real-time subscription — admin sees crew updates instantly
  useEffect(function() {
    if (!companyId) return;
    var sub = supabase
      .channel("company-"+companyId)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"logs", filter:"company_id=eq."+companyId },
        function(payload) {
          if (!payload.new) return;
          setLogs(function(ls) {
            if (ls.some(function(l){ return l.id===payload.new.id; })) return ls;
            return [{ id:payload.new.id, equipmentId:payload.new.equipment_id, equipmentName:payload.new.equipment_name, date:payload.new.date, usage:payload.new.usage, driver:payload.new.driver, notes:payload.new.notes, status:payload.new.status }].concat(ls);
          });
        }
      )
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"requests", filter:"company_id=eq."+companyId },
        function(payload) {
          if (!payload.new) return;
          setRequestsRaw(function(rs) {
            if (rs.some(function(r){ return r.id===payload.new.id; })) return rs;
            return [{ id:payload.new.id, equipmentId:payload.new.equipment_id, equipmentName:payload.new.equipment_name, requestedBy:payload.new.requested_by, title:payload.new.title, details:payload.new.details, priority:payload.new.priority, status:payload.new.status, autoGenerated:payload.new.auto_generated, createdAt:payload.new.created_at }].concat(rs);
          });
        }
      )
      .subscribe();
    return function() { supabase.removeChannel(sub); };
  }, [companyId]);

  // Sync from Supabase on mount — pulls fresh data from database
  useEffect(function() {
    if (!companyId) return;
    fetchEquipment(companyId).then(function(data) {
      if (data && data.length > 0) { setEquipment(data); save(SK.equipment, data); }
    });
    fetchLogs(companyId).then(function(data) {
      if (data) { setLogs(data); save(SK.logs, data); }
    });
    fetchRepairs(companyId).then(function(data) {
      if (data) { setRepairs(data); save(SK.repairs, data); }
    });
    fetchRequests(companyId).then(function(data) {
      if (data) { setRequestsRaw(data); }
    });
    setDbLoaded(true);
  }, [companyId]);

  // Also sync onboarding equipment to Supabase
  useEffect(function() {
    if (!companyId || !props.initialEquipment || !props.initialEquipment.length) return;
    props.initialEquipment.forEach(function(eq) {
      saveEquipment(companyId, eq);
    });
  }, [companyId]);

  function setReminders(v) { var n=typeof v==="function"?v(reminders):v; setRemRaw(n); save(SK.reminders,n); }
  function setServicesLib(v) { var n=typeof v==="function"?v(servicesLib):v; setSvcRaw(n); save(SK.servicesLib,n); }
  function setRequests(v) {
    var n=typeof v==="function"?v(requests):v;
    setRequestsRaw(n);
    // Sync any changed requests to Supabase
    if (companyId && Array.isArray(n)) {
      n.forEach(function(req) { saveRequest(companyId, req); });
    }
  }
  function navigate(screen, equipmentId) { setNav({screen:screen,equipmentId:equipmentId||null}); }

  function updateEq(updated) {
    setEquipment(function(eqs){ var next=eqs.map(function(e){ return e.id===updated.id?updated:e; }); save(SK.equipment,next); return next; });
    if (companyId) saveEquipment(companyId, updated);
  }
  function addLog(entry) {
    setLogs(function(ls){ var next=[entry].concat(ls); save(SK.logs,next); return next; });
    setEquipment(function(eqs){ var next=eqs.map(function(e){ return e.id===entry.equipmentId?Object.assign({},e,{totalUsage:entry.usage}):e; }); save(SK.equipment,next); return next; });
    if (companyId) saveLog(companyId, entry);
    // #8 — yellow/red status auto-creates a service request
    if (entry.status==="yellow"||entry.status==="red") {
      var autoReq = {
        id:genId(),
        equipmentId:entry.equipmentId,
        equipmentName:entry.equipmentName||entry.equipmentId,
        requestedBy:entry.driver||"Crew",
        requestedById:entry.driver||"crew",
        title:entry.status==="red"?"URGENT: Equipment reported down":"Needs Attention — crew report",
        details:entry.notes||"",
        priority:entry.status==="red"?"urgent":"soon",
        status:"pending",
        createdAt:new Date().toISOString(),
        resolvedAt:null, resolvedBy:null, adminNotes:"",
        autoGenerated:true
      };
      setRequests(function(rs){ return [autoReq].concat(rs); });
      if (companyId) saveRequest(companyId, autoReq);
    }
  }
  function addRepair(repair) {
    setRepairs(function(rs){ var next=[repair].concat(rs); save(SK.repairs,next); return next; });
    if (companyId) saveRepair(companyId, repair);
  }
  function addEquipment(eq) {
    if (!canAddEquipment(currentUser, equipment.length)) {
      setUpgradeMsg(equipmentLimitMsg(currentUser));
      return;
    }
    setEquipment(function(eqs){ var next=eqs.concat([eq]); save(SK.equipment,next); return next; });
    if (companyId) saveEquipment(companyId, eq);
    navigate("profile",eq.id);
  }
  function deleteEquipment(eqId) {
    setEquipment(function(eqs){ var next=eqs.filter(function(e){ return e.id!==eqId; }); save(SK.equipment,next); return next; });
    if (companyId) deleteEquipmentById(companyId, eqId);
    navigate("fleet");
  }
  function savePartsLib(next) { setPartsLib(next); save(SK.partsLib,next); }
  function addRequest(req) {
    setRequests(function(rs){ return [req].concat(rs); });
    if (companyId) saveRequest(companyId, req);
  }

  var css="@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap'); *{box-sizing:border-box;margin:0;padding:0;} input:focus,textarea:focus,select:focus{border-color:#F5A623!important;outline:none;} input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;} ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-thumb{background:#2E3248;border-radius:2px;} button:hover{opacity:0.85;}";

  var screen=nav.screen;
  var profileEq=nav.equipmentId?equipment.filter(function(e){ return e.id===nav.equipmentId; })[0]:null;

  var sharedProfileProps = {
    logs:logs, repairs:repairs, partsLib:partsLib, isAdmin:isAdmin,
    servicesLib:servicesLib, onUpdateServicesLib:setServicesLib,
    onAddLog:addLog, onAddRepair:addRepair, onUpdatePartsLib:savePartsLib,
    currentUser:currentUser, requests:requests, onAddRequest:addRequest,
    lang:lang
  };

  return (
    <div>
      <style>{css}</style>
      {upgradeMsg && <UpgradeSheet msg={upgradeMsg} onClose={function(){ setUpgradeMsg(null); }} />}
      {showServices && (
        <div style={{ position:"fixed", top:0, right:0, bottom:0, left:0, zIndex:200 }}>
          <ServicesLibraryScreen servicesLib={servicesLib} onUpdate={setServicesLib} isAdmin={isAdmin} lang={lang} onBack={function(){ setShowServices(false); }} />
        </div>
      )}
      {screen==="dashboard" && !isAdmin && (
        <CrewDashboard
          equipment={equipment} logs={logs} reminders={reminders} repairs={repairs}
          requests={requests} onAddLog={addLog} onAddRequest={addRequest}
          onNavigate={navigate} currentUser={currentUser} onLogout={onLogout}
          onShowTutorial={onShowTutorial}
          lang={lang} />
      )}
      {screen==="dashboard" && isAdmin && (
        <DashboardScreen
          equipment={equipment} logs={logs} reminders={reminders}
          repairs={repairs} requests={requests}
          onNavigate={navigate} onUpdateEq={updateEq}
          isAdmin={isAdmin} currentUser={currentUser}
          onLogout={onLogout} onManageUsers={onManageUsers}
          onShowServices={function(){ setShowServices(true); }}
          plan={plan}
          lang={lang} />
      )}
      {screen==="alerts" && (
        <CrewAlertsScreen equipment={equipment} reminders={reminders} lang={lang} onUpdateEq={updateEq} onAddRepair={addRepair} currentUser={currentUser} onBack={function(){ navigate("dashboard"); }} />
      )}
      {screen==="logbook" && (
        <LogbookScreen equipment={equipment} logs={logs} reminders={reminders} onAddLog={addLog} lang={lang} currentUser={currentUser} onBack={function(){ navigate("dashboard"); }} />
      )}
      {screen==="fleet" && (
        <FleetScreen
          equipment={equipment} logs={logs} repairs={repairs}
          partsLib={partsLib} isAdmin={isAdmin}
          servicesLib={servicesLib} onUpdateServicesLib={setServicesLib}
          onUpdateEq={updateEq} onAddLog={addLog}
          onAddRepair={addRepair} onUpdatePartsLib={savePartsLib}
          currentUser={currentUser} requests={requests} onAddRequest={addRequest}
          plan={plan}
          onUpgradeMsg={setUpgradeMsg}
          onAddEquipmentNav={function(){ navigate("add"); }}
          lang={lang} onBack={function(){ navigate("dashboard"); }} />
      )}
      {screen==="profile"&&profileEq && (
        <EquipmentProfileScreen
          eq={profileEq}
          onUpdate={updateEq}
          onDelete={deleteEquipment}
          onBack={function(){ navigate("fleet"); }}
          {...sharedProfileProps} />
      )}
      {screen==="add" && (
        <AddEquipmentScreen onSave={addEquipment} lang={lang} plan={plan} onBack={function(){ navigate("dashboard"); }} />
      )}
      {screen==="down" && (
        <DownPage equipment={equipment} onUpdate={updateEq} isAdmin={isAdmin} lang={lang} onBack={function(){ navigate("dashboard"); }} />
      )}
      {screen==="requests" && (
        plan.serviceRequests
          ? <ServiceRequestsScreen
              requests={requests} onUpdate={setRequests}
              isAdmin={isAdmin} currentUser={currentUser}
              equipment={equipment} lang={lang}
              onBack={function(){ navigate("dashboard"); }} />
          : <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:40, textAlign:"center" }}>
              <div style={{ fontSize:36, marginBottom:16 }}>🔧</div>
              <div style={{ fontSize:15, fontWeight:700, color:C.accent, marginBottom:8 }}>Service Requests</div>
              <div style={{ fontSize:13, color:C.dim, marginBottom:24, maxWidth:300, lineHeight:1.7 }}>Service requests are available on One Man Band and above. Upgrade to let your crew flag issues in real time.</div>
              <button onClick={function(){ navigate("dashboard"); }} style={{ background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, fontWeight:700, padding:"12px 24px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>Back to Dashboard</button>
            </div>
      )}
      {screen==="report" && isAdmin && (
        plan.weeklyReports
          ? <WeeklyReportScreen equipment={equipment} logs={logs} repairs={repairs} lang={lang} onBack={function(){ navigate("dashboard"); }} />
          : <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:40, textAlign:"center" }}>
              <div style={{ fontSize:36, marginBottom:16 }}>📊</div>
              <div style={{ fontSize:15, fontWeight:700, color:C.accent, marginBottom:8 }}>Weekly Reports</div>
              <div style={{ fontSize:13, color:C.dim, marginBottom:24, maxWidth:300, lineHeight:1.7 }}>Weekly reports are available on One Man Band and above. Upgrade to track fleet hours, service history, and costs.</div>
              <button onClick={function(){ navigate("dashboard"); }} style={{ background:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, fontWeight:700, padding:"12px 24px", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>Back to Dashboard</button>
            </div>
      )}
      {screen==="account" && isAdmin && (
        <AccountScreen
          currentUser={currentUser}
          companyName={props.companyName}
          onBack={function(){ navigate("dashboard"); }}
          onLogout={onLogout}
          onDeleteAccount={function(){ onLogout(); }}
          onNavigate={navigate}
        />
      )}
      {(screen==="terms"||screen==="privacy"||screen==="support") && (
        <LegalScreen page={screen} onBack={function(){ navigate("dashboard"); }} />
      )}
    </div>
  );
}
